import * as i0 from '@angular/core';
import { Component, Input, ViewChild, NgModule, EventEmitter, forwardRef, ChangeDetectionStrategy, Output, Pipe, CUSTOM_ELEMENTS_SCHEMA, Injectable, Directive } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import { of, BehaviorSubject } from 'rxjs';
import * as i1$1 from '@angular/forms';
import { NG_VALUE_ACCESSOR, FormsModule, Validators, ReactiveFormsModule } from '@angular/forms';
import * as i2 from '@ng-bootstrap/ng-bootstrap';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { Md5 } from 'ts-md5';
import * as _ from 'underscore';

window.unifiedAngular = { version: '15.0.1' };

class InlineHelpComponent {
    constructor(renderer, component) {
        this.renderer = renderer;
        this.component = component;
        this.modalSelector = '[uib-modal-window], ngb-modal-window';
        this.bubbleArrowHeight = 12; // the height of the arrow/pointer connected to the info bubble
        this.updateStyle = () => {
            if (!this.fixed) {
                return;
            }
            const tooltipHeight = this.tooltip.nativeElement.offsetHeight;
            let tooltipPadding = 0;
            // I don't remember why this is necessary. If someone else does, please explain in a comment :(
            if (this.isInModal()) {
                const topPadding = parseInt(window.getComputedStyle(this.tooltip.nativeElement).paddingTop, 10);
                const bottomPadding = parseInt(window.getComputedStyle(this.tooltip.nativeElement).paddingBottom, 10);
                tooltipPadding = topPadding + bottomPadding;
            }
            const top = this.getTooltipTopPosition(tooltipHeight, tooltipPadding);
            this.renderer.setStyle(this.tooltip.nativeElement, 'top', `${top}px`);
            this.renderer.setStyle(this.tooltip.nativeElement, 'height', `${tooltipHeight}px`);
        };
    }
    ngAfterViewInit() {
        if (this.fixed) {
            // if using fixed positioning, make the bubble fixed immediately so it doesn't affect scrollbars
            this.renderer.setStyle(this.tooltip.nativeElement, 'position', 'fixed');
        }
    }
    handleMouseOver() {
        this.visible = true;
        this.updateStyle();
        this.applyContainer();
    }
    handleMouseOut() {
        this.visible = false;
        this.resetContainer();
    }
    /**
     * Polyfill for jquery offset
     */
    getOffset(el) {
        return {
            top: el.getBoundingClientRect().top + window.scrollY,
            left: el.getBoundingClientRect().left + window.scrollX,
        };
    }
    isInModal() {
        return !!document.querySelector(this.modalSelector);
    }
    /**
     * Calculates the CSS `top` property value for the tooltip
     * Note: Fixed position currently only supports top positioning
     */
    getTooltipTopPosition(tooltipHeight, tooltipPadding) {
        let top = this.getBaseTooltipTopPosition(tooltipHeight, tooltipPadding);
        const outerDropdown = this.component.nativeElement.closest('.inline-help-dropdown');
        const isInDropdown = !!outerDropdown;
        if (isInDropdown) {
            top -= this.getOffset(outerDropdown).top;
        }
        else if (this.isInModal()) {
            top += document.querySelector(this.modalSelector).scrollTop;
        }
        return top;
    }
    /**
     * Calculates the base position of the fixed-position tooltip
     * Accounting for default position, then moving the bubble higher up the page subtracting the height of the element, the padding, and the arrow height
     * Note: Fixed position currently only supports top positioning
     */
    getBaseTooltipTopPosition(tooltipHeight, tooltipPadding) {
        return this.getOffset(this.component.nativeElement).top - tooltipHeight - tooltipPadding - this.bubbleArrowHeight;
    }
    resetContainer() {
        const renderer = this.renderer;
        if (this.tooltip) {
            const blockElement = this.block.nativeElement;
            const textElement = this.tooltip.nativeElement;
            renderer.appendChild(blockElement, textElement);
            if (!this.fixed) {
                renderer.removeStyle(textElement, 'position');
            }
        }
        if (this.bodyContainer) {
            renderer.removeChild(document.body, this.bodyContainer);
            this.bodyContainer = null;
        }
    }
    applyContainer() {
        // helper function that does the jquery function el.width()
        const getWidth = (el) => {
            return parseFloat(getComputedStyle(el, null).width.replace('px', ''));
        };
        this.resetContainer();
        if (this.container === 'body') {
            const tooltipElement = this.tooltip.nativeElement;
            const bodyContainer = (this.bodyContainer = this.renderer.createElement('div'));
            const bubbleHeight = tooltipElement.offsetHeight;
            const offsetTop = this.component.nativeElement.getBoundingClientRect().top + document.body.scrollTop;
            const offsetLeft = this.component.nativeElement.getBoundingClientRect().left + document.body.scrollLeft;
            let top = offsetTop - bubbleHeight - this.bubbleArrowHeight;
            top = top > 0 ? top : 0;
            let left = offsetLeft + getWidth(this.component.nativeElement) / 2 - getWidth(this.component.nativeElement) / 2;
            if (left + getWidth(tooltipElement) > window.innerWidth) {
                left = window.innerWidth - getWidth(tooltipElement);
            }
            left = left > 0 ? left : 0;
            // Override some styles to have the positioning working
            this.renderer.setStyle(bodyContainer, 'position', 'absolute');
            this.renderer.setStyle(bodyContainer, 'top', `${top}px`);
            this.renderer.setStyle(bodyContainer, 'left', `${left}px`);
            this.renderer.setStyle(tooltipElement, 'position', 'static');
            this.renderer.setStyle(bodyContainer, 'z-index', '1050');
            this.renderer.appendChild(bodyContainer, tooltipElement);
            this.renderer.appendChild(document.body, bodyContainer);
        }
    }
}
InlineHelpComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InlineHelpComponent, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
InlineHelpComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: InlineHelpComponent, selector: "cb-inline-help", inputs: { fixed: "fixed", container: "container" }, viewQueries: [{ propertyName: "block", first: true, predicate: ["helpBlock"], descendants: true }, { propertyName: "tooltip", first: true, predicate: ["helpText"], descendants: true }], ngImport: i0, template: `
		<div class="inline-help" [class.inline-help--visible]="visible" #helpBlock aria-live="assertive">
			<div class="inline-help__icon" (mouseover)="handleMouseOver()" (mouseout)="handleMouseOut()">
				<span #customIcon><ng-content select="icon,.q-icon"></ng-content></span>
				<span *ngIf="!customIcon.children.length" class="q-icon q-icon-help"></span>
			</div>
			<div #helpText
				class="font-normal inline-help__text text-left white-space-normal bg-white border-radius-4 text-gray-1000 p-16"
				[class.inline-help__text--visible]="visible">

				<ng-content select="help-title" class="inline-help__title"></ng-content>
				<ng-content select="help-body" class="inline-help__body"></ng-content>
			</div>
		</div>`, isInline: true, styles: [":host-context(:root,.light-mode){--black-50-percent-fallback: rgba(0, 0, 0, .5)}:host-context(.dark-mode,.application-dark-mode){--black-50-percent-fallback: rgba(255, 255, 255, .5)}.inline-help{align-items:center;display:inline-flex;justify-content:center;margin-left:6px;position:relative;vertical-align:middle;z-index:1;font-family:\"Helvetica Neue\",Helvetica,Arial,sans serif}.inline-help__icon{cursor:help;font-size:16px;line-height:1.5}.inline-help.inline-help--visible{z-index:2}.inline-help__text{font-size:.875rem;box-shadow:0 0 8px var(--black-50-percent, var(--black-50-percent-fallback));position:absolute;transition:opacity .3s;width:300px;box-sizing:border-box;line-height:1.5;bottom:calc(100% + 10px);opacity:0;visibility:hidden}.inline-help__text:after{border-top-color:transparent!important;border-bottom-color:transparent!important;border-right-color:transparent!important;border-left-color:transparent!important;border-style:solid;border-width:5px 5px 0 5px;border-top-color:var(--white)!important;position:absolute;top:100%;left:calc(50% - 5px);content:\"\"}.inline-help__text.inline-help__text--visible{visibility:visible;opacity:1}:host[right] .inline-help__text{bottom:auto;left:calc(100% + 10px)}:host[right] .inline-help__text:after{border-width:5px 5px 5px 0;border-top-color:transparent!important;border-bottom-color:transparent!important;border-right-color:transparent!important;border-left-color:transparent!important;border-right-color:var(--white)!important;right:100%;left:auto;top:calc(50% - 5px)}:host[bottom] .inline-help__text{bottom:auto;top:calc(100% + 10px)}:host[bottom] .inline-help__text:after{border-top-color:transparent!important;border-bottom-color:transparent!important;border-right-color:transparent!important;border-left-color:transparent!important;border-width:0 5px 5px 5px;border-bottom-color:var(--white)!important;bottom:100%;top:auto;left:calc(50% - 5px)}:host[left] .inline-help__text{bottom:auto;right:calc(100% + 10px)}:host[left] .inline-help__text:after{border-top-color:transparent!important;border-bottom-color:transparent!important;border-right-color:transparent!important;border-left-color:transparent!important;border-width:5px 0 5px 5px;border-left-color:var(--white)!important;left:100%;top:calc(50% - 5px)}:host ::ng-deep .inline-help__text help-title{font-weight:700;display:flex;margin-bottom:8px}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InlineHelpComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cb-inline-help', template: `
		<div class="inline-help" [class.inline-help--visible]="visible" #helpBlock aria-live="assertive">
			<div class="inline-help__icon" (mouseover)="handleMouseOver()" (mouseout)="handleMouseOut()">
				<span #customIcon><ng-content select="icon,.q-icon"></ng-content></span>
				<span *ngIf="!customIcon.children.length" class="q-icon q-icon-help"></span>
			</div>
			<div #helpText
				class="font-normal inline-help__text text-left white-space-normal bg-white border-radius-4 text-gray-1000 p-16"
				[class.inline-help__text--visible]="visible">

				<ng-content select="help-title" class="inline-help__title"></ng-content>
				<ng-content select="help-body" class="inline-help__body"></ng-content>
			</div>
		</div>`, styles: [":host-context(:root,.light-mode){--black-50-percent-fallback: rgba(0, 0, 0, .5)}:host-context(.dark-mode,.application-dark-mode){--black-50-percent-fallback: rgba(255, 255, 255, .5)}.inline-help{align-items:center;display:inline-flex;justify-content:center;margin-left:6px;position:relative;vertical-align:middle;z-index:1;font-family:\"Helvetica Neue\",Helvetica,Arial,sans serif}.inline-help__icon{cursor:help;font-size:16px;line-height:1.5}.inline-help.inline-help--visible{z-index:2}.inline-help__text{font-size:.875rem;box-shadow:0 0 8px var(--black-50-percent, var(--black-50-percent-fallback));position:absolute;transition:opacity .3s;width:300px;box-sizing:border-box;line-height:1.5;bottom:calc(100% + 10px);opacity:0;visibility:hidden}.inline-help__text:after{border-top-color:transparent!important;border-bottom-color:transparent!important;border-right-color:transparent!important;border-left-color:transparent!important;border-style:solid;border-width:5px 5px 0 5px;border-top-color:var(--white)!important;position:absolute;top:100%;left:calc(50% - 5px);content:\"\"}.inline-help__text.inline-help__text--visible{visibility:visible;opacity:1}:host[right] .inline-help__text{bottom:auto;left:calc(100% + 10px)}:host[right] .inline-help__text:after{border-width:5px 5px 5px 0;border-top-color:transparent!important;border-bottom-color:transparent!important;border-right-color:transparent!important;border-left-color:transparent!important;border-right-color:var(--white)!important;right:100%;left:auto;top:calc(50% - 5px)}:host[bottom] .inline-help__text{bottom:auto;top:calc(100% + 10px)}:host[bottom] .inline-help__text:after{border-top-color:transparent!important;border-bottom-color:transparent!important;border-right-color:transparent!important;border-left-color:transparent!important;border-width:0 5px 5px 5px;border-bottom-color:var(--white)!important;bottom:100%;top:auto;left:calc(50% - 5px)}:host[left] .inline-help__text{bottom:auto;right:calc(100% + 10px)}:host[left] .inline-help__text:after{border-top-color:transparent!important;border-bottom-color:transparent!important;border-right-color:transparent!important;border-left-color:transparent!important;border-width:5px 0 5px 5px;border-left-color:var(--white)!important;left:100%;top:calc(50% - 5px)}:host ::ng-deep .inline-help__text help-title{font-weight:700;display:flex;margin-bottom:8px}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i0.ElementRef }]; }, propDecorators: { fixed: [{
                type: Input
            }], container: [{
                type: Input
            }], block: [{
                type: ViewChild,
                args: ['helpBlock', { static: false }]
            }], tooltip: [{
                type: ViewChild,
                args: ['helpText', { static: false }]
            }] } });

class InlineHelpModule {
}
InlineHelpModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InlineHelpModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
InlineHelpModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: InlineHelpModule, declarations: [InlineHelpComponent], imports: [CommonModule], exports: [InlineHelpComponent] });
InlineHelpModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InlineHelpModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InlineHelpModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [InlineHelpComponent],
                    imports: [CommonModule],
                    exports: [InlineHelpComponent],
                }]
        }] });

class LoaderComponent {
}
LoaderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: LoaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
LoaderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: LoaderComponent, selector: "cb-loader", host: { attributes: { "role": "progressbar" } }, ngImport: i0, template: `
		<div class='cb-loader w-100-percent h-100-percent' aria-busy="true" aria-live="polite">
			<div class='spinner'></div>
		</div>`, isInline: true, styles: [".cb-loader .spinner{overflow:hidden!important;margin:30px auto;font-size:5px;position:relative;text-indent:-9999em;transform:translateZ(0);animation:load8 2.1s infinite linear;border-radius:50%;height:10em;width:10em;border-top:1em solid rgba(180,180,180,.3);border-right:1em solid rgba(180,180,180,.3);border-bottom:1em solid rgba(180,180,180,.3);border-left:1em solid rgba(140,140,140,.7)}.cb-loader .spinner,.cb-loader .spinner:after{border-radius:50%;height:10em;width:10em}.cb-loader .spinner.small{border-width:.8em;margin:6px auto}.cb-loader .spinner.small,.cb-loader .spinner.small:after{height:4em;width:4em}.large .cb-loader .spinner{border-width:2em!important;margin:60px auto}.large .cb-loader .spinner,.large .cb-loader .spinner:after{height:30em;width:30em}.cb-loader .spinner.button{border-width:.6em;margin:10px auto}.cb-loader .spinner.button,.cb-loader .spinner.button:after{height:3em;width:3em}@keyframes load8{0%{transform:rotate(0)}to{transform:rotate(360deg)}}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: LoaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cb-loader', template: `
		<div class='cb-loader w-100-percent h-100-percent' aria-busy="true" aria-live="polite">
			<div class='spinner'></div>
		</div>`, host: {
                        role: 'progressbar'
                    }, styles: [".cb-loader .spinner{overflow:hidden!important;margin:30px auto;font-size:5px;position:relative;text-indent:-9999em;transform:translateZ(0);animation:load8 2.1s infinite linear;border-radius:50%;height:10em;width:10em;border-top:1em solid rgba(180,180,180,.3);border-right:1em solid rgba(180,180,180,.3);border-bottom:1em solid rgba(180,180,180,.3);border-left:1em solid rgba(140,140,140,.7)}.cb-loader .spinner,.cb-loader .spinner:after{border-radius:50%;height:10em;width:10em}.cb-loader .spinner.small{border-width:.8em;margin:6px auto}.cb-loader .spinner.small,.cb-loader .spinner.small:after{height:4em;width:4em}.large .cb-loader .spinner{border-width:2em!important;margin:60px auto}.large .cb-loader .spinner,.large .cb-loader .spinner:after{height:30em;width:30em}.cb-loader .spinner.button{border-width:.6em;margin:10px auto}.cb-loader .spinner.button,.cb-loader .spinner.button:after{height:3em;width:3em}@keyframes load8{0%{transform:rotate(0)}to{transform:rotate(360deg)}}\n"] }]
        }] });

// @dynamic
class UuidUtil {
    static uuidv3() {
        return 'xxxxxxxx-xxxx-3xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (symbol) => {
            const randomNumber = window.crypto.getRandomValues(new Uint32Array(1))[0] % 10;
            const changedString = symbol === 'x' ? randomNumber : (randomNumber & 0x3 | 0x9);
            return changedString.toString(16);
        });
    }
}

class ClearableInputComponent {
    constructor() {
        this.disabled = false;
        this.placeholder = '';
        this.clearLabel = 'Clear value';
        this.iconLabel = 'Find';
        this.noBorder = false;
        this.inputLabel = null;
        this.inputId = UuidUtil.uuidv3();
        this.clear = new EventEmitter();
        this.focus = false;
        this.classes = {
            input: [
                'bg-white',
                'border-gray-600',
                'border-radius-4',
                'box-sizing-box',
                'font-standard',
                'h-40',
                'pr-8',
                'pl-16',
                'pv-8',
                'text-base',
                'text-gray-900',
                'w-inherit'
            ]
        };
        // Placeholders for the callbacks which are later provided
        // by the Control Value Accessor
        this.onChangeCallback = ((_) => {
            // intentionally left empty
        });
        this.onTouchedCallback = (() => {
            // intentionally left empty
        });
        this.isValueSet = () => {
            return !!this.value;
        };
        this.clearInput = () => {
            if (!this.disabled) {
                this.valueChangeHandler();
                this.clear.emit();
            }
        };
        this.setFocus = (focus) => {
            this.focus = focus;
        };
    }
    valueChangeHandler(value) {
        this.value = value;
        this.onChangeCallback(value);
        this.onTouchedCallback();
    }
    // ControlValueAccessor
    writeValue(value) {
        if (value && value !== this.value) {
            this.value = value;
            this.onChangeCallback(value);
        }
    }
    registerOnChange(fn) {
        this.onChangeCallback = fn;
    }
    registerOnTouched(fn) {
        this.onTouchedCallback = fn;
    }
}
ClearableInputComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ClearableInputComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ClearableInputComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ClearableInputComponent, selector: "cb-clearable-input", inputs: { disabled: "disabled", placeholder: "placeholder", clearLabel: "clearLabel", iconLabel: "iconLabel", noBorder: "noBorder", inputLabel: "inputLabel", inputId: "inputId" }, outputs: { clear: "clear" }, providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => ClearableInputComponent), multi: true },
    ], ngImport: i0, template: `
		<div class="p-relative w-inherit">
			<label *ngIf="inputLabel" [attr.for]="inputId" class="d-block ml-8 mb-12 font-standard text-base text-gray-900">
				{{inputLabel}}
			</label>
			<div class="w-inherit p-relative d-flex-inline" [ngClass]="{'mt-8': inputLabel !== null}">
				<span *ngIf="!isValueSet() && !focus"
						tabindex="-1"
						class="q-icon q-icon-search search h-40 d-flex align-items-center
				        text-gray-900 p-absolute text-1_5rem"
						aria-hidden="true"
						[attr.aria-label]="'Find'">
				</span>
				<input
					[id]="inputId"
					spellcheck="false"
					[disabled]="disabled"
					[placeholder]="placeholder || ''"
					(focus)="setFocus(true)"
					(focusout)="setFocus(false)"
					tabindex="0"
					class="form-control {{classes.input.join(' ')}}"
					[ngClass]="{'border-0' : this.noBorder, 'border-solid border-1': !this.noBorder}"
					[ngModel]="value"
					[attr.placeholder]="placeholder"
					[attr.aria-label]="placeholder"
					(ngModelChange)="valueChangeHandler($event)">
				<button *ngIf="isValueSet()"
				        (click)="clearInput()"
				        (keydown.enter)="clearInput()"
				        tabindex="0"
				        class="p-absolute border-r-radius-2 cursor-pointer h-40 border-0 bg-none ph-8
				        text-gray-900 hover:text-action-600 focus:text-action-600 text-1_5rem"
				        [class.disabled]="disabled"
				        [attr.aria-label]="clearLabel"
				        [title]="clearLabel">
					<span *ngIf="!disabled" class="q-icon q-icon-delete" aria-hidden="true"></span>
				</button>
			</div>
		</div>
	`, isInline: true, styles: [".text-focus-action-600:focus,.text-focus-action-600:focus-visible{color:var(--action-600)!important}.w-inherit{width:inherit!important}input{padding-right:30px!important;width:inherit}.search{left:12px}button{padding-top:7px;padding-bottom:7px;top:0;right:0}.h-40{height:40px}.text-1_5rem{font-size:1.5rem}\n"], dependencies: [{ kind: "directive", type: i1$1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ClearableInputComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cb-clearable-input', changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => ClearableInputComponent), multi: true },
                    ], template: `
		<div class="p-relative w-inherit">
			<label *ngIf="inputLabel" [attr.for]="inputId" class="d-block ml-8 mb-12 font-standard text-base text-gray-900">
				{{inputLabel}}
			</label>
			<div class="w-inherit p-relative d-flex-inline" [ngClass]="{'mt-8': inputLabel !== null}">
				<span *ngIf="!isValueSet() && !focus"
						tabindex="-1"
						class="q-icon q-icon-search search h-40 d-flex align-items-center
				        text-gray-900 p-absolute text-1_5rem"
						aria-hidden="true"
						[attr.aria-label]="'Find'">
				</span>
				<input
					[id]="inputId"
					spellcheck="false"
					[disabled]="disabled"
					[placeholder]="placeholder || ''"
					(focus)="setFocus(true)"
					(focusout)="setFocus(false)"
					tabindex="0"
					class="form-control {{classes.input.join(' ')}}"
					[ngClass]="{'border-0' : this.noBorder, 'border-solid border-1': !this.noBorder}"
					[ngModel]="value"
					[attr.placeholder]="placeholder"
					[attr.aria-label]="placeholder"
					(ngModelChange)="valueChangeHandler($event)">
				<button *ngIf="isValueSet()"
				        (click)="clearInput()"
				        (keydown.enter)="clearInput()"
				        tabindex="0"
				        class="p-absolute border-r-radius-2 cursor-pointer h-40 border-0 bg-none ph-8
				        text-gray-900 hover:text-action-600 focus:text-action-600 text-1_5rem"
				        [class.disabled]="disabled"
				        [attr.aria-label]="clearLabel"
				        [title]="clearLabel">
					<span *ngIf="!disabled" class="q-icon q-icon-delete" aria-hidden="true"></span>
				</button>
			</div>
		</div>
	`, styles: [".text-focus-action-600:focus,.text-focus-action-600:focus-visible{color:var(--action-600)!important}.w-inherit{width:inherit!important}input{padding-right:30px!important;width:inherit}.search{left:12px}button{padding-top:7px;padding-bottom:7px;top:0;right:0}.h-40{height:40px}.text-1_5rem{font-size:1.5rem}\n"] }]
        }], propDecorators: { disabled: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], clearLabel: [{
                type: Input
            }], iconLabel: [{
                type: Input
            }], noBorder: [{
                type: Input
            }], inputLabel: [{
                type: Input
            }], inputId: [{
                type: Input
            }], clear: [{
                type: Output
            }] } });

class HighlightMatchPipe {
    constructor() {
        this.escapeRegExp = (str) => {
            return str.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, '\\$1');
        };
    }
    transform(item, matchString) {
        if (!matchString) {
            return item;
        }
        return item.replace(new RegExp(`(${this.escapeRegExp(matchString)})`, 'gi'), `<b>$1</b>`);
    }
}
HighlightMatchPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HighlightMatchPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
HighlightMatchPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: HighlightMatchPipe, name: "highlightMatch" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HighlightMatchPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'highlightMatch'
                }]
        }] });

class RecursiveSearchPipe {
    transform(options, filter, property = 'name') {
        if (!options || !filter) {
            return options;
        }
        const lowercaseSearch = filter.toString().toLowerCase();
        const hasTextMatch = (item) => item[property].toString().toLowerCase().includes(lowercaseSearch);
        function hasMatchingDescendant(option) {
            return option.children?.some(hasTextMatch) || option.children?.some(hasMatchingDescendant);
        }
        function crawlList(listOptions, list) {
            listOptions.forEach((option) => {
                if (hasTextMatch(option)) {
                    list.push(option);
                }
                else {
                    if (hasMatchingDescendant(option)) {
                        const newItem = { ...option };
                        newItem.children = [];
                        list.push(newItem);
                        crawlList(option.children, newItem.children);
                    }
                }
            });
            return list;
        }
        const filteredList = crawlList(options, []);
        return filteredList;
    }
}
RecursiveSearchPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RecursiveSearchPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
RecursiveSearchPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: RecursiveSearchPipe, name: "recursiveSearch" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RecursiveSearchPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'recursiveSearch'
                }]
        }] });

class EscapeHtmlPipe {
    static escapeHtml(text) {
        if (!text) {
            return text;
        }
        return String(text).replace(/[&<>"'\/]/g, (s) => {
            return EscapeHtmlPipe.specialCharactersMap[s];
        });
    }
    transform(item) {
        return EscapeHtmlPipe.escapeHtml(item);
    }
}
EscapeHtmlPipe.specialCharactersMap = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    '\'': '&#39;',
    '/': '&#x2F;'
};
EscapeHtmlPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EscapeHtmlPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
EscapeHtmlPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: EscapeHtmlPipe, name: "escapeHtml" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EscapeHtmlPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'escapeHtml'
                }]
        }] });

/* eslint-disable max-len */
/* eslint-enable max-len */
class SidebarNavComponent {
    constructor(elementRef, changeDetector) {
        this.elementRef = elementRef;
        this.changeDetector = changeDetector;
        this.links = [{ name: '', url: null }];
        this.isLoading = true;
        this.isError = false;
        this.isOpenViaKeyboard = false;
        this.linkTextFilter = '';
        // eslint-disable-next-line @angular-eslint/no-input-rename
        this.urlLinks$ = of();
        this.findLabel = 'Find Space';
        this.noResultsLabel = 'No Results Match Query';
        this.closeFn = new EventEmitter();
        // TODO: This does not work after recursivesearch pipe
        // @HostListener('window:keydown.shift.tab', ['$event'])
        // @HostListener('window:keydown.tab', ['$event'])
        this.FOCUSABLE_ELEMENTS = `[tabindex]:not([tabindex='-1'])`;
        this.LOCAL_CLASS = 'local-link';
        // Will return false for callbacks, which will redirecting to href
        this.onClick = ($event, link) => {
            if (!this.isURLLink(link)) {
                $event.preventDefault();
                $event.stopPropagation();
                link.callback();
                return false;
            }
            else {
                return true;
            }
        };
        this.tabOnTheLastItem = (event) => {
            const focusableItems = this.getFocusableItems();
            const lastFocusableItem = focusableItems[focusableItems.length - 1];
            return lastFocusableItem && (event.target === lastFocusableItem);
        };
        this.shiftTabOnTheFirstItem = (event) => {
            const firstFocusableItem = this.getFocusableItems()[0];
            return firstFocusableItem && (event.target === firstFocusableItem);
        };
    }
    ngOnInit() {
        this.getLinks();
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    ngOnChanges(changes) {
        if (changes?.isOpen?.currentValue === true && changes?.isOpen?.previousValue === false) {
            this.focusOnSearchInput();
        }
    }
    focusOnSearchInput() {
        const input = document.getElementById('linkTextFilter');
        input?.focus();
    }
    checkExitingSidebar($event) {
        if (!this.isOpen) {
            return;
        }
        if (this.isTabbingOutOfSidebar($event)) {
            $event.preventDefault();
            this.closeSidebar();
        }
    }
    getLinks() {
        this.isLoading = true;
        this.isError = false;
        this.subscription = this.urlLinks$.subscribe(data => {
            this.links = this.filterLinks(data);
            this.isLoading = false;
            this.changeDetector.detectChanges();
        }, err => {
            this.isLoading = false;
            this.isError = true;
            this.changeDetector.detectChanges();
        });
    }
    ngAfterViewChecked() {
        if (this.keyboardFocus && this.isOpen && !this.isOpenViaKeyboard) {
            this.isOpenViaKeyboard = true;
            setTimeout(() => {
                // Make sure focusable elements are visible before trying to locate them
                // Note: Without setTimeout here, the first item focused is clicked automatically (in Edge only).
                const focusElems = this.getFocusableItems();
                if (focusElems.length > 0) {
                    focusElems[0].focus();
                }
            }, 0);
        }
        if (!this.isOpen) {
            this.isOpenViaKeyboard = false;
        }
    }
    isLocalLink(url) {
        return (url?.substr(0, 4).toLowerCase() !== 'http');
    }
    isLocalTarget(keyboardTarget) {
        return keyboardTarget.classList.contains(this.LOCAL_CLASS);
    }
    getFocusableItems() {
        return this.elementRef.nativeElement.querySelectorAll(this.FOCUSABLE_ELEMENTS);
    }
    isTabbingOutOfSidebar($event) {
        if ($event.key === 'Tab') {
            if ($event.shiftKey) {
                return this.shiftTabOnTheFirstItem($event);
            }
            else {
                return this.tabOnTheLastItem($event);
            }
        }
        return false;
    }
    isOpeningLocalLink($event) {
        return ($event.key === 'Enter' || $event.key === 'Space') && this.isLocalTarget($event.target);
    }
    checkAndCloseSidebar($event) {
        if (!this.isOpen) {
            return;
        }
        if ($event.key === 'Escape') {
            $event.preventDefault();
            this.closeSidebar();
        }
        if (this.isOpeningLocalLink($event)) {
            // doing this without timeout results in page not changing
            setTimeout(() => {
                this.closeSidebar(true);
            });
        }
    }
    closeSidebar(isLocalLink = false) {
        this.isOpenViaKeyboard = false;
        if (this.closeFn) {
            this.closeFn.emit(isLocalLink);
        }
    }
    isURLLink(link) {
        return link && !!(link.url && !link.callback);
    }
    isLinkUnclickable(link) {
        return !(link?.url || link?.callback);
    }
    // filter out any items that do not have a name as those cannot be rendered properly
    filterLinks(links) {
        const level1Links = links.filter(link => !!link.name);
        level1Links.forEach((link) => {
            if (link.children) {
                link.children = link.children.filter(child => !!child.name);
                link.children.forEach(child => {
                    if (child.children) {
                        child.children = child.children.filter(subchild => !!subchild.name);
                    }
                });
            }
        });
        return this.setCollapsedState(level1Links);
    }
    setCollapsedState(links) {
        links.forEach(link => link.collapsed = true);
        return links;
    }
    showLinkExpanded(link) {
        return !link.collapsed || !!this.linkTextFilter.length;
    }
    toggleLink(link) {
        const match = this.links.find(item => (item.name === link.name && (!item.url || item.url === link.url)));
        if (match) {
            const idx = this.links.indexOf(match);
            this.links[idx].collapsed = !this.links[idx].collapsed;
            this.links = [...this.links];
        }
    }
    getToggleAriaLabel(link) {
        const collapsedState = link.collapsed ? 'Collapsed' : 'Expanded';
        return `${link.name} Accordion ${collapsedState}`;
    }
    onClear() {
        this.linkTextFilter = '';
    }
}
SidebarNavComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SidebarNavComponent, deps: [{ token: i0.ElementRef }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
SidebarNavComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: SidebarNavComponent, selector: "cb-sidebar-nav", inputs: { urlLinks$: ["urlLinks", "urlLinks$"], keyboardFocus: "keyboardFocus", isOpen: "isOpen", findLabel: "findLabel", noResultsLabel: "noResultsLabel" }, outputs: { closeFn: "closeFn" }, usesOnChanges: true, ngImport: i0, template: `
<nav class='bg-sidebar-nav font-standard d-flex flex-direction-column w-280 h-100-vh overflow-hidden'
	aria-label="Clarabridge Navigation"
	(keydown)="checkAndCloseSidebar($event)">
	<div class='cb-sidenav__logo d-flex align-items-center ph-16 mb-16' tabindex="-1">
		<svg class="cb-sidenav__image" viewBox="0 0 148 80" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path
				fill-rule="evenodd"
				clip-rule="evenodd"
				d="M107.435 58.7823L124.041 0.0461255H147.823V80H130.858V25.5258H130.646C130.155 28.6344 129.496 31.714 128.672 34.7509C127.915 37.4908 127.103 40.3413 126.227 43.2841L114.234 79.9539H98.6993L86.845 43.2841C86.4207 41.7804 86.0148 40.3598 85.6089 39.1236C85.2029 37.8875 84.8247 36.5314 84.4742 35.1199C84.1236 33.6993 83.7638 32.1617 83.3948 30.5074C83.0258 28.853 82.6292 26.8696 82.2048 24.5572H82.0018V79.9077H51.7804L34.8247 51.9834L18.4502 79.8616H0L25.5535 37.6661L2.26937 0H22.2786L36.559 23.9852L50.0461 0.0461255H89.4188L107.223 58.7823H107.435ZM66.2454 3.22878L45.4059 37.8229L66.2454 71.7251V3.22878Z"
				fill="url(#paint0_linear_339_7818)"/>
			<defs>
				<linearGradient id="paint0_linear_339_7818" x1="-61.0498" y1="33.3738" x2="-13.6685" y2="150.368" gradientUnits="userSpaceOnUse">
					<stop offset="0.04" stop-color="#21DBAA"/>
					<stop offset="0.36" stop-color="#00B4EF"/>
					<stop offset="0.43" stop-color="#01A8EC"/>
					<stop offset="0.57" stop-color="#0487E4"/>
					<stop offset="0.68" stop-color="#0768DD"/>
					<stop offset="0.96" stop-color="#5F1AE5"/>
				</linearGradient>
			</defs>
		</svg>
	</div>
	<cb-loader class='cb-sidenav__loader' aria-label="cb-loader" *ngIf="isLoading"></cb-loader>
	<div *ngIf="!isLoading && !isError" class="d-flex flex-direction-column mb-32">
		<label class="sidebar-label mb-12 ml-16 font-semibold" for="linkTextFilter" (click)="$event.stopPropagation()">{{findLabel}}</label>
		<cb-clearable-input
			(click)="$event.stopPropagation()"
			[(ngModel)]="linkTextFilter"
			[inputId]="'linkTextFilter'"
			[noBorder]="false"
			(clear)="onClear()"
			class="focus-blue-800-outline outline-inset-2 w-100-percent ph-16 box-sizing-box"
		>
		</cb-clearable-input>
	</div>
	<div *ngIf="!isLoading"  class='overflow-y-auto ph-16'>
		<div *ngIf="links && links.length !== 0 && !isError" class="h-0"></div> <!-- Divider at top-->
		<div *ngIf="(links | recursiveSearch:linkTextFilter).length === 0"
		     tabindex="0"
		     [attr.aria-label]="noResultsLabel"
		     class="align-items-center border-0 d-flex focus-outline h-40 no-wrap outline-inset-2 ph-16 sidebar-label text-decoration-none">
			{{noResultsLabel}}
		</div>
		<div *ngFor="let link of links | recursiveSearch:linkTextFilter">
			<div *ngIf="link.children && link.children.length > 0" [attr.aria-expanded]="showLinkExpanded(link)">
				<!--New Link -->
				<div *ngIf="link.children && link.children.length > 0">
					<!-- Toggle Item -->
					<button [attr.aria-label]="getToggleAriaLabel(link)"
					        class='font-semibold cursor-pointer bg-none w-100-percent align-items-center border-0 d-flex font-standard text-1rem
					        focus-outline h-40 no-wrap outline-inset-2 pr-16 pl-16 border-radius-8 text-gray-1000 text-decoration-underline'
					        tabindex="0"
					        (click)="$event.stopPropagation(); toggleLink(link)">
						<span class="q-icon pr-8 text-1_5rem"
						   [ngClass]="{
						   'q-icon-angle-bottom': showLinkExpanded(link),
						   'q-icon-angle-right': !showLinkExpanded(link)
						   }">
						</span>
						<span
							class="text-overflow-ellipsis overflow-hidden"
							[innerHTML]="link.name | escapeHtml | highlightMatch:linkTextFilter"></span>
					</button>
				</div>
				<!--Old Link -->
				<div *ngIf="showLinkExpanded(link)">
					<div *ngIf="!isLinkUnclickable(link)">
						<a class='pr-16 pl-48 hover:bg-blue-200 text-gray-1000 hover:text-blue-1000 border-radius-8
						align-items-center border-0 d-flex focus-outline h-40 no-wrap outline-inset-2 ph-16 text-decoration-underline'
						   [tabindex]="0"
						   [ngClass]="{'local-link': isLocalLink(link.url)}"
						   (click)="onClick($event, link)" href='{{link.url ? link.url : "javascript:void()"}}'>
							<span class="text-overflow-ellipsis overflow-hidden">Home</span>
						</a>
					</div>
					<ul *ngIf="link.children && link.children.length" class='p-0 m-0 list-style-none'>
						<li *ngFor="let child of link.children">
							<a [tabindex]="isLinkUnclickable(child) ? -1 : 0"
							   class='pl-48 hover:bg-blue-200 text-gray-1000 hover:text-blue-1000 border-radius-8
							   align-items-center border-0 d-flex focus-outline h-40 no-wrap outline-inset-2 ph-16 text-decoration-underline'
							   [ngClass]="{'local-link': isLocalLink(child.url), 'cursor-default' : isLinkUnclickable(child)}"
							   (click)="onClick($event, child)" href='{{child.url ? child.url : "javascript:void()"}}'>
						<span
							class="text-overflow-ellipsis overflow-hidden" [innerHTML]="child.name | escapeHtml | highlightMatch:linkTextFilter"></span>
							</a>
							<ul *ngIf="child.children" class='p-0 m-0 list-style-none'>
								<li *ngFor="let subchild of child.children">
									<a class='pl-56 hover:bg-blue-200 text-gray-1000 hover:text-blue-1000 border-radius-8
									align-items-center border-0 d-flex focus-outline h-40 no-wrap outline-inset-2 ph-16 text-decoration-underline'
									   [tabindex]="isLinkUnclickable(subchild) ? -1 : 0"
									   [ngClass]="{'local-link': isLocalLink(subchild.url), 'cursor-default' : isLinkUnclickable(subchild)}"
									   (click)="onClick($event, subchild)" href='{{subchild.url ? subchild.url : "javascript:void()"}}'>
								<span
									class="text-overflow-ellipsis overflow-hidden" [innerHTML]="subchild.name | escapeHtml | highlightMatch:linkTextFilter"></span>
									</a>
								</li>
							</ul>
						</li>
					</ul>
				</div>

			</div>
			<div *ngIf="(!link.children || link.children.length === 0) && link.name">
				<div>
					<a class='pr-16 pl-48 hover:bg-blue-200 text-gray-1000 hover:text-blue-1000 border-radius-8 font-semibold
					align-items-center border-0 d-flex focus-outline h-40 no-wrap outline-inset-2 ph-16 text-decoration-underline'
					   [tabindex]="isLinkUnclickable(link) ? -1 : 0"
					   [ngClass]="{'local-link': isLocalLink(link.url), 'cursor-default' : isLinkUnclickable(link)}"
					   (click)="onClick($event, link)" href='{{link.url ? link.url : "javascript:void()"}}'>
					<span
						class="text-overflow-ellipsis overflow-hidden" [innerHTML]="link.name | escapeHtml | highlightMatch:linkTextFilter"></span>
					</a>
				</div>
			</div>
		</div>
	</div>
	<div *ngIf="isError" class="p-16 d-flex sidebar-label align-items-center flex-direction-column mt-48 justify-between">
		<p class="font-bold m-0 mb-24">Server Error. Please try again.</p>
		<button type="button" aria-label="Refresh" (click)="getLinks()"
		        class="bg-none sidebar-label hover:text-blue-600 d-table btn btn-secondary btn-sm
		        border-gray-200 hover:border-blue-600">
			Refresh
		</button>
	</div>
</nav>`, isInline: true, styles: ["*{scrollbar-color:#8f99a0 #eceeef;scrollbar-width:thin}::-webkit-scrollbar{-webkit-appearance:none;width:8px;height:8px}::-webkit-scrollbar-thumb{background-color:#8f99a0;margin-right:1px;border-radius:0}::-webkit-scrollbar-track{background-color:#eceeef;-webkit-box-shadow:none;border-radius:0}.btn{border-width:1px;border-style:solid;height:32px;cursor:pointer;font-size:16px;display:inline-flex;align-items:center;justify-content:center;padding-left:16px;padding-right:16px;border-radius:2px}.btn.btn-sm,.btn-group-sm .btn{height:24px;font-size:14px;padding-left:8px;padding-right:8px}.btn+.btn{margin-left:4px}.btn:hover{background:var(--white);color:var(--action-600);border-color:var(--action-600);z-index:1}.btn-primary{border-color:var(--action-600);color:var(--white);background:var(--action-600)}.btn-secondary{color:var(--gray-1000);background:var(--white)}.cb-sidenav__logo{flex:0 0 56px}.cb-sidenav__image{height:24px}nav{left:0;top:0}.list-style-none{list-style:none}.text-decoration-none{text-decoration:none}.no-wrap{white-space:nowrap}.text-overflow-ellipsis{text-overflow:ellipsis}.h-fit-content{height:fit-content!important}.w-fit-content{width:fit-content!important}.pl-56{padding-left:56px!important}.pl-38{padding-left:38px!important}.outline-inset-2{outline-offset:-2px!important}.h-40{height:40px!important}.focus-blue-800-outline:focus,.focus-blue-800-outline:focus-visible{outline:solid 2px var(--blue-800)!important}.focus-gray-outline:focus,.focus-white-outline:focus-visible{outline:solid 2px var(--gray-900)!important}.bg-sidebar-nav{background-color:var(--white)!important}.sidebar-label{color:var(--gray-1000, #2A2A2A)}.text-hover-gray-900:hover{color:var(--gray-900)!important}.focus-outline:focus,.focus-outline:focus-visible{outline:solid 2px var(--gray-900)!important}.text-1_5rem{font-size:var(--font-size-1_5rem, 1.5rem)}.text-1rem{font-size:var(--font-size-1rem, 1rem)}.mb-12{margin-bottom:12px}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: LoaderComponent, selector: "cb-loader" }, { kind: "directive", type: i1$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: ClearableInputComponent, selector: "cb-clearable-input", inputs: ["disabled", "placeholder", "clearLabel", "iconLabel", "noBorder", "inputLabel", "inputId"], outputs: ["clear"] }, { kind: "pipe", type: HighlightMatchPipe, name: "highlightMatch" }, { kind: "pipe", type: RecursiveSearchPipe, name: "recursiveSearch" }, { kind: "pipe", type: EscapeHtmlPipe, name: "escapeHtml" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SidebarNavComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cb-sidebar-nav', template: `
<nav class='bg-sidebar-nav font-standard d-flex flex-direction-column w-280 h-100-vh overflow-hidden'
	aria-label="Clarabridge Navigation"
	(keydown)="checkAndCloseSidebar($event)">
	<div class='cb-sidenav__logo d-flex align-items-center ph-16 mb-16' tabindex="-1">
		<svg class="cb-sidenav__image" viewBox="0 0 148 80" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path
				fill-rule="evenodd"
				clip-rule="evenodd"
				d="M107.435 58.7823L124.041 0.0461255H147.823V80H130.858V25.5258H130.646C130.155 28.6344 129.496 31.714 128.672 34.7509C127.915 37.4908 127.103 40.3413 126.227 43.2841L114.234 79.9539H98.6993L86.845 43.2841C86.4207 41.7804 86.0148 40.3598 85.6089 39.1236C85.2029 37.8875 84.8247 36.5314 84.4742 35.1199C84.1236 33.6993 83.7638 32.1617 83.3948 30.5074C83.0258 28.853 82.6292 26.8696 82.2048 24.5572H82.0018V79.9077H51.7804L34.8247 51.9834L18.4502 79.8616H0L25.5535 37.6661L2.26937 0H22.2786L36.559 23.9852L50.0461 0.0461255H89.4188L107.223 58.7823H107.435ZM66.2454 3.22878L45.4059 37.8229L66.2454 71.7251V3.22878Z"
				fill="url(#paint0_linear_339_7818)"/>
			<defs>
				<linearGradient id="paint0_linear_339_7818" x1="-61.0498" y1="33.3738" x2="-13.6685" y2="150.368" gradientUnits="userSpaceOnUse">
					<stop offset="0.04" stop-color="#21DBAA"/>
					<stop offset="0.36" stop-color="#00B4EF"/>
					<stop offset="0.43" stop-color="#01A8EC"/>
					<stop offset="0.57" stop-color="#0487E4"/>
					<stop offset="0.68" stop-color="#0768DD"/>
					<stop offset="0.96" stop-color="#5F1AE5"/>
				</linearGradient>
			</defs>
		</svg>
	</div>
	<cb-loader class='cb-sidenav__loader' aria-label="cb-loader" *ngIf="isLoading"></cb-loader>
	<div *ngIf="!isLoading && !isError" class="d-flex flex-direction-column mb-32">
		<label class="sidebar-label mb-12 ml-16 font-semibold" for="linkTextFilter" (click)="$event.stopPropagation()">{{findLabel}}</label>
		<cb-clearable-input
			(click)="$event.stopPropagation()"
			[(ngModel)]="linkTextFilter"
			[inputId]="'linkTextFilter'"
			[noBorder]="false"
			(clear)="onClear()"
			class="focus-blue-800-outline outline-inset-2 w-100-percent ph-16 box-sizing-box"
		>
		</cb-clearable-input>
	</div>
	<div *ngIf="!isLoading"  class='overflow-y-auto ph-16'>
		<div *ngIf="links && links.length !== 0 && !isError" class="h-0"></div> <!-- Divider at top-->
		<div *ngIf="(links | recursiveSearch:linkTextFilter).length === 0"
		     tabindex="0"
		     [attr.aria-label]="noResultsLabel"
		     class="align-items-center border-0 d-flex focus-outline h-40 no-wrap outline-inset-2 ph-16 sidebar-label text-decoration-none">
			{{noResultsLabel}}
		</div>
		<div *ngFor="let link of links | recursiveSearch:linkTextFilter">
			<div *ngIf="link.children && link.children.length > 0" [attr.aria-expanded]="showLinkExpanded(link)">
				<!--New Link -->
				<div *ngIf="link.children && link.children.length > 0">
					<!-- Toggle Item -->
					<button [attr.aria-label]="getToggleAriaLabel(link)"
					        class='font-semibold cursor-pointer bg-none w-100-percent align-items-center border-0 d-flex font-standard text-1rem
					        focus-outline h-40 no-wrap outline-inset-2 pr-16 pl-16 border-radius-8 text-gray-1000 text-decoration-underline'
					        tabindex="0"
					        (click)="$event.stopPropagation(); toggleLink(link)">
						<span class="q-icon pr-8 text-1_5rem"
						   [ngClass]="{
						   'q-icon-angle-bottom': showLinkExpanded(link),
						   'q-icon-angle-right': !showLinkExpanded(link)
						   }">
						</span>
						<span
							class="text-overflow-ellipsis overflow-hidden"
							[innerHTML]="link.name | escapeHtml | highlightMatch:linkTextFilter"></span>
					</button>
				</div>
				<!--Old Link -->
				<div *ngIf="showLinkExpanded(link)">
					<div *ngIf="!isLinkUnclickable(link)">
						<a class='pr-16 pl-48 hover:bg-blue-200 text-gray-1000 hover:text-blue-1000 border-radius-8
						align-items-center border-0 d-flex focus-outline h-40 no-wrap outline-inset-2 ph-16 text-decoration-underline'
						   [tabindex]="0"
						   [ngClass]="{'local-link': isLocalLink(link.url)}"
						   (click)="onClick($event, link)" href='{{link.url ? link.url : "javascript:void()"}}'>
							<span class="text-overflow-ellipsis overflow-hidden">Home</span>
						</a>
					</div>
					<ul *ngIf="link.children && link.children.length" class='p-0 m-0 list-style-none'>
						<li *ngFor="let child of link.children">
							<a [tabindex]="isLinkUnclickable(child) ? -1 : 0"
							   class='pl-48 hover:bg-blue-200 text-gray-1000 hover:text-blue-1000 border-radius-8
							   align-items-center border-0 d-flex focus-outline h-40 no-wrap outline-inset-2 ph-16 text-decoration-underline'
							   [ngClass]="{'local-link': isLocalLink(child.url), 'cursor-default' : isLinkUnclickable(child)}"
							   (click)="onClick($event, child)" href='{{child.url ? child.url : "javascript:void()"}}'>
						<span
							class="text-overflow-ellipsis overflow-hidden" [innerHTML]="child.name | escapeHtml | highlightMatch:linkTextFilter"></span>
							</a>
							<ul *ngIf="child.children" class='p-0 m-0 list-style-none'>
								<li *ngFor="let subchild of child.children">
									<a class='pl-56 hover:bg-blue-200 text-gray-1000 hover:text-blue-1000 border-radius-8
									align-items-center border-0 d-flex focus-outline h-40 no-wrap outline-inset-2 ph-16 text-decoration-underline'
									   [tabindex]="isLinkUnclickable(subchild) ? -1 : 0"
									   [ngClass]="{'local-link': isLocalLink(subchild.url), 'cursor-default' : isLinkUnclickable(subchild)}"
									   (click)="onClick($event, subchild)" href='{{subchild.url ? subchild.url : "javascript:void()"}}'>
								<span
									class="text-overflow-ellipsis overflow-hidden" [innerHTML]="subchild.name | escapeHtml | highlightMatch:linkTextFilter"></span>
									</a>
								</li>
							</ul>
						</li>
					</ul>
				</div>

			</div>
			<div *ngIf="(!link.children || link.children.length === 0) && link.name">
				<div>
					<a class='pr-16 pl-48 hover:bg-blue-200 text-gray-1000 hover:text-blue-1000 border-radius-8 font-semibold
					align-items-center border-0 d-flex focus-outline h-40 no-wrap outline-inset-2 ph-16 text-decoration-underline'
					   [tabindex]="isLinkUnclickable(link) ? -1 : 0"
					   [ngClass]="{'local-link': isLocalLink(link.url), 'cursor-default' : isLinkUnclickable(link)}"
					   (click)="onClick($event, link)" href='{{link.url ? link.url : "javascript:void()"}}'>
					<span
						class="text-overflow-ellipsis overflow-hidden" [innerHTML]="link.name | escapeHtml | highlightMatch:linkTextFilter"></span>
					</a>
				</div>
			</div>
		</div>
	</div>
	<div *ngIf="isError" class="p-16 d-flex sidebar-label align-items-center flex-direction-column mt-48 justify-between">
		<p class="font-bold m-0 mb-24">Server Error. Please try again.</p>
		<button type="button" aria-label="Refresh" (click)="getLinks()"
		        class="bg-none sidebar-label hover:text-blue-600 d-table btn btn-secondary btn-sm
		        border-gray-200 hover:border-blue-600">
			Refresh
		</button>
	</div>
</nav>`, changeDetection: ChangeDetectionStrategy.OnPush, styles: ["*{scrollbar-color:#8f99a0 #eceeef;scrollbar-width:thin}::-webkit-scrollbar{-webkit-appearance:none;width:8px;height:8px}::-webkit-scrollbar-thumb{background-color:#8f99a0;margin-right:1px;border-radius:0}::-webkit-scrollbar-track{background-color:#eceeef;-webkit-box-shadow:none;border-radius:0}.btn{border-width:1px;border-style:solid;height:32px;cursor:pointer;font-size:16px;display:inline-flex;align-items:center;justify-content:center;padding-left:16px;padding-right:16px;border-radius:2px}.btn.btn-sm,.btn-group-sm .btn{height:24px;font-size:14px;padding-left:8px;padding-right:8px}.btn+.btn{margin-left:4px}.btn:hover{background:var(--white);color:var(--action-600);border-color:var(--action-600);z-index:1}.btn-primary{border-color:var(--action-600);color:var(--white);background:var(--action-600)}.btn-secondary{color:var(--gray-1000);background:var(--white)}.cb-sidenav__logo{flex:0 0 56px}.cb-sidenav__image{height:24px}nav{left:0;top:0}.list-style-none{list-style:none}.text-decoration-none{text-decoration:none}.no-wrap{white-space:nowrap}.text-overflow-ellipsis{text-overflow:ellipsis}.h-fit-content{height:fit-content!important}.w-fit-content{width:fit-content!important}.pl-56{padding-left:56px!important}.pl-38{padding-left:38px!important}.outline-inset-2{outline-offset:-2px!important}.h-40{height:40px!important}.focus-blue-800-outline:focus,.focus-blue-800-outline:focus-visible{outline:solid 2px var(--blue-800)!important}.focus-gray-outline:focus,.focus-white-outline:focus-visible{outline:solid 2px var(--gray-900)!important}.bg-sidebar-nav{background-color:var(--white)!important}.sidebar-label{color:var(--gray-1000, #2A2A2A)}.text-hover-gray-900:hover{color:var(--gray-900)!important}.focus-outline:focus,.focus-outline:focus-visible{outline:solid 2px var(--gray-900)!important}.text-1_5rem{font-size:var(--font-size-1_5rem, 1.5rem)}.text-1rem{font-size:var(--font-size-1rem, 1rem)}.mb-12{margin-bottom:12px}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { urlLinks$: [{
                type: Input,
                args: ['urlLinks']
            }], keyboardFocus: [{
                type: Input
            }], isOpen: [{
                type: Input
            }], findLabel: [{
                type: Input
            }], noResultsLabel: [{
                type: Input
            }], closeFn: [{
                type: Output
            }] } });

class LoaderModule {
}
LoaderModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: LoaderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
LoaderModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: LoaderModule, declarations: [LoaderComponent], imports: [CommonModule], exports: [LoaderComponent] });
LoaderModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: LoaderModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: LoaderModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [LoaderComponent],
                    imports: [
                        CommonModule
                    ],
                    exports: [LoaderComponent]
                }]
        }] });

class SearchPipe {
    transform(options, filter, property = 'displayName') {
        if (!options || !filter) {
            return options;
        }
        return options.filter((option) => {
            return option[property] && option[property].toString().toLowerCase().includes(filter.toString().toLowerCase());
        });
    }
}
SearchPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SearchPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
SearchPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: SearchPipe, name: "search" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SearchPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'search'
                }]
        }] });

class SearchableListComponent {
    constructor(elementRef) {
        this.elementRef = elementRef;
        this.searchValue = '';
        this.classes = {
            listItem: [
                'active-text-white',
                'align-items-center',
                'bg-active-action-800',
                'bg-focus-action-700',
                'hover:bg-action-700',
                'box-sizing-box',
                'cursor-pointer',
                'd-block',
                'h-min-32',
                'focus:text-white',
                'focus-outline-default',
                'hover:text-white',
                'line-height-16',
                'ph-16',
                'pv-8',
                'text-decoration-none',
                'text-wrap',
                'w-100-percent',
                'whitespace-pre-line'
            ]
        };
        this.options = []; // could be anything, to make this component reusable
        this.displayProperty = 'displayName';
        this.allowTabOut = true;
        this.noItemsLabel = 'No items available';
        this.noResultsLabel = 'No results found';
        this.filterLabel = 'Filter items';
        this.selectOption = new EventEmitter();
    }
    filterSortOptions(options) {
        return options
            .filter((option) => option[this.displayProperty] && option[this.displayProperty].trim().length !== 0)
            .sort((a, b) => a[this.displayProperty].localeCompare(b[this.displayProperty]));
    }
    showNoSearchResults() {
        return this.options && this.searchValue !== null && (this.searchValue.trim().length > 0) && !this.options.some(option => option[this.displayProperty] &&
            (option[this.displayProperty]).toLowerCase().indexOf(this.searchValue.trim().toLowerCase()) !== -1);
    }
    focusOnSearchField() {
        setTimeout(() => {
            this.searchInput.nativeElement.focus();
        }, 0);
    }
    focusOnLastItem($event) {
        if (!this.allowTabOut) {
            $event.preventDefault();
            const listItems = this.elementRef.nativeElement.querySelectorAll('.focusable');
            listItems[listItems.length - 1]?.focus();
        }
    }
    keyboardNavigate($event, index, count) {
        $event.stopPropagation();
        if (!this.allowTabOut && index === count - 1) {
            this.focusOnSearchField();
        }
    }
    arrowNavigate($event, nextIndex, count) {
        $event.stopPropagation();
        $event.preventDefault();
        const listItems = this.elementRef.nativeElement.querySelectorAll('.focusable');
        if (nextIndex < count && nextIndex >= 0) {
            listItems[nextIndex].focus();
        }
    }
}
SearchableListComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SearchableListComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
SearchableListComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: SearchableListComponent, selector: "cb-searchable-list", inputs: { options: "options", displayProperty: "displayProperty", allowTabOut: "allowTabOut", noItemsLabel: "noItemsLabel", noResultsLabel: "noResultsLabel", filterLabel: "filterLabel" }, outputs: { selectOption: "selectOption" }, viewQueries: [{ propertyName: "searchInput", first: true, predicate: ["searchInput"], descendants: true }], ngImport: i0, template: "<div class=\"pv-8 border-radius-inherit\">\r\n\t<div *ngIf=\"options.length > 0\" class=\"d-flex flex-direction-column mt-8 ph-16\">\r\n\t\t<label class=\"ml-8 mb-8 text-base text-gray-900\" for=\"dropdownSearch\">{{filterLabel}}</label>\r\n\t\t<input class=\"search p-8 text-base border-1 border-solid border-radius-2 bg-white\r\n\t\t\tborder-gray-800 outline-none text-gray-900\"\r\n\t\t\t   #searchInput\r\n\t\t\t   autocomplete=\"off\"\r\n\t\t\t   (keydown.shift.tab)=\"focusOnLastItem($event)\"\r\n\t\t\t   [(ngModel)]=\"searchValue\"\r\n\t\t\t   type=\"search\" id=\"dropdownSearch\">\r\n\t</div>\r\n\t<hr *ngIf=\"options.length > 0\" class=\"mv-8 border-b-0 border-t-1 border-t-solid\r\n\t\tborder-gray-800\" role=\"separator\">\r\n\t<ul class=\"p-0 m-0 h-max-75-vh overflow-y-auto\">\r\n\t\t<li *ngFor=\"let option of filterSortOptions(options) | search:searchValue:displayProperty; let i = index; let count = count\"\r\n\t\t\tclass=\"list-none text-gray-900\">\r\n\t\t\t<a class=\"{{classes.listItem.join(' ')}} focusable\"\r\n\t\t\t   tabindex=\"0\" href=\"javascript:void(0)\"\r\n\t\t\t   (keydown.tab)=\"keyboardNavigate($event, i, count)\"\r\n\t\t\t   (keydown.arrowDown)=\"arrowNavigate($event, i + 1, count)\"\r\n\t\t\t   (keydown.arrowUp)=\"arrowNavigate($event, i - 1, count)\"\r\n\t\t\t   (click)=\"this.selectOption.emit(option)\"\r\n\t\t\t   [innerHTML]=\"option[this.displayProperty] | highlightMatch:searchValue\"></a>\r\n\t\t</li>\r\n\t</ul>\r\n\t<p class=\"font-italic ph-16 mv-8\" *ngIf=\"options.length === 0\">\r\n\t\t{{noItemsLabel}}\r\n\t</p>\r\n\t<div aria-live=\"assertive\">\r\n\t\t<p class=\"font-italic ph-16 mv-8\"\r\n\t\t   *ngIf=\"options.length !== 0 && showNoSearchResults()\" attr.aria-live=\"assertive\">\r\n\r\n\t\t\t{{noResultsLabel}}\r\n\t\t</p>\r\n\t</div>\r\n</div>\r\n", styles: ["a{color:inherit!important}a:focus,a:focus-visible{outline-offset:-2px}.text-wrap{white-space:normal!important}.line-height-16{line-height:16px!important}.text-decoration-none{text-decoration:none!important}.whitespace-pre-line{white-space:pre-line!important}.bg-focus-primary-color-300:focus-visible,.bg-focus-primary-color-300:focus{background-color:var(--action-400)!important}.bg-active-action-800:active{background-color:var(--action-800)!important}.active-text-white:active,.hover-text-white:hover{color:var(--white)!important}.font-italic{font-style:italic!important}.focus\\:text-white:focus,.focus\\:text-white:focus-visible{color:var(--white)!important}.bg-focus-action-700:focus,.bg-focus-action-700:focus-visible{background-color:var(--action-700, var(--blue-700))}.focus-outline-default:focus,.focus-outline-default:focus-visible{outline:solid 2px var(--focus-outline-color)}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "pipe", type: SearchPipe, name: "search" }, { kind: "pipe", type: HighlightMatchPipe, name: "highlightMatch" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SearchableListComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cb-searchable-list', template: "<div class=\"pv-8 border-radius-inherit\">\r\n\t<div *ngIf=\"options.length > 0\" class=\"d-flex flex-direction-column mt-8 ph-16\">\r\n\t\t<label class=\"ml-8 mb-8 text-base text-gray-900\" for=\"dropdownSearch\">{{filterLabel}}</label>\r\n\t\t<input class=\"search p-8 text-base border-1 border-solid border-radius-2 bg-white\r\n\t\t\tborder-gray-800 outline-none text-gray-900\"\r\n\t\t\t   #searchInput\r\n\t\t\t   autocomplete=\"off\"\r\n\t\t\t   (keydown.shift.tab)=\"focusOnLastItem($event)\"\r\n\t\t\t   [(ngModel)]=\"searchValue\"\r\n\t\t\t   type=\"search\" id=\"dropdownSearch\">\r\n\t</div>\r\n\t<hr *ngIf=\"options.length > 0\" class=\"mv-8 border-b-0 border-t-1 border-t-solid\r\n\t\tborder-gray-800\" role=\"separator\">\r\n\t<ul class=\"p-0 m-0 h-max-75-vh overflow-y-auto\">\r\n\t\t<li *ngFor=\"let option of filterSortOptions(options) | search:searchValue:displayProperty; let i = index; let count = count\"\r\n\t\t\tclass=\"list-none text-gray-900\">\r\n\t\t\t<a class=\"{{classes.listItem.join(' ')}} focusable\"\r\n\t\t\t   tabindex=\"0\" href=\"javascript:void(0)\"\r\n\t\t\t   (keydown.tab)=\"keyboardNavigate($event, i, count)\"\r\n\t\t\t   (keydown.arrowDown)=\"arrowNavigate($event, i + 1, count)\"\r\n\t\t\t   (keydown.arrowUp)=\"arrowNavigate($event, i - 1, count)\"\r\n\t\t\t   (click)=\"this.selectOption.emit(option)\"\r\n\t\t\t   [innerHTML]=\"option[this.displayProperty] | highlightMatch:searchValue\"></a>\r\n\t\t</li>\r\n\t</ul>\r\n\t<p class=\"font-italic ph-16 mv-8\" *ngIf=\"options.length === 0\">\r\n\t\t{{noItemsLabel}}\r\n\t</p>\r\n\t<div aria-live=\"assertive\">\r\n\t\t<p class=\"font-italic ph-16 mv-8\"\r\n\t\t   *ngIf=\"options.length !== 0 && showNoSearchResults()\" attr.aria-live=\"assertive\">\r\n\r\n\t\t\t{{noResultsLabel}}\r\n\t\t</p>\r\n\t</div>\r\n</div>\r\n", styles: ["a{color:inherit!important}a:focus,a:focus-visible{outline-offset:-2px}.text-wrap{white-space:normal!important}.line-height-16{line-height:16px!important}.text-decoration-none{text-decoration:none!important}.whitespace-pre-line{white-space:pre-line!important}.bg-focus-primary-color-300:focus-visible,.bg-focus-primary-color-300:focus{background-color:var(--action-400)!important}.bg-active-action-800:active{background-color:var(--action-800)!important}.active-text-white:active,.hover-text-white:hover{color:var(--white)!important}.font-italic{font-style:italic!important}.focus\\:text-white:focus,.focus\\:text-white:focus-visible{color:var(--white)!important}.bg-focus-action-700:focus,.bg-focus-action-700:focus-visible{background-color:var(--action-700, var(--blue-700))}.focus-outline-default:focus,.focus-outline-default:focus-visible{outline:solid 2px var(--focus-outline-color)}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { options: [{
                type: Input
            }], displayProperty: [{
                type: Input
            }], allowTabOut: [{
                type: Input
            }], noItemsLabel: [{
                type: Input
            }], noResultsLabel: [{
                type: Input
            }], filterLabel: [{
                type: Input
            }], selectOption: [{
                type: Output
            }], searchInput: [{
                type: ViewChild,
                args: ['searchInput', { static: false }]
            }] } });

var SearchConstants;
(function (SearchConstants) {
    SearchConstants["TITLE"] = "Find Filters";
})(SearchConstants || (SearchConstants = {}));
var DropdownPlacement;
(function (DropdownPlacement) {
    DropdownPlacement["RIGHT"] = "bottom-right";
    DropdownPlacement["LEFT"] = "bottom-left";
    DropdownPlacement["TOP"] = "top-start";
})(DropdownPlacement || (DropdownPlacement = {}));

class SelectComponent {
    constructor() {
        this.selectionMade = new EventEmitter();
        // eslint-disable-next-line @angular-eslint/no-input-rename
        this.placeholderForSelect = 'Select'; // This custom placeholder for select
        this.options = [];
        this.hasSearch = false; // This variable show search field if they try
        this.disabled = false;
        this.placement = DropdownPlacement.LEFT;
        this.onChange = ((_) => {
            // intentionally left empty
        });
        this.onTouched = (() => {
            // intentionally left empty
        });
        this.searchValue = ''; // It's value that help filter select options
        this.dropdownSearchId = '';
        this.SearchConstants = SearchConstants;
        this.MAX_ELEMENT_WITHOUT_SEARCH_FIELD = 10; // It's threshold of showing search field
    }
    ngOnInit() {
        this.dropdownSearchId = this.generateUniqueId('dropdownSearch');
    }
    ngOnChanges(changes) {
        if (changes?.options?.currentValue?.length) {
            // Leave unselected if not found
            this.setDefault();
        }
    }
    // Sets to default element
    setDefault() {
        for (const element of this.options) {
            if (element.default) {
                this.selectOption(element);
                break;
            }
        }
    }
    // This method return displayName for selected option or placeholder
    get selectedName() {
        return this.selectedOption ? this.selectedOption.displayName : this.placeholderForSelect;
    }
    // This method write current value for selected option
    selectOption(option, e) {
        if (e) {
            e.preventDefault();
        }
        this.writeValue(option.value);
        this.onTouched();
        this.dropdown.close();
    }
    // This method help disable html tags
    get isDisabled() {
        return this.disabled || !this.options || !this.options.length;
    }
    // This method shown search field if condition will be true
    get showSearchField() {
        if (this.options) {
            return this.hasSearch || this.options?.length > this.MAX_ELEMENT_WITHOUT_SEARCH_FIELD;
        }
        return this.hasSearch;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    // This method emit event with `selectValue` or rewrite current value to selected value if used form control
    writeValue(value) {
        if (!this.options || !this.options.length) {
            return;
        }
        const selectedOption = this.options.find((selected) => selected?.value === value);
        if (selectedOption) {
            this.selectedOption = selectedOption;
            this.onChange(value);
            this.selectionMade.emit(value);
        }
    }
    // This method generate unique id for the html element
    generateUniqueId(elementName) {
        return `${elementName}-${UuidUtil.uuidv3()}`;
    }
}
SelectComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SelectComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SelectComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: SelectComponent, selector: "cb-select", inputs: { placeholderForSelect: ["placeholder", "placeholderForSelect"], options: "options", customWidth: "customWidth", hasSearch: "hasSearch", disabled: "disabled", placement: "placement" }, outputs: { selectionMade: "selectionMade" }, providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SelectComponent),
            multi: true,
        }], viewQueries: [{ propertyName: "dropdown", first: true, predicate: ["dropdown"], descendants: true, static: true }], usesOnChanges: true, ngImport: i0, template: `
		<div ngbDropdown class="d-inline-block font-standard text-base" #dropdown="ngbDropdown" placement="{{placement}}">
		<button
			ngbDropdownToggle
			id="dropdownManual"
			class="btn d-flex justify-content-between align-items-center h-min-32 text-gray-900 border-1 border-radius-2"
			type="button"
			[ngClass]="{
				'bg-gray-200 border-transparent cursor-not-allowed': isDisabled,
				'bg-white border-gray-300': !isDisabled,
				'w-200': !customWidth
			}"
			[ngStyle]="{ width: customWidth }"
			[disabled]="isDisabled"
		>
			<span class="overflow-hidden text-ellipsis" [title]="selectedName">{{ selectedName }}</span>
		</button>
		<div
			ngbDropdownMenu
			class="bg-white w-max-320 w-min-240 mt-8 pv-8 border-0 border-radius-4 box-shadow-default"
		>
			<ng-container *ngIf="showSearchField">
				<div class="d-flex flex-direction-column mt-8 ph-16">
					<label [for]="dropdownSearchId" class="mb-8 text-base text-gray-900 font-bold">{{ SearchConstants.TITLE }}</label>
					<input
						ngbDropdownAnchor
						[id]="dropdownSearchId"
						type="search"
						class="search p-8 text-base border-1 border-radius-2 border-solid
							border-gray-900 outline-none border-gray-900 bg-white text-gray-900"
						[(ngModel)]="searchValue"
					/>
				</div>
				<hr class="mv-8 bg-gray-900" role="separator" />
			</ng-container>

			<div class="h-max-200 overflow-auto">
				<a
					ngbDropdownItem
					class="h-min-32 d-flex align-items-center ph-16 pv-4 hover:bg-action-600 hover:text-white cursor-pointer"
					*ngFor="let option of options | search: searchValue"
					[ngClass]="{ 'bg-action-800 text-white': option.displayName === selectedName, 'text-gray-900': option.displayName !== selectedName}"
					[attr.disabled]="option.disabled"
					[title]="option.displayName"
					(click)="selectOption(option, $event)"
					[attr.href]="option.disabled ? null: 'javascript:void(0)'"
					[attr.aria-label]="option.disabled ? (option.disabledAriaLabel ? option.disabledAriaLabel : 'disabled') : option.displayName"
					tabindex="0"
					role="button"
				>
					<span class="overflow-hidden text-ellipsis">{{ option.displayName }}</span>
				</a>
			</div>
		</div>
	</div>`, isInline: true, styles: [".btn{box-shadow:none!important}.dropdown-toggle:after{content:\"\\f117\"!important;font-family:var(--qualtrics-icons)!important;font-size:14px!important;border:none!important;color:var(--gray-1000)!important}.search::placeholder{color:var(--gray-600)!important}.outline-none{outline:none!important}.outline-none:focus-visible{border-color:var(--action-800)!important}.text-ellipsis{text-overflow:ellipsis!important}.overflow-hidden{overflow:hidden!important}:focus-visible:not(.outline-none){outline:solid 2px var(--action-800)!important;outline-offset:-2px!important;background-color:var(--action-300)!important;color:var(--gray-1000)}:host-context(:root,.light-mode){--shadow-color: rgba(0, 0, 0, .2)}:host-context(.dark-mode,.application-dark-mode){--shadow-color: rgba(255, 255, 255, .2)}.box-shadow-default{box-shadow:0 0 8px var(--shadow-color)!important}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: i2.NgbDropdown, selector: "[ngbDropdown]", inputs: ["autoClose", "dropdownClass", "open", "placement", "popperOptions", "container", "display"], outputs: ["openChange"], exportAs: ["ngbDropdown"] }, { kind: "directive", type: i2.NgbDropdownAnchor, selector: "[ngbDropdownAnchor]" }, { kind: "directive", type: i2.NgbDropdownToggle, selector: "[ngbDropdownToggle]" }, { kind: "directive", type: i2.NgbDropdownMenu, selector: "[ngbDropdownMenu]" }, { kind: "directive", type: i2.NgbDropdownItem, selector: "[ngbDropdownItem]", inputs: ["disabled"] }, { kind: "directive", type: i1$1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "pipe", type: SearchPipe, name: "search" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SelectComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cb-select', template: `
		<div ngbDropdown class="d-inline-block font-standard text-base" #dropdown="ngbDropdown" placement="{{placement}}">
		<button
			ngbDropdownToggle
			id="dropdownManual"
			class="btn d-flex justify-content-between align-items-center h-min-32 text-gray-900 border-1 border-radius-2"
			type="button"
			[ngClass]="{
				'bg-gray-200 border-transparent cursor-not-allowed': isDisabled,
				'bg-white border-gray-300': !isDisabled,
				'w-200': !customWidth
			}"
			[ngStyle]="{ width: customWidth }"
			[disabled]="isDisabled"
		>
			<span class="overflow-hidden text-ellipsis" [title]="selectedName">{{ selectedName }}</span>
		</button>
		<div
			ngbDropdownMenu
			class="bg-white w-max-320 w-min-240 mt-8 pv-8 border-0 border-radius-4 box-shadow-default"
		>
			<ng-container *ngIf="showSearchField">
				<div class="d-flex flex-direction-column mt-8 ph-16">
					<label [for]="dropdownSearchId" class="mb-8 text-base text-gray-900 font-bold">{{ SearchConstants.TITLE }}</label>
					<input
						ngbDropdownAnchor
						[id]="dropdownSearchId"
						type="search"
						class="search p-8 text-base border-1 border-radius-2 border-solid
							border-gray-900 outline-none border-gray-900 bg-white text-gray-900"
						[(ngModel)]="searchValue"
					/>
				</div>
				<hr class="mv-8 bg-gray-900" role="separator" />
			</ng-container>

			<div class="h-max-200 overflow-auto">
				<a
					ngbDropdownItem
					class="h-min-32 d-flex align-items-center ph-16 pv-4 hover:bg-action-600 hover:text-white cursor-pointer"
					*ngFor="let option of options | search: searchValue"
					[ngClass]="{ 'bg-action-800 text-white': option.displayName === selectedName, 'text-gray-900': option.displayName !== selectedName}"
					[attr.disabled]="option.disabled"
					[title]="option.displayName"
					(click)="selectOption(option, $event)"
					[attr.href]="option.disabled ? null: 'javascript:void(0)'"
					[attr.aria-label]="option.disabled ? (option.disabledAriaLabel ? option.disabledAriaLabel : 'disabled') : option.displayName"
					tabindex="0"
					role="button"
				>
					<span class="overflow-hidden text-ellipsis">{{ option.displayName }}</span>
				</a>
			</div>
		</div>
	</div>`, changeDetection: ChangeDetectionStrategy.OnPush, providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => SelectComponent),
                            multi: true,
                        }], styles: [".btn{box-shadow:none!important}.dropdown-toggle:after{content:\"\\f117\"!important;font-family:var(--qualtrics-icons)!important;font-size:14px!important;border:none!important;color:var(--gray-1000)!important}.search::placeholder{color:var(--gray-600)!important}.outline-none{outline:none!important}.outline-none:focus-visible{border-color:var(--action-800)!important}.text-ellipsis{text-overflow:ellipsis!important}.overflow-hidden{overflow:hidden!important}:focus-visible:not(.outline-none){outline:solid 2px var(--action-800)!important;outline-offset:-2px!important;background-color:var(--action-300)!important;color:var(--gray-1000)}:host-context(:root,.light-mode){--shadow-color: rgba(0, 0, 0, .2)}:host-context(.dark-mode,.application-dark-mode){--shadow-color: rgba(255, 255, 255, .2)}.box-shadow-default{box-shadow:0 0 8px var(--shadow-color)!important}\n"] }]
        }], propDecorators: { selectionMade: [{
                type: Output
            }], placeholderForSelect: [{
                type: Input,
                args: ['placeholder']
            }], options: [{
                type: Input
            }], customWidth: [{
                type: Input
            }], hasSearch: [{
                type: Input
            }], disabled: [{
                type: Input
            }], placement: [{
                type: Input
            }], dropdown: [{
                type: ViewChild,
                args: ['dropdown', { static: true }]
            }] } });

class SelectModule {
}
SelectModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SelectModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SelectModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: SelectModule, declarations: [SelectComponent, SearchPipe], imports: [CommonModule,
        NgbModule,
        FormsModule], exports: [SelectComponent, SearchPipe] });
SelectModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SelectModule, imports: [CommonModule,
        NgbModule,
        FormsModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SelectModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SelectComponent, SearchPipe],
                    imports: [
                        CommonModule,
                        NgbModule,
                        FormsModule
                    ],
                    exports: [SelectComponent, SearchPipe],
                }]
        }] });

class SearchableListModule {
}
SearchableListModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SearchableListModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SearchableListModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: SearchableListModule, declarations: [SearchableListComponent, HighlightMatchPipe], imports: [CommonModule,
        FormsModule,
        SelectModule], exports: [SearchableListComponent, HighlightMatchPipe] });
SearchableListModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SearchableListModule, imports: [CommonModule,
        FormsModule,
        SelectModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SearchableListModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SearchableListComponent, HighlightMatchPipe],
                    imports: [
                        CommonModule,
                        FormsModule,
                        SelectModule
                    ],
                    exports: [SearchableListComponent, HighlightMatchPipe]
                }]
        }] });

class ClearableInputModule {
}
ClearableInputModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ClearableInputModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ClearableInputModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: ClearableInputModule, declarations: [ClearableInputComponent], imports: [FormsModule,
        CommonModule], exports: [ClearableInputComponent] });
ClearableInputModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ClearableInputModule, imports: [FormsModule,
        CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ClearableInputModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        FormsModule,
                        CommonModule
                    ],
                    exports: [
                        ClearableInputComponent
                    ],
                    declarations: [ClearableInputComponent],
                    providers: [],
                }]
        }] });

class SidebarNavModule {
}
SidebarNavModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SidebarNavModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SidebarNavModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: SidebarNavModule, declarations: [SidebarNavComponent, RecursiveSearchPipe, EscapeHtmlPipe], imports: [CommonModule, LoaderModule, FormsModule, SearchableListModule, ClearableInputModule], exports: [SidebarNavComponent] });
SidebarNavModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SidebarNavModule, imports: [CommonModule, LoaderModule, FormsModule, SearchableListModule, ClearableInputModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SidebarNavModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SidebarNavComponent, RecursiveSearchPipe, EscapeHtmlPipe],
                    imports: [CommonModule, LoaderModule, FormsModule, SearchableListModule, ClearableInputModule],
                    exports: [SidebarNavComponent],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA],
                }]
        }] });

class PushButtonsComponent {
    constructor() {
        this.list = [];
        this.selectedValueChange = new EventEmitter();
    }
    setSelectedValue(value) {
        this.selectedValue = value;
        this.selectedValueChange.emit(value);
    }
    getButtonText(option) {
        return this.isIcon(option) ?
            '' :
            option.displayName;
    }
    isIcon(option) {
        return !!(option.icon || (option.class && option.class.indexOf('q-icon') !== -1));
    }
    isSelected(value) {
        return value === this.selectedValue;
    }
}
PushButtonsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PushButtonsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
PushButtonsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: PushButtonsComponent, selector: "cb-push-buttons", inputs: { list: "list", selectedValue: "selectedValue" }, outputs: { selectedValueChange: "selectedValueChange" }, ngImport: i0, template: `
		<div class="btn-group p-relative d-flex m-0" role="radiogroup">
			<button
				type="button"
				*ngFor="let option of list; let isLast = last; let isFirst = first;"
				class="btn btn--secondary {{option.class}} {{option.icon}}"
				[ngClass]="{'border-l-radius-4': isFirst, 'border-r-radius-4': isLast}"
				(click)="setSelectedValue(option.value)"
				[class.selected]="isSelected(option.value)"
				[class.btn--icon]="isIcon(option)"
				[attr.aria-label]="!!option.ariaLabel ? option.ariaLabel : option.displayName"
				[attr.aria-checked]="isSelected(option.value)"
				title="{{option.tooltip || option.displayName}}"
				role="radio">
				{{getButtonText(option)}}
			</button>
		</div>`, isInline: true, styles: [".btn{border-width:1px;border-style:solid;height:2.5em;cursor:pointer;font-size:var(--button-font-size, 1em);display:inline-flex;align-items:center;justify-content:center;padding-left:16px;padding-right:16px;text-transform:capitalize;transition:background .15s,border-color .15s,color .15s}.btn.btn--sm,.btn-group--sm .btn{height:24px;font-size:16px;padding-left:8px;padding-right:8px}.btn+.btn{margin-left:8px}.btn:hover{background:var(--button-hover-background, var(--action-100));color:var(--button-hover-text-color, var(--action-800));fill:var(--button-hover-text-color, var(--action-800));z-index:1}.btn:active{color:var(--button-active-text-color, var(--action-900));background:var(--action-100, var(--action-100))}.btn:focus{outline:solid 2px var(--focus-outline-color, var(--action-1000));outline-offset:var(--default-outline-offset, 2px);position:relative;z-index:1}.btn[disabled]:not([disabled=false]),.btn.disabled,.btn-group[disabled]:not([disabled=false])>.btn,.btn-group.disabled>.btn{background-color:var(--white);color:var(--gray-900);border-color:var(--gray-900);cursor:not-allowed;opacity:1}.btn[disabled]:focus:not([disabled=false]),.btn.disabled:focus,.btn-group[disabled]:not([disabled=false])>.btn:focus,.btn-group.disabled>.btn:focus{box-shadow:none}.btn--primary{border-color:var(--action-600);color:var(--white);background:var(--action-600)}.btn--secondary{border-color:var(--gray-1000);color:var(--gray-1000);background:transparent}.btn--inverse{background:transparent;color:var(--white);border-color:var(--white)}.btn.selected{border-color:var(--action-800, var(--gray-1000));color:var(--white);background:var(--action-800, var(--gray-1000));z-index:1}.btn--link{background:transparent}.btn--link.btn--primary:not(.disabled):not([disabled]),.btn--link.btn--primary[disabled=false]{color:var(--action-600, var(--gray-1000))}.btn--link.btn--primary:not(.disabled):not([disabled]):active,.btn--link.btn--primary[disabled=false]:active{color:var(--white)}.btn--link,.btn--link.disabled,.btn--link[disabled]:not([disabled=false]){background:transparent;border-color:transparent}.btn-group{display:inline-flex;align-items:center}.btn-group .btn{border-radius:0}.btn-group .btn+.btn{margin-left:-1px}.btn-group+.btn-group{margin-left:8px}.btn--icon{width:32px;padding:0;justify-content:center}.sort-direction-icon{padding:7px 0}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PushButtonsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cb-push-buttons', template: `
		<div class="btn-group p-relative d-flex m-0" role="radiogroup">
			<button
				type="button"
				*ngFor="let option of list; let isLast = last; let isFirst = first;"
				class="btn btn--secondary {{option.class}} {{option.icon}}"
				[ngClass]="{'border-l-radius-4': isFirst, 'border-r-radius-4': isLast}"
				(click)="setSelectedValue(option.value)"
				[class.selected]="isSelected(option.value)"
				[class.btn--icon]="isIcon(option)"
				[attr.aria-label]="!!option.ariaLabel ? option.ariaLabel : option.displayName"
				[attr.aria-checked]="isSelected(option.value)"
				title="{{option.tooltip || option.displayName}}"
				role="radio">
				{{getButtonText(option)}}
			</button>
		</div>`, styles: [".btn{border-width:1px;border-style:solid;height:2.5em;cursor:pointer;font-size:var(--button-font-size, 1em);display:inline-flex;align-items:center;justify-content:center;padding-left:16px;padding-right:16px;text-transform:capitalize;transition:background .15s,border-color .15s,color .15s}.btn.btn--sm,.btn-group--sm .btn{height:24px;font-size:16px;padding-left:8px;padding-right:8px}.btn+.btn{margin-left:8px}.btn:hover{background:var(--button-hover-background, var(--action-100));color:var(--button-hover-text-color, var(--action-800));fill:var(--button-hover-text-color, var(--action-800));z-index:1}.btn:active{color:var(--button-active-text-color, var(--action-900));background:var(--action-100, var(--action-100))}.btn:focus{outline:solid 2px var(--focus-outline-color, var(--action-1000));outline-offset:var(--default-outline-offset, 2px);position:relative;z-index:1}.btn[disabled]:not([disabled=false]),.btn.disabled,.btn-group[disabled]:not([disabled=false])>.btn,.btn-group.disabled>.btn{background-color:var(--white);color:var(--gray-900);border-color:var(--gray-900);cursor:not-allowed;opacity:1}.btn[disabled]:focus:not([disabled=false]),.btn.disabled:focus,.btn-group[disabled]:not([disabled=false])>.btn:focus,.btn-group.disabled>.btn:focus{box-shadow:none}.btn--primary{border-color:var(--action-600);color:var(--white);background:var(--action-600)}.btn--secondary{border-color:var(--gray-1000);color:var(--gray-1000);background:transparent}.btn--inverse{background:transparent;color:var(--white);border-color:var(--white)}.btn.selected{border-color:var(--action-800, var(--gray-1000));color:var(--white);background:var(--action-800, var(--gray-1000));z-index:1}.btn--link{background:transparent}.btn--link.btn--primary:not(.disabled):not([disabled]),.btn--link.btn--primary[disabled=false]{color:var(--action-600, var(--gray-1000))}.btn--link.btn--primary:not(.disabled):not([disabled]):active,.btn--link.btn--primary[disabled=false]:active{color:var(--white)}.btn--link,.btn--link.disabled,.btn--link[disabled]:not([disabled=false]){background:transparent;border-color:transparent}.btn-group{display:inline-flex;align-items:center}.btn-group .btn{border-radius:0}.btn-group .btn+.btn{margin-left:-1px}.btn-group+.btn-group{margin-left:8px}.btn--icon{width:32px;padding:0;justify-content:center}.sort-direction-icon{padding:7px 0}\n"] }]
        }], propDecorators: { list: [{
                type: Input
            }], selectedValue: [{
                type: Input
            }], selectedValueChange: [{
                type: Output
            }] } });

class PushButtonsModule {
}
PushButtonsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PushButtonsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
PushButtonsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: PushButtonsModule, declarations: [PushButtonsComponent], imports: [CommonModule], exports: [PushButtonsComponent] });
PushButtonsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PushButtonsModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PushButtonsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [PushButtonsComponent],
                    imports: [CommonModule],
                    exports: [PushButtonsComponent],
                }]
        }] });

// helper to make sure that drag handle is consistent in appearance and accessibility
class DragHandleComponent {
    constructor() {
        this.altText = 'Click and drag to reorder';
    }
}
DragHandleComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DragHandleComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DragHandleComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DragHandleComponent, selector: "cb-drag-handle", inputs: { altText: "altText" }, ngImport: i0, template: `
		<span
			class="q-icon q-icon-drag text-gray-600 cursor-move"
			[title]='altText'>
</span>`, isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DragHandleComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'cb-drag-handle',
                    template: `
		<span
			class="q-icon q-icon-drag text-gray-600 cursor-move"
			[title]='altText'>
</span>`
                }]
        }], propDecorators: { altText: [{
                type: Input
            }] } });

class DragHandleModule {
}
DragHandleModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DragHandleModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DragHandleModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DragHandleModule, declarations: [DragHandleComponent], exports: [DragHandleComponent] });
DragHandleModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DragHandleModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DragHandleModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [DragHandleComponent],
                    exports: [DragHandleComponent]
                }]
        }] });

var GravatarFallbackOption;
(function (GravatarFallbackOption) {
    GravatarFallbackOption["FAIL"] = "404";
    GravatarFallbackOption["MYSTERY_PERSON"] = "mp";
    GravatarFallbackOption["IDENTICON"] = "identicon";
    GravatarFallbackOption["BLANK"] = "blank";
})(GravatarFallbackOption || (GravatarFallbackOption = {}));
var AvatarSize;
(function (AvatarSize) {
    AvatarSize[AvatarSize["SMALL"] = 16] = "SMALL";
    AvatarSize[AvatarSize["MEDIUM"] = 32] = "MEDIUM";
    AvatarSize[AvatarSize["LARGE"] = 64] = "LARGE";
})(AvatarSize || (AvatarSize = {}));
class AvatarComponent {
    constructor() {
        this.user = {};
        this.size = AvatarSize.MEDIUM;
        this.gravatarFallback = GravatarFallbackOption.FAIL;
        this.userAvatarUrl = '';
        this.useFallback = false;
        this.getHash = (str = '') => Md5.hashStr(str.trim().toLowerCase());
        this.getUrl = (str) => {
            if (this.userAvatarUrl) {
                return this.userAvatarUrl;
            }
            const queryString = `?d=${this.gravatarFallback}&r=pg&s=${this.getSize()}`;
            this.userAvatarUrl = `https://secure.gravatar.com/avatar/${this.getHash(str)}${queryString}`;
            return this.userAvatarUrl;
        };
        this.getUserInitials = () => {
            let initials = '?';
            if (!this.user) {
                return initials;
            }
            const firstName = this.user.firstName;
            const lastName = this.user.lastName;
            if (firstName && lastName) {
                initials = firstName[0] + lastName[0];
            }
            else if (firstName) {
                initials = firstName[0];
            }
            else if (lastName) {
                initials = lastName[0];
            }
            return initials.toUpperCase();
        };
        this.useCustomFallback = () => this.useFallback && this.gravatarFallback === GravatarFallbackOption.FAIL;
    }
    getStyles() {
        return {
            height: `${this.getSize()}px`,
            width: `${this.getSize()}px`,
            'font-size': `${Math.floor(this.getSize() / 2)}px`
        };
    }
    getSize() {
        if (Object.values(AvatarSize).includes(this.size)) {
            return this.size;
        }
        return AvatarSize.MEDIUM;
    }
}
AvatarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AvatarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
AvatarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AvatarComponent, selector: "cb-avatar", inputs: { user: "user", size: "size", gravatarFallback: "gravatarFallback" }, ngImport: i0, template: `
<img *ngIf="!useCustomFallback()"
	class="border-radius-full cursor-no-selection"
	alt="{{user.firstName}} {{user.lastName}}'s Avatar"
	src="{{getUrl(user.email)}}"
	(error)="useFallback = true">
<div
	[ngStyle]="getStyles()"
	*ngIf="useCustomFallback()"
	class="font-standard d-flex align-items-center justify-center border-radius-full bg-gray-200 text-gray-1000">{{getUserInitials()}}</div>
`, isInline: true, styles: [":host{--font-standard: Helvetica Neue, Helvetica, Arial, sans serif}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AvatarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cb-avatar', template: `
<img *ngIf="!useCustomFallback()"
	class="border-radius-full cursor-no-selection"
	alt="{{user.firstName}} {{user.lastName}}'s Avatar"
	src="{{getUrl(user.email)}}"
	(error)="useFallback = true">
<div
	[ngStyle]="getStyles()"
	*ngIf="useCustomFallback()"
	class="font-standard d-flex align-items-center justify-center border-radius-full bg-gray-200 text-gray-1000">{{getUserInitials()}}</div>
`, changeDetection: ChangeDetectionStrategy.OnPush, styles: [":host{--font-standard: Helvetica Neue, Helvetica, Arial, sans serif}\n"] }]
        }], propDecorators: { user: [{
                type: Input
            }], size: [{
                type: Input
            }], gravatarFallback: [{
                type: Input
            }] } });

class AvatarModule {
}
AvatarModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AvatarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AvatarModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: AvatarModule, declarations: [AvatarComponent], imports: [CommonModule], exports: [AvatarComponent] });
AvatarModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AvatarModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AvatarModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [AvatarComponent],
                    exports: [AvatarComponent]
                }]
        }] });

var AlertLevel;
(function (AlertLevel) {
    AlertLevel["INFO"] = "info";
    AlertLevel["ERROR"] = "error";
    AlertLevel["DANGER"] = "danger";
    AlertLevel["CONFIRM"] = "confirm";
    AlertLevel["WARNING"] = "warning";
    AlertLevel["SUCCESS"] = "success";
})(AlertLevel || (AlertLevel = {}));

class Toast {
    constructor(content, type, isHtml = false) {
        this.isError = () => [AlertLevel.ERROR, AlertLevel.DANGER].includes(this.type);
        this.isInfo = () => AlertLevel.INFO === this.type;
        this.isConfirmation = () => AlertLevel.CONFIRM === this.type;
        this.isSuccess = () => AlertLevel.SUCCESS === this.type;
        this.isWarning = () => AlertLevel.WARNING === this.type;
        this.content = content;
        this.type = type;
        this.isHtml = isHtml;
    }
}

class ToastService {
    constructor() {
        this.DEFAULT_TIMEOUT = 4000;
        this.toastSource = new BehaviorSubject([]);
        this.toasts$ = this.toastSource.asObservable();
        this.currentToasts = [];
    }
    addToast(message, type, html = false) {
        this.currentToasts.push(new Toast(message, type, html));
        this.toastSource.next(this.currentToasts);
        this.scheduleRemoval();
    }
    scheduleRemoval() {
        setTimeout(() => {
            if (this.currentToasts.length > 0) {
                this.currentToasts.shift();
                this.toastSource.next(this.currentToasts);
            }
        }, this.DEFAULT_TIMEOUT);
    }
}
ToastService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ToastService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ToastService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ToastService, providedIn: 'root' // this module should already have that service now
 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ToastService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root' // this module should already have that service now
                }]
        }] });

class ToastItemComponent {
}
ToastItemComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ToastItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ToastItemComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ToastItemComponent, selector: "cb-toast-item", inputs: { toast: "toast" }, ngImport: i0, template: `
		<aside role="alert" class="toast-item w-max-480 font-standard text-base ph-16 pv-8 d-flex border-radius-4 mt-8 align-items-start border-1 border-solid border-current-color"
			[ngClass]="{
				'toast-confirmation bg-gray-200 text-gray-1000': toast.isConfirmation(),
				'toast-error bg-danger-200 text-danger-1000': toast.isError(),
				'toast-info bg-blue-200 text-blue-1000': toast.isInfo(),
				'toast-success bg-success-200 text-success-1000': toast.isSuccess(),
				'toast-warning bg-warning-200 text-warning-1000': toast.isWarning()
			}">
			<span aria-hidden="true" *ngIf="toast.isError()" class="mr-8 d-flex-inline q-icon q-icon-accessdenied"></span>
			<span aria-hidden="true" *ngIf="toast.isInfo()" class="mr-8 d-flex-inline q-icon q-icon-info"></span>
			<span aria-hidden="true" *ngIf="toast.isSuccess()" class="mr-8 d-flex-inline q-icon q-icon-check"></span>
			<span aria-hidden="true" *ngIf="toast.isWarning()" class="mr-8 d-flex-inline q-icon q-icon-warning"></span>
			<span *ngIf="!toast.isHtml">{{toast.content}}</span>
			<span *ngIf="toast.isHtml" [innerHTML]="toast.content"></span>
		</aside>
	`, isInline: true, styles: [".toast-item{line-height:1.5}.q-icon{font-size:1.25rem!important;line-height:1.5rem}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ToastItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cb-toast-item', template: `
		<aside role="alert" class="toast-item w-max-480 font-standard text-base ph-16 pv-8 d-flex border-radius-4 mt-8 align-items-start border-1 border-solid border-current-color"
			[ngClass]="{
				'toast-confirmation bg-gray-200 text-gray-1000': toast.isConfirmation(),
				'toast-error bg-danger-200 text-danger-1000': toast.isError(),
				'toast-info bg-blue-200 text-blue-1000': toast.isInfo(),
				'toast-success bg-success-200 text-success-1000': toast.isSuccess(),
				'toast-warning bg-warning-200 text-warning-1000': toast.isWarning()
			}">
			<span aria-hidden="true" *ngIf="toast.isError()" class="mr-8 d-flex-inline q-icon q-icon-accessdenied"></span>
			<span aria-hidden="true" *ngIf="toast.isInfo()" class="mr-8 d-flex-inline q-icon q-icon-info"></span>
			<span aria-hidden="true" *ngIf="toast.isSuccess()" class="mr-8 d-flex-inline q-icon q-icon-check"></span>
			<span aria-hidden="true" *ngIf="toast.isWarning()" class="mr-8 d-flex-inline q-icon q-icon-warning"></span>
			<span *ngIf="!toast.isHtml">{{toast.content}}</span>
			<span *ngIf="toast.isHtml" [innerHTML]="toast.content"></span>
		</aside>
	`, changeDetection: ChangeDetectionStrategy.OnPush, styles: [".toast-item{line-height:1.5}.q-icon{font-size:1.25rem!important;line-height:1.5rem}\n"] }]
        }], propDecorators: { toast: [{
                type: Input
            }] } });

class ToastComponent {
    constructor(toastService) {
        this.toastService = toastService;
        this.limit = 5;
    }
}
ToastComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ToastComponent, deps: [{ token: ToastService }], target: i0.ɵɵFactoryTarget.Component });
ToastComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ToastComponent, selector: "cb-toast", inputs: { limit: "limit" }, ngImport: i0, template: `
		<cb-toast-item
			*ngFor="let toast of (toastService.toasts$ | async) | slice:(-1 * limit)"
			[toast]="toast">
		</cb-toast-item>
	`, isInline: true, styles: [":host{position:absolute;bottom:8px;left:8px;display:flex;flex-direction:column-reverse;z-index:80000}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "component", type: ToastItemComponent, selector: "cb-toast-item", inputs: ["toast"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i1.SlicePipe, name: "slice" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ToastComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cb-toast', template: `
		<cb-toast-item
			*ngFor="let toast of (toastService.toasts$ | async) | slice:(-1 * limit)"
			[toast]="toast">
		</cb-toast-item>
	`, changeDetection: ChangeDetectionStrategy.OnPush, styles: [":host{position:absolute;bottom:8px;left:8px;display:flex;flex-direction:column-reverse;z-index:80000}\n"] }]
        }], ctorParameters: function () { return [{ type: ToastService }]; }, propDecorators: { limit: [{
                type: Input
            }] } });

class ToastModule {
}
ToastModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ToastModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ToastModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: ToastModule, declarations: [ToastComponent, ToastItemComponent], imports: [CommonModule], exports: [ToastComponent, ToastItemComponent] });
ToastModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ToastModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ToastModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [ToastComponent, ToastItemComponent],
                    imports: [CommonModule],
                    exports: [ToastComponent, ToastItemComponent],
                }]
        }] });

class ModalHeaderComponent {
    constructor() {
        this.closeLabel = 'Close Modal';
        // eslint-disable-next-line @angular-eslint/no-output-on-prefix
        this.onCancel = new EventEmitter();
    }
}
ModalHeaderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ModalHeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ModalHeaderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ModalHeaderComponent, selector: "cb-modal-header", inputs: { modalTitle: "modalTitle", closeLabel: "closeLabel" }, outputs: { onCancel: "onCancel" }, ngImport: i0, template: `
		<header class="p-16 bg-gray-900 text-gray-900 d-flex justify-between border-t-radius-2 box-sizing-box align-items-center">
			<p class="text-white text-base font-standard m-0 text-base break-word">{{modalTitle}}</p>
			<button
				type="button"
				class="p-8 text-white border-style-none background-zero
				hover:text-gray-200 cursor-pointer text-lg-1 focus-visible-outline-white"
				(click)="onCancel.emit()"
				[attr.aria-label]="closeLabel">
				<span aria-hidden="true" class="q-icon q-icon-delete"></span>
			</button>
		</header>`, isInline: true, styles: [".background-zero{background:0 0!important}.border-style-none{border-style:none!important}.focus-visible-outline-white:focus-visible,:focus{outline:solid 2px #fff!important;outline-offset:-2px!important}.break-word{word-break:break-word!important}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ModalHeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cb-modal-header', changeDetection: ChangeDetectionStrategy.OnPush, template: `
		<header class="p-16 bg-gray-900 text-gray-900 d-flex justify-between border-t-radius-2 box-sizing-box align-items-center">
			<p class="text-white text-base font-standard m-0 text-base break-word">{{modalTitle}}</p>
			<button
				type="button"
				class="p-8 text-white border-style-none background-zero
				hover:text-gray-200 cursor-pointer text-lg-1 focus-visible-outline-white"
				(click)="onCancel.emit()"
				[attr.aria-label]="closeLabel">
				<span aria-hidden="true" class="q-icon q-icon-delete"></span>
			</button>
		</header>`, styles: [".background-zero{background:0 0!important}.border-style-none{border-style:none!important}.focus-visible-outline-white:focus-visible,:focus{outline:solid 2px #fff!important;outline-offset:-2px!important}.break-word{word-break:break-word!important}\n"] }]
        }], propDecorators: { modalTitle: [{
                type: Input
            }], closeLabel: [{
                type: Input
            }], onCancel: [{
                type: Output
            }] } });

class ModalFooterComponent {
}
ModalFooterComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ModalFooterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ModalFooterComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ModalFooterComponent, selector: "cb-modal-footer", ngImport: i0, template: `
		<footer class="p-16 bg-gray-100 text-gray-900 d-flex justify-end border-b-radius-2 box-sizing-box">
			<ng-content></ng-content>
		</footer>`, isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ModalFooterComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'cb-modal-footer',
                    template: `
		<footer class="p-16 bg-gray-100 text-gray-900 d-flex justify-end border-b-radius-2 box-sizing-box">
			<ng-content></ng-content>
		</footer>`,
                }]
        }] });

class ModalModule {
}
ModalModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ModalModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ModalModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: ModalModule, declarations: [ModalHeaderComponent, ModalFooterComponent], exports: [ModalHeaderComponent,
        ModalFooterComponent] });
ModalModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ModalModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ModalModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [],
                    exports: [
                        ModalHeaderComponent,
                        ModalFooterComponent
                    ],
                    declarations: [ModalHeaderComponent, ModalFooterComponent],
                    providers: [],
                }]
        }] });

class NoticeComponent {
    constructor() {
        this.rounded = true;
        this.dismissable = true;
        this.hideIcon = false;
        this.closeLabel = 'Close Notice';
        this.closeNotice = new EventEmitter();
        this.dismissed = false;
        this.classes = {
            btnClasses: [
                'q-icon',
                'q-icon-delete',
                'p-8',
                'm-0',
                'bg-none',
                'text-white',
                'hover:text-gray-200',
                'border-0',
                'cursor-pointer',
                'align-self-center'
            ],
            noticeContainerClasses: [
                'font-standard',
                'text-base',
                'text-white',
                'd-flex',
                'justify-between',
                'h-min-32',
                'break-words',
            ]
        };
    }
    ngOnInit() {
        this.type = this.type?.toLowerCase();
        switch (this.type) {
            case AlertLevel.INFO:
                this.icon = 'q-icon-info';
                this.background = 'bg-info-600';
                break;
            case AlertLevel.SUCCESS:
                this.icon = 'q-icon-check';
                this.background = 'bg-success-600';
                break;
            case AlertLevel.DANGER:
                this.icon = 'q-icon-accessdenied';
                this.background = 'bg-danger-600';
                break;
            case AlertLevel.CONFIRM:
                this.icon = 'q-icon-check-3';
                this.background = 'bg-gray-900';
                break;
            case AlertLevel.WARNING:
            default:
                // warning or undefined
                this.icon = 'q-icon-warning';
                this.background = 'bg-warning-600';
                break;
        }
        if (this.customIcon) {
            this.icon = this.customIcon;
        }
    }
    dismiss() {
        this.dismissed = true;
        if (this.closeNotice) {
            this.closeNotice.emit();
        }
    }
}
NoticeComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NoticeComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
NoticeComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: NoticeComponent, selector: "cb-notice", inputs: { rounded: "rounded", text: "text", type: "type", dismissable: "dismissable", hideIcon: "hideIcon", customIcon: "customIcon", closeLabel: "closeLabel" }, outputs: { closeNotice: "closeNotice" }, ngImport: i0, template: `
		<div *ngIf="!dismissed" role="alert"
				class="notice-item {{classes.noticeContainerClasses.join(' ')}} {{!rounded ? 'border-radius-0': 'border-radius-2'}}"
				[ngClass]="background">
			<div class="d-flex ph-8 pv-4 line-height-1-5 align-items-center overflow-hidden">
				<span class="mr-8 q-icon" [ngClass]="icon" aria-hidden="true"></span>
				<div class="d-flex flex-direction-column">
					<p class="p-0 m-0 overflow-hidden">{{text}}</p>
					<ng-content></ng-content>
				</div>
			</div>
			<button type="button" [attr.aria-label]="closeLabel" *ngIf="dismissable"
				class="{{classes.btnClasses.join(' ')}}" (click)="dismiss()"></button>
		</div>`, isInline: true, styles: [":host{display:flex;flex-direction:column-reverse;z-index:80000}.notice-item{width:inherit;min-width:288px;align-self:flex-start}.line-height-1-5{line-height:1.5!important}.break-words{overflow-wrap:break-word!important}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NoticeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cb-notice', changeDetection: ChangeDetectionStrategy.OnPush, template: `
		<div *ngIf="!dismissed" role="alert"
				class="notice-item {{classes.noticeContainerClasses.join(' ')}} {{!rounded ? 'border-radius-0': 'border-radius-2'}}"
				[ngClass]="background">
			<div class="d-flex ph-8 pv-4 line-height-1-5 align-items-center overflow-hidden">
				<span class="mr-8 q-icon" [ngClass]="icon" aria-hidden="true"></span>
				<div class="d-flex flex-direction-column">
					<p class="p-0 m-0 overflow-hidden">{{text}}</p>
					<ng-content></ng-content>
				</div>
			</div>
			<button type="button" [attr.aria-label]="closeLabel" *ngIf="dismissable"
				class="{{classes.btnClasses.join(' ')}}" (click)="dismiss()"></button>
		</div>`, styles: [":host{display:flex;flex-direction:column-reverse;z-index:80000}.notice-item{width:inherit;min-width:288px;align-self:flex-start}.line-height-1-5{line-height:1.5!important}.break-words{overflow-wrap:break-word!important}\n"] }]
        }], propDecorators: { rounded: [{
                type: Input
            }], text: [{
                type: Input
            }], type: [{
                type: Input
            }], dismissable: [{
                type: Input
            }], hideIcon: [{
                type: Input
            }], customIcon: [{
                type: Input
            }], closeLabel: [{
                type: Input
            }], closeNotice: [{
                type: Output
            }] } });

class NoticeModule {
}
NoticeModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NoticeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
NoticeModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: NoticeModule, declarations: [NoticeComponent], imports: [CommonModule], exports: [NoticeComponent] });
NoticeModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NoticeModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NoticeModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    exports: [NoticeComponent],
                    declarations: [NoticeComponent],
                    providers: [],
                }]
        }] });

/*
 * The component can be used by the consumer as:
 * <cb-header (sidebarToggle)="customFunction()" [buttonLabel]="customButtonLabel">
 *  <logo-component class='logo'></logo-component> <!-- immediate child with class logo is aligned to the left -->
 *  <search-component class='search></search-component>  <!-- immediate child with class search is aligned to the
 *  left -->
 *  <custom-component></custom-component> <!-- everything else is aligned to the right -->
 *  <custom-component-2></custom-component-2>
 * </cb-header>
 */
class HeaderComponent {
    constructor() {
        this.buttonLabel = 'Sidebar Toggle Button';
        this.sidebarToggle = new EventEmitter();
        this.sidebarToggleKeydown = new EventEmitter();
        this.sidebarShown = false;
        this.classes = {
            button: [
                'border-0',
                'cursor-pointer',
                'q-icon',
                'q-icon-layer',
                'has-dark-background',
                'ph-16',
                'pv-18',
                'text-lg-1',
            ]
        };
    }
    onKeydown($event) {
        this.sidebarToggleKeydown.emit($event);
    }
}
HeaderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
HeaderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: HeaderComponent, selector: "cb-header", inputs: { buttonLabel: "buttonLabel", sidebarShown: "sidebarShown" }, outputs: { sidebarToggle: "sidebarToggle", sidebarToggleKeydown: "sidebarToggleKeydown" }, ngImport: i0, template: `
		<header class="align-items-center d-flex h-56 header p-relative w-100-vw border-color-inherit" role="banner">
			<div class="align-items-center">
				<ul class="list-style-none p-0 m-0">
					<li class="d-flex">
						<button type="button" id="sidebar-toggle" [attr.aria-label]="buttonLabel"
						        [ngClass]="{'show-sidebar' : sidebarShown}"
						        class="button {{classes.button.join(' ')}}"
						        (click)="sidebarToggle.emit()" (keydown)="onKeydown($event)"></button>
					</li>
				</ul>
			</div>
			<div class="h-100-percent d-flex-inline justify-between flex-grow border-color-inherit">
				<div class="d-flex-inline flex-1 border-color-inherit">
					<ng-content select="cb-app-name"></ng-content>
					<div class="d-flex border-color-inherit mr-16">
						<div *ngIf="ref.children.length" role="separator"
							 class="h-100-percent bg-none border-solid border-l-1
							 border-r-0 border-b-0 w-0 box-sizing-box border-color-inherit"></div>
						<div #ref><ng-content select="cb-logo, .logo"></ng-content></div>
					</div>
					<ng-content select=".search"></ng-content>
				</div>
				<div class="d-flex-inline align-items-center">
					<ng-content></ng-content>
					<ng-content select=".account-switcher"></ng-content>
					<ng-content select=".spaces"></ng-content>
					<ng-content select=".user-menu"></ng-content>
				</div>
			</div>
		</header>`, isInline: true, styles: [".header{background:var(--custom-bg-header, var(--white))}.h-56{height:56px!important}.w-0{width:0!important}.border-color-inherit{border-color:inherit!important}.list-style-none{list-style:none!important}.pv-18{padding-top:18px!important;padding-bottom:18px!important}#sidebar-toggle{background:none;color:var(--header-link-color, var(--gray-1000))}#sidebar-toggle:hover{color:var(--header-link-color-hover, var(--action-700))}#sidebar-toggle.show-sidebar{background:var(--header-active-menu-bg, var(--gray-200))}:host ::ng-deep .header-link{color:var(--header-link-color, var(--gray-1000))}:host ::ng-deep .header-link:link{color:var(--header-link-color, var(--gray-1000))}:host ::ng-deep .header-link:visited{color:var(--header-link-color, var(--gray-1000))}:host ::ng-deep .header-link:hover{color:var(--header-link-color-hover, var(--action-700))}:host ::ng-deep .header-link:active{color:var(--header-link-color-hover, var(--action-700))}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cb-header', template: `
		<header class="align-items-center d-flex h-56 header p-relative w-100-vw border-color-inherit" role="banner">
			<div class="align-items-center">
				<ul class="list-style-none p-0 m-0">
					<li class="d-flex">
						<button type="button" id="sidebar-toggle" [attr.aria-label]="buttonLabel"
						        [ngClass]="{'show-sidebar' : sidebarShown}"
						        class="button {{classes.button.join(' ')}}"
						        (click)="sidebarToggle.emit()" (keydown)="onKeydown($event)"></button>
					</li>
				</ul>
			</div>
			<div class="h-100-percent d-flex-inline justify-between flex-grow border-color-inherit">
				<div class="d-flex-inline flex-1 border-color-inherit">
					<ng-content select="cb-app-name"></ng-content>
					<div class="d-flex border-color-inherit mr-16">
						<div *ngIf="ref.children.length" role="separator"
							 class="h-100-percent bg-none border-solid border-l-1
							 border-r-0 border-b-0 w-0 box-sizing-box border-color-inherit"></div>
						<div #ref><ng-content select="cb-logo, .logo"></ng-content></div>
					</div>
					<ng-content select=".search"></ng-content>
				</div>
				<div class="d-flex-inline align-items-center">
					<ng-content></ng-content>
					<ng-content select=".account-switcher"></ng-content>
					<ng-content select=".spaces"></ng-content>
					<ng-content select=".user-menu"></ng-content>
				</div>
			</div>
		</header>`, styles: [".header{background:var(--custom-bg-header, var(--white))}.h-56{height:56px!important}.w-0{width:0!important}.border-color-inherit{border-color:inherit!important}.list-style-none{list-style:none!important}.pv-18{padding-top:18px!important;padding-bottom:18px!important}#sidebar-toggle{background:none;color:var(--header-link-color, var(--gray-1000))}#sidebar-toggle:hover{color:var(--header-link-color-hover, var(--action-700))}#sidebar-toggle.show-sidebar{background:var(--header-active-menu-bg, var(--gray-200))}:host ::ng-deep .header-link{color:var(--header-link-color, var(--gray-1000))}:host ::ng-deep .header-link:link{color:var(--header-link-color, var(--gray-1000))}:host ::ng-deep .header-link:visited{color:var(--header-link-color, var(--gray-1000))}:host ::ng-deep .header-link:hover{color:var(--header-link-color-hover, var(--action-700))}:host ::ng-deep .header-link:active{color:var(--header-link-color-hover, var(--action-700))}\n"] }]
        }], propDecorators: { buttonLabel: [{
                type: Input
            }], sidebarToggle: [{
                type: Output
            }], sidebarToggleKeydown: [{
                type: Output
            }], sidebarShown: [{
                type: Input
            }] } });

class HeaderModule {
}
HeaderModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HeaderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
HeaderModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: HeaderModule, declarations: [HeaderComponent], imports: [CommonModule], exports: [HeaderComponent] });
HeaderModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HeaderModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HeaderModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    exports: [
                        HeaderComponent
                    ],
                    declarations: [HeaderComponent],
                    providers: [],
                }]
        }] });

class CapsLockService {
    constructor() {
        this.onCapsLock = (callback) => {
            const handler = (e) => this.onButtonPressed(e, callback);
            window.addEventListener('keydown', handler);
            window.addEventListener('keyup', handler);
            return { handler };
        };
        this.removeHandler = (detection) => {
            window.removeEventListener('keydown', detection.handler);
            window.removeEventListener('keyup', detection.handler);
        };
        this.onButtonPressed = (e, callback) => {
            callback(e.getModifierState('CapsLock'));
        };
        // empty constructor
    }
}
CapsLockService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CapsLockService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CapsLockService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CapsLockService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CapsLockService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

class LoginAlertComponent {
    constructor() {
        this.alertLevel = AlertLevel.INFO;
        this.rounded = true; // if notices are stacked, pass rounded=false from parent for all notices except the first
        this.isDanger = () => {
            return this.alertLevel === AlertLevel.DANGER;
        };
        this.isInfo = () => {
            return this.alertLevel === AlertLevel.INFO;
        };
    }
    getBgColor() {
        switch (this.alertLevel) {
            case AlertLevel.INFO:
                return 'bg-info-600';
            case AlertLevel.DANGER:
                return 'bg-danger-600';
            case AlertLevel.WARNING:
                return 'bg-warning-600';
            default:
                return 'bg-info-600';
        }
    }
}
LoginAlertComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: LoginAlertComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
LoginAlertComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: LoginAlertComponent, selector: "cb-login-alert", inputs: { alertLevel: "alertLevel", rounded: "rounded" }, ngImport: i0, template: `
		<div [ngClass]="[ getBgColor(), rounded ? 'border-t-radius-2' : '']" role="alert" tabindex="0"
		     class="text-white text-base font-standard pv-4 ph-8 line-height-lg-5 w-max-432 w-min-280 border-radius-0 box-sizing-box">
			<div class="d-flex align-items-center">
				<span class="q-icon mr-8 align-self-start" aria-hidden="true"
				   [ngClass]="{'q-icon-info':isInfo(), 'q-icon-accessdenied':isDanger()}"></span>
				<span>
					<ng-content></ng-content>
				</span>
			</div>
		</div>`, isInline: true, styles: [".img-container{max-width:288px}.form-container{max-width:432px}.text-decoration-none{text-decoration:none!important}.line-height-lg-5{line-height:1.71rem!important}.line-height-1{line-height:1!important}.transition-border{transition:background .15s,border-color .15s,color .15s!important}.w-max-432{max-width:432px!important}.w-288{width:288px!important}.btn:disabled,.btn-disabled{background-color:var(--white-100)!important;border-color:var(--gray-900);color:var(--gray-900);pointer-events:none!important;cursor:not-allowed!important}.btn:active,.btn-disabled:active{color:var(--white-100)!important;background-color:var(--action-800)!important;border-color:var(action-800)!important}.rotate-infinite{animation:spin 2.1s infinite linear;-webkit-animation:spin 2.1s infinite linear}@keyframes spin{0%{transform:rotate(0)}to{transform:rotate(360deg)}}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: LoginAlertComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cb-login-alert', template: `
		<div [ngClass]="[ getBgColor(), rounded ? 'border-t-radius-2' : '']" role="alert" tabindex="0"
		     class="text-white text-base font-standard pv-4 ph-8 line-height-lg-5 w-max-432 w-min-280 border-radius-0 box-sizing-box">
			<div class="d-flex align-items-center">
				<span class="q-icon mr-8 align-self-start" aria-hidden="true"
				   [ngClass]="{'q-icon-info':isInfo(), 'q-icon-accessdenied':isDanger()}"></span>
				<span>
					<ng-content></ng-content>
				</span>
			</div>
		</div>`, styles: [".img-container{max-width:288px}.form-container{max-width:432px}.text-decoration-none{text-decoration:none!important}.line-height-lg-5{line-height:1.71rem!important}.line-height-1{line-height:1!important}.transition-border{transition:background .15s,border-color .15s,color .15s!important}.w-max-432{max-width:432px!important}.w-288{width:288px!important}.btn:disabled,.btn-disabled{background-color:var(--white-100)!important;border-color:var(--gray-900);color:var(--gray-900);pointer-events:none!important;cursor:not-allowed!important}.btn:active,.btn-disabled:active{color:var(--white-100)!important;background-color:var(--action-800)!important;border-color:var(action-800)!important}.rotate-infinite{animation:spin 2.1s infinite linear;-webkit-animation:spin 2.1s infinite linear}@keyframes spin{0%{transform:rotate(0)}to{transform:rotate(360deg)}}\n"] }]
        }], propDecorators: { alertLevel: [{
                type: Input
            }], rounded: [{
                type: Input
            }] } });

class LoginLogoComponent {
    constructor() {
        // empty constructor
    }
}
LoginLogoComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: LoginLogoComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
LoginLogoComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: LoginLogoComponent, selector: "cb-login-logo", ngImport: i0, template: `
    <!-- Generator: Adobe Illustrator 25.2.3, SVG Export Plug-In . SVG Version: 6.00 Build 0)  -->
    <svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
         x="0px" y="0px"
         viewBox="0 0 1001 105.7" style="enable-background:new 0 0 1001 105.7;" xml:space="preserve">
<style type="text/css">
\t.st0 {
    fill: #FFFFFF;
  }

  .st1 {
    fill: #F8A962;
  }

  .st2 {
    fill: #67A0E6;
  }

  .st3 {
    fill: #2760A7;
  }

  .st4 {
    fill: #68CEDD;
  }

  .st5 {
    fill: #03AEC7;
  }

  .st6 {
    fill: #3480DE;
  }

  .st7 {
    fill: #F68D2E;
  }
</style>
      <g id="guides">
\t<g>
\t\t<path class="st0" d="M173.8,67.8c3.7,0,6.8-0.6,9.5-1.8c2.6-1.2,5.4-3.2,8.3-5.9l6.8,7c-6.7,7.4-14.8,11-24.3,11
\t\t\ts-17.4-3.1-23.7-9.2c-6.3-6.1-9.4-13.8-9.4-23.2s3.2-17.1,9.6-23.3c6.4-6.2,14.5-9.3,24.2-9.3c9.8,0,17.9,3.6,24.5,10.8l-6.8,7.4
\t\t\tc-3-2.9-5.8-4.9-8.5-6c-2.7-1.1-5.8-1.7-9.4-1.7c-6.4,0-11.7,2-16,6.2c-4.3,4.1-6.5,9.3-6.5,15.7s2.1,11.7,6.4,16
\t\t\tC162.9,65.7,167.9,67.8,173.8,67.8"/>
    <polygon class="st0" points="229.4,77.5 229.4,14.7 240.1,14.7 240.1,67.4 268.8,67.4 268.8,77.5 \t\t"/>
    <path class="st0" d="M310,63.2l-6.3,14.3h-11.4l27.8-62.8h11.4l27.8,62.8h-11.4l-6.3-14.3H310z M337.2,53.4l-11.4-25.8l-11.5,25.8
\t\t\tH337.2z"/>
    <path class="st0" d="M436.2,35.1c0,10.4-4.5,16.9-13.6,19.6l16.5,22.8h-13.5l-15-21.1h-14v21.1h-10.6V14.7h23.4
\t\t\tc9.6,0,16.5,1.6,20.6,4.8C434.1,22.8,436.2,28,436.2,35.1 M422.1,44c2.2-1.9,3.2-4.8,3.2-8.9s-1.1-6.9-3.3-8.4
\t\t\tc-2.2-1.5-6.2-2.3-11.9-2.3h-13.5v22.4h13.2C415.8,46.7,419.9,45.8,422.1,44"/>
    <path class="st0" d="M481,63.2l-6.3,14.3h-11.4l27.8-62.8h11.3l27.8,62.8h-11.3l-6.3-14.3H481z M508.3,53.4l-11.4-25.8l-11.4,25.8
\t\t\tH508.3z"/>
    <path class="st0" d="M586.4,77.5h-27.6V14.7h24.5c4.3,0,8,0.5,11,1.5c3.1,1,5.4,2.4,6.9,4.1c2.9,3.2,4.3,6.9,4.3,11
\t\t\tc0,4.9-1.6,8.6-4.7,10.9c-1.1,0.8-1.9,1.4-2.3,1.6c-0.4,0.2-1.2,0.6-2.3,1c3.9,0.8,7,2.6,9.3,5.2c2.3,2.7,3.5,6,3.5,9.9
\t\t\tc0,4.4-1.5,8.2-4.5,11.6C601,75.5,595,77.5,586.4,77.5 M569.5,40.7H583c7.7,0,11.5-2.6,11.5-7.8c0-3-0.9-5.2-2.8-6.5
\t\t\tc-1.9-1.3-4.8-2-8.7-2h-13.6L569.5,40.7L569.5,40.7z M569.5,67.7h16.7c3.9,0,6.9-0.6,9-1.8c2.1-1.2,3.1-3.5,3.1-7
\t\t\tc0-5.6-4.5-8.3-13.4-8.3h-15.3V67.7z"/>
    <path class="st0" d="M694.3,35.1c0,10.4-4.5,16.9-13.6,19.6l16.5,22.8h-13.5l-15-21.1h-14v21.1h-10.6V14.7h23.4
\t\t\tc9.6,0,16.5,1.6,20.6,4.8C692.2,22.8,694.3,28,694.3,35.1 M680.2,44c2.2-1.9,3.2-4.8,3.2-8.9s-1.1-6.9-3.3-8.4
\t\t\tc-2.2-1.5-6.2-2.3-11.9-2.3h-13.5v22.4h13.2C673.9,46.7,678,45.8,680.2,44"/>
    <rect x="731.3" y="14.7" class="st0" width="10.6" height="62.8"/>
    <path class="st0" d="M828.7,22.9c6.1,5.5,9.1,13.1,9.1,22.9s-2.9,17.5-8.8,23.2c-5.9,5.7-14.9,8.5-27,8.5H781V14.7h21.6
\t\t\tC813.9,14.7,822.6,17.4,828.7,22.9 M827.1,46c0-14.4-8.3-21.5-24.8-21.5h-10.6v43h11.8c7.6,0,13.5-1.8,17.5-5.4
\t\t\tC825,58.4,827.1,53.1,827.1,46"/>
    <path class="st0" d="M913.1,46.9h10.6v21.5c-5.9,6.5-14.5,9.8-25.5,9.8c-9.3,0-17.1-3.1-23.4-9.2c-6.3-6.1-9.4-13.8-9.4-23.2
\t\t\ts3.2-17.1,9.6-23.3c6.4-6.2,14.1-9.3,23.2-9.3s16.6,2.6,22.6,7.9l-5.7,8.1c-2.5-2.1-4.9-3.5-7.4-4.3c-2.5-0.8-5.4-1.2-8.7-1.2
\t\t\tc-6.4,0-11.7,2-16,6.2c-4.3,4.1-6.5,9.5-6.5,16.2c0,6.7,2.1,12,6.3,16.1s9.3,6.1,15.3,6.1c6,0,11-1.3,15-3.9L913.1,46.9
\t\t\tL913.1,46.9z"/>
    <polygon class="st0" points="1000,14.7 1000,24.7 966.4,24.7 966.4,41.3 996.5,41.3 996.5,50.7 966.4,50.7 966.4,67.5 1001,67.5
\t\t\t1001,77.5 955.7,77.5 955.7,14.7 \t\t"/>
\t</g>
        <g>
\t\t<path class="st1" d="M28.8,40.5c-1.2-2.9-1.9-5.9-1.9-9c0-2,0.3-3.9,0.8-5.8C12.2,26.4,0,35.6,0,46.8c0,6.3,3.9,12,10,15.9
\t\t\tL4.7,77.5l17.7-10.1c0.7,0.1,1.3,0.2,2,0.3c-1.5-3.2-2.3-6.6-2.3-10.2C22.1,51.3,24.5,45.5,28.8,40.5z"/>
          <path class="st2" d="M45.5,29c6.5-2.6,14-4.1,22-4.1c18.4,0,34.2,7.8,41.4,19.1c2.4-3.8,3.7-8.1,3.7-12.5
\t\t\tc0-17-19.2-30.8-42.9-30.8c-20.9,0-38.3,10.8-42.1,25c0.6,0,1.3-0.1,1.9-0.1C35.5,25.6,40.9,26.9,45.5,29z"/>
          <path class="st3" d="M28.8,40.5c4.1-4.8,9.9-8.8,16.8-11.5c-4.6-2.1-10.1-3.4-16-3.4c-0.7,0-1.3,0-1.9,0.1
\t\t\tc-0.5,1.9-0.8,3.8-0.8,5.8C26.9,34.6,27.5,37.7,28.8,40.5z"/>
          <path class="st4" d="M113,57.6c0-4.8-1.5-9.4-4.1-13.6c-5.7,9.2-17.4,16-31.6,17.8l-2.8,13.5L64.4,62c-4-0.4-7.8-1.1-11.4-2.2
\t\t\tc-5.4,5-13.9,8.3-23.4,8.3c-1.8,0-3.5-0.1-5.2-0.3c6,13.1,23,22.5,43.2,22.5c3.6,0,7.1-0.3,10.5-0.9l23,16.3l-7.5-21.3
\t\t\tC105.3,78.5,113,68.7,113,57.6z"/>
          <path class="st5" d="M52.9,59.8c-11.5-3.5-20.5-10.6-24.2-19.3c-4.2,5-6.7,10.8-6.7,17c0,3.6,0.8,7,2.3,10.2
\t\t\tc1.7,0.2,3.4,0.3,5.2,0.3C39.1,68.1,47.5,64.8,52.9,59.8z"/>
          <path class="st6" d="M67.6,24.9c-8,0-15.5,1.5-22,4.1c8.2,3.8,13.6,10.4,13.6,17.9c0,4.9-2.3,9.4-6.2,13c3.6,1.1,7.4,1.9,11.4,2.2
\t\t\tl10.2,13.2l2.8-13.5c14.2-1.8,25.9-8.6,31.6-17.8C101.8,32.7,85.9,24.9,67.6,24.9z"/>
          <path class="st7" d="M59.1,46.8c0-7.5-5.4-14.1-13.6-17.9c-6.9,2.7-12.7,6.7-16.8,11.5c3.7,8.7,12.6,15.8,24.2,19.3
\t\t\tC56.8,56.2,59.1,51.7,59.1,46.8z"/>
\t</g>
</g>
</svg>

  `, isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: LoginLogoComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'cb-login-logo',
                    template: `
    <!-- Generator: Adobe Illustrator 25.2.3, SVG Export Plug-In . SVG Version: 6.00 Build 0)  -->
    <svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
         x="0px" y="0px"
         viewBox="0 0 1001 105.7" style="enable-background:new 0 0 1001 105.7;" xml:space="preserve">
<style type="text/css">
\t.st0 {
    fill: #FFFFFF;
  }

  .st1 {
    fill: #F8A962;
  }

  .st2 {
    fill: #67A0E6;
  }

  .st3 {
    fill: #2760A7;
  }

  .st4 {
    fill: #68CEDD;
  }

  .st5 {
    fill: #03AEC7;
  }

  .st6 {
    fill: #3480DE;
  }

  .st7 {
    fill: #F68D2E;
  }
</style>
      <g id="guides">
\t<g>
\t\t<path class="st0" d="M173.8,67.8c3.7,0,6.8-0.6,9.5-1.8c2.6-1.2,5.4-3.2,8.3-5.9l6.8,7c-6.7,7.4-14.8,11-24.3,11
\t\t\ts-17.4-3.1-23.7-9.2c-6.3-6.1-9.4-13.8-9.4-23.2s3.2-17.1,9.6-23.3c6.4-6.2,14.5-9.3,24.2-9.3c9.8,0,17.9,3.6,24.5,10.8l-6.8,7.4
\t\t\tc-3-2.9-5.8-4.9-8.5-6c-2.7-1.1-5.8-1.7-9.4-1.7c-6.4,0-11.7,2-16,6.2c-4.3,4.1-6.5,9.3-6.5,15.7s2.1,11.7,6.4,16
\t\t\tC162.9,65.7,167.9,67.8,173.8,67.8"/>
    <polygon class="st0" points="229.4,77.5 229.4,14.7 240.1,14.7 240.1,67.4 268.8,67.4 268.8,77.5 \t\t"/>
    <path class="st0" d="M310,63.2l-6.3,14.3h-11.4l27.8-62.8h11.4l27.8,62.8h-11.4l-6.3-14.3H310z M337.2,53.4l-11.4-25.8l-11.5,25.8
\t\t\tH337.2z"/>
    <path class="st0" d="M436.2,35.1c0,10.4-4.5,16.9-13.6,19.6l16.5,22.8h-13.5l-15-21.1h-14v21.1h-10.6V14.7h23.4
\t\t\tc9.6,0,16.5,1.6,20.6,4.8C434.1,22.8,436.2,28,436.2,35.1 M422.1,44c2.2-1.9,3.2-4.8,3.2-8.9s-1.1-6.9-3.3-8.4
\t\t\tc-2.2-1.5-6.2-2.3-11.9-2.3h-13.5v22.4h13.2C415.8,46.7,419.9,45.8,422.1,44"/>
    <path class="st0" d="M481,63.2l-6.3,14.3h-11.4l27.8-62.8h11.3l27.8,62.8h-11.3l-6.3-14.3H481z M508.3,53.4l-11.4-25.8l-11.4,25.8
\t\t\tH508.3z"/>
    <path class="st0" d="M586.4,77.5h-27.6V14.7h24.5c4.3,0,8,0.5,11,1.5c3.1,1,5.4,2.4,6.9,4.1c2.9,3.2,4.3,6.9,4.3,11
\t\t\tc0,4.9-1.6,8.6-4.7,10.9c-1.1,0.8-1.9,1.4-2.3,1.6c-0.4,0.2-1.2,0.6-2.3,1c3.9,0.8,7,2.6,9.3,5.2c2.3,2.7,3.5,6,3.5,9.9
\t\t\tc0,4.4-1.5,8.2-4.5,11.6C601,75.5,595,77.5,586.4,77.5 M569.5,40.7H583c7.7,0,11.5-2.6,11.5-7.8c0-3-0.9-5.2-2.8-6.5
\t\t\tc-1.9-1.3-4.8-2-8.7-2h-13.6L569.5,40.7L569.5,40.7z M569.5,67.7h16.7c3.9,0,6.9-0.6,9-1.8c2.1-1.2,3.1-3.5,3.1-7
\t\t\tc0-5.6-4.5-8.3-13.4-8.3h-15.3V67.7z"/>
    <path class="st0" d="M694.3,35.1c0,10.4-4.5,16.9-13.6,19.6l16.5,22.8h-13.5l-15-21.1h-14v21.1h-10.6V14.7h23.4
\t\t\tc9.6,0,16.5,1.6,20.6,4.8C692.2,22.8,694.3,28,694.3,35.1 M680.2,44c2.2-1.9,3.2-4.8,3.2-8.9s-1.1-6.9-3.3-8.4
\t\t\tc-2.2-1.5-6.2-2.3-11.9-2.3h-13.5v22.4h13.2C673.9,46.7,678,45.8,680.2,44"/>
    <rect x="731.3" y="14.7" class="st0" width="10.6" height="62.8"/>
    <path class="st0" d="M828.7,22.9c6.1,5.5,9.1,13.1,9.1,22.9s-2.9,17.5-8.8,23.2c-5.9,5.7-14.9,8.5-27,8.5H781V14.7h21.6
\t\t\tC813.9,14.7,822.6,17.4,828.7,22.9 M827.1,46c0-14.4-8.3-21.5-24.8-21.5h-10.6v43h11.8c7.6,0,13.5-1.8,17.5-5.4
\t\t\tC825,58.4,827.1,53.1,827.1,46"/>
    <path class="st0" d="M913.1,46.9h10.6v21.5c-5.9,6.5-14.5,9.8-25.5,9.8c-9.3,0-17.1-3.1-23.4-9.2c-6.3-6.1-9.4-13.8-9.4-23.2
\t\t\ts3.2-17.1,9.6-23.3c6.4-6.2,14.1-9.3,23.2-9.3s16.6,2.6,22.6,7.9l-5.7,8.1c-2.5-2.1-4.9-3.5-7.4-4.3c-2.5-0.8-5.4-1.2-8.7-1.2
\t\t\tc-6.4,0-11.7,2-16,6.2c-4.3,4.1-6.5,9.5-6.5,16.2c0,6.7,2.1,12,6.3,16.1s9.3,6.1,15.3,6.1c6,0,11-1.3,15-3.9L913.1,46.9
\t\t\tL913.1,46.9z"/>
    <polygon class="st0" points="1000,14.7 1000,24.7 966.4,24.7 966.4,41.3 996.5,41.3 996.5,50.7 966.4,50.7 966.4,67.5 1001,67.5
\t\t\t1001,77.5 955.7,77.5 955.7,14.7 \t\t"/>
\t</g>
        <g>
\t\t<path class="st1" d="M28.8,40.5c-1.2-2.9-1.9-5.9-1.9-9c0-2,0.3-3.9,0.8-5.8C12.2,26.4,0,35.6,0,46.8c0,6.3,3.9,12,10,15.9
\t\t\tL4.7,77.5l17.7-10.1c0.7,0.1,1.3,0.2,2,0.3c-1.5-3.2-2.3-6.6-2.3-10.2C22.1,51.3,24.5,45.5,28.8,40.5z"/>
          <path class="st2" d="M45.5,29c6.5-2.6,14-4.1,22-4.1c18.4,0,34.2,7.8,41.4,19.1c2.4-3.8,3.7-8.1,3.7-12.5
\t\t\tc0-17-19.2-30.8-42.9-30.8c-20.9,0-38.3,10.8-42.1,25c0.6,0,1.3-0.1,1.9-0.1C35.5,25.6,40.9,26.9,45.5,29z"/>
          <path class="st3" d="M28.8,40.5c4.1-4.8,9.9-8.8,16.8-11.5c-4.6-2.1-10.1-3.4-16-3.4c-0.7,0-1.3,0-1.9,0.1
\t\t\tc-0.5,1.9-0.8,3.8-0.8,5.8C26.9,34.6,27.5,37.7,28.8,40.5z"/>
          <path class="st4" d="M113,57.6c0-4.8-1.5-9.4-4.1-13.6c-5.7,9.2-17.4,16-31.6,17.8l-2.8,13.5L64.4,62c-4-0.4-7.8-1.1-11.4-2.2
\t\t\tc-5.4,5-13.9,8.3-23.4,8.3c-1.8,0-3.5-0.1-5.2-0.3c6,13.1,23,22.5,43.2,22.5c3.6,0,7.1-0.3,10.5-0.9l23,16.3l-7.5-21.3
\t\t\tC105.3,78.5,113,68.7,113,57.6z"/>
          <path class="st5" d="M52.9,59.8c-11.5-3.5-20.5-10.6-24.2-19.3c-4.2,5-6.7,10.8-6.7,17c0,3.6,0.8,7,2.3,10.2
\t\t\tc1.7,0.2,3.4,0.3,5.2,0.3C39.1,68.1,47.5,64.8,52.9,59.8z"/>
          <path class="st6" d="M67.6,24.9c-8,0-15.5,1.5-22,4.1c8.2,3.8,13.6,10.4,13.6,17.9c0,4.9-2.3,9.4-6.2,13c3.6,1.1,7.4,1.9,11.4,2.2
\t\t\tl10.2,13.2l2.8-13.5c14.2-1.8,25.9-8.6,31.6-17.8C101.8,32.7,85.9,24.9,67.6,24.9z"/>
          <path class="st7" d="M59.1,46.8c0-7.5-5.4-14.1-13.6-17.9c-6.9,2.7-12.7,6.7-16.8,11.5c3.7,8.7,12.6,15.8,24.2,19.3
\t\t\tC56.8,56.2,59.1,51.7,59.1,46.8z"/>
\t</g>
</g>
</svg>

  `
                }]
        }], ctorParameters: function () { return []; } });

class LoginComponent {
    constructor(formBuilder, capsLockService) {
        this.formBuilder = formBuilder;
        this.capsLockService = capsLockService;
        this.submitLoginForm = new EventEmitter();
        this.forgotPassword = new EventEmitter();
        this.selectRememberMe = new EventEmitter();
        this.defaultEmail = '';
        this.infoText = ''; // if length is 0, we don't show the info notice
        this.isError = false;
        this.loading = false;
        this.isLockedOut = false;
        this.infoAlertLevel = AlertLevel.INFO;
        this.errorAlertLevel = AlertLevel.DANGER;
        this.defaultTextLabels = {
            rememberMe: 'Remember me',
            forgotPassword: 'Forgot Password?',
            email: 'Email',
            password: 'Password',
            submit: 'Login',
            error: 'Invalid Email/Password Combination.',
            capsLock: 'Check Caps Lock.',
            lockedOut: 'You have been locked out of CX Studio due to too many incorrect login attempts. Please contact' +
                ' support@clarabridge.com to reset your account.'
        };
        this.btnClasses = [
            'border-radius-2',
            'border-1',
            'border-solid',
            'cursor-pointer',
            'text-lg-1',
            'line-height-1',
            'ph-16',
            'pv-8',
            'align-self-center',
            'align-self-center',
            'text-transform-capitalize',
            'transition-border',
            'd-inline-block',
            'w-100-percent',
            'border-action-600',
            'hover:bg-white'
        ];
        this.inputClasses = [
            'input',
            'form-control',
            'd-block',
            'border-radius-2',
            'p-8',
            'text-base',
            'border-1',
            'border-gray-900',
            'mt-8',
            'h-32',
            'box-sizing-box',
            'text-gray-900',
            'w-100-percent'
        ];
        this.inputDisabledClasses = [
            'bg-gray-200',
            'opacity-50',
            'cursor-default'
        ];
        this.formClasses = [
            'bg-white'
        ];
        this.classes = {
            btn: [...this.btnClasses],
            btnPrimary: [
                ...this.btnClasses,
                'bg-action-600',
                'border-color-action-600',
                'text-white',
                'hover:text-action-600',
            ],
            input: [...this.inputClasses],
            inputDisabled: [...this.inputClasses, ...this.inputDisabledClasses],
            form: [...this.formClasses]
        };
    }
    ngOnInit() {
        this.loginTexts = { ...this.defaultTextLabels, ...this.textLabels };
        this.capsLockHandLer = this.capsLockService.onCapsLock((state) => {
            this.isCapsLockOn = state;
        });
        this.form = this.formBuilder.group({
            email: [this.defaultEmail, [Validators.email, Validators.required]],
            password: ['', Validators.required],
            rememberUser: [false]
        });
    }
    ngOnDestroy() {
        this.capsLockService.removeHandler(this.capsLockHandLer);
    }
    submitForm() {
        if (this.isLockedOut) {
            return;
        }
        else if (this.form.invalid) {
            this.isError = true;
        }
        else {
            this.submitLoginForm.emit(this.form.getRawValue());
        }
    }
    getCompleteErrorText() {
        const { error = '', capsLock = '' } = this.loginTexts;
        return this.isCapsLockOn ? `${error} ${capsLock}` : error;
    }
}
LoginComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: LoginComponent, deps: [{ token: i1$1.UntypedFormBuilder }, { token: CapsLockService }], target: i0.ɵɵFactoryTarget.Component });
LoginComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: LoginComponent, selector: "cb-login", inputs: { defaultEmail: "defaultEmail", infoText: "infoText", isError: "isError", loading: "loading", isLockedOut: "isLockedOut", textLabels: "textLabels" }, outputs: { submitLoginForm: "submitLoginForm", forgotPassword: "forgotPassword", selectRememberMe: "selectRememberMe" }, ngImport: i0, template: `
		<div class="w-max-432 w-min-280 ">
			<div class="p-relative w-288 mh-auto mb-32">
				<cb-login-logo></cb-login-logo>
			</div>
			<div class="d-block">
				<cb-login-alert *ngIf="isLockedOut" [alertLevel]="errorAlertLevel">
					<span>{{loginTexts.lockedOut}}</span>
				</cb-login-alert>
				<cb-login-alert *ngIf="isError && !isLockedOut" [alertLevel]="errorAlertLevel">
					<span>{{getCompleteErrorText()}}</span>
				</cb-login-alert>
				<cb-login-alert *ngIf="!isError && !isLockedOut && infoText?.length > 0" [alertLevel]="infoAlertLevel">
					<span>{{infoText}}</span>
				</cb-login-alert>
			</div>
			<div [ngClass]="(infoText || isError) ? 'border-t-radius-0': 'border-t-radius-2'"
			     class="p-24 bg-white w-100-percent p-relative box-sizing-box border-b-radius-2">
				<form [formGroup]="form" (ngSubmit)="submitForm()" [class]="classes.form.join(' ')">
					<div class="mb-16">
						<label>
							<span class="text-base font-standard text-gray-900 ml-8">{{loginTexts.email}}</span>
							<input type="email" [attr.disabled]="loading || isLockedOut || null" [formControlName]="'email'"
							       [class]="(!loading && !isLockedOut) ? classes.input.join(' ') : classes.inputDisabled.join(' ')"/>
						</label>
					</div>
					<div class="mb-16">
						<label>
							<span class="text-base font-standard text-gray-900 ml-8">{{loginTexts.password}}</span>
							<input type="password" [attr.disabled]="loading || isLockedOut || null" [formControlName]="'password'"
							       [class]="(!loading && !isLockedOut) ? classes.input.join(' ') : classes.inputDisabled.join(' ')"/>
						</label>
					</div>
					<button type="submit" [disabled]="loading" [class]="classes.btnPrimary.join(' ')"
					        [class.btn-disabled]="loading" class="btn">
						<span *ngIf="!loading">{{loginTexts.submit}}</span>
						<div *ngIf="loading" class="q-icon-spinner rotate-infinite"></div>
					</button>
					<div class="d-flex-inline w-100-percent justify-between mt-32">
						<label class="d-flex-inline cursor-pointer">
							<input type="checkbox" class="m-2" [formControlName]="'rememberUser'"
							       (change)="selectRememberMe.emit($event.target['checked'])">
							<p class="m-0 cursor-pointer text-base font-standard text-gray-900 ml-8">{{loginTexts.rememberMe}}</p>
						</label>
						<a tabindex="0" class="text-decoration-none cursor-pointer text-base font-standard text-gray-900"
						   (click)="forgotPassword.emit()">{{loginTexts.forgotPassword}}</a>
					</div>
				</form>
			</div>
		</div>


	`, isInline: true, styles: [".img-container{max-width:288px}.form-container{max-width:432px}.text-decoration-none{text-decoration:none!important}.line-height-lg-5{line-height:1.71rem!important}.line-height-1{line-height:1!important}.transition-border{transition:background .15s,border-color .15s,color .15s!important}.w-max-432{max-width:432px!important}.w-288{width:288px!important}.btn:disabled,.btn-disabled{background-color:var(--white-100)!important;border-color:var(--gray-900);color:var(--gray-900);pointer-events:none!important;cursor:not-allowed!important}.btn:active,.btn-disabled:active{color:var(--white-100)!important;background-color:var(--action-800)!important;border-color:var(action-800)!important}.rotate-infinite{animation:spin 2.1s infinite linear;-webkit-animation:spin 2.1s infinite linear}@keyframes spin{0%{transform:rotate(0)}to{transform:rotate(360deg)}}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { kind: "directive", type: i1$1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1$1.CheckboxControlValueAccessor, selector: "input[type=checkbox][formControlName],input[type=checkbox][formControl],input[type=checkbox][ngModel]" }, { kind: "directive", type: i1$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$1.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i1$1.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i1$1.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "component", type: LoginAlertComponent, selector: "cb-login-alert", inputs: ["alertLevel", "rounded"] }, { kind: "component", type: LoginLogoComponent, selector: "cb-login-logo" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: LoginComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cb-login', template: `
		<div class="w-max-432 w-min-280 ">
			<div class="p-relative w-288 mh-auto mb-32">
				<cb-login-logo></cb-login-logo>
			</div>
			<div class="d-block">
				<cb-login-alert *ngIf="isLockedOut" [alertLevel]="errorAlertLevel">
					<span>{{loginTexts.lockedOut}}</span>
				</cb-login-alert>
				<cb-login-alert *ngIf="isError && !isLockedOut" [alertLevel]="errorAlertLevel">
					<span>{{getCompleteErrorText()}}</span>
				</cb-login-alert>
				<cb-login-alert *ngIf="!isError && !isLockedOut && infoText?.length > 0" [alertLevel]="infoAlertLevel">
					<span>{{infoText}}</span>
				</cb-login-alert>
			</div>
			<div [ngClass]="(infoText || isError) ? 'border-t-radius-0': 'border-t-radius-2'"
			     class="p-24 bg-white w-100-percent p-relative box-sizing-box border-b-radius-2">
				<form [formGroup]="form" (ngSubmit)="submitForm()" [class]="classes.form.join(' ')">
					<div class="mb-16">
						<label>
							<span class="text-base font-standard text-gray-900 ml-8">{{loginTexts.email}}</span>
							<input type="email" [attr.disabled]="loading || isLockedOut || null" [formControlName]="'email'"
							       [class]="(!loading && !isLockedOut) ? classes.input.join(' ') : classes.inputDisabled.join(' ')"/>
						</label>
					</div>
					<div class="mb-16">
						<label>
							<span class="text-base font-standard text-gray-900 ml-8">{{loginTexts.password}}</span>
							<input type="password" [attr.disabled]="loading || isLockedOut || null" [formControlName]="'password'"
							       [class]="(!loading && !isLockedOut) ? classes.input.join(' ') : classes.inputDisabled.join(' ')"/>
						</label>
					</div>
					<button type="submit" [disabled]="loading" [class]="classes.btnPrimary.join(' ')"
					        [class.btn-disabled]="loading" class="btn">
						<span *ngIf="!loading">{{loginTexts.submit}}</span>
						<div *ngIf="loading" class="q-icon-spinner rotate-infinite"></div>
					</button>
					<div class="d-flex-inline w-100-percent justify-between mt-32">
						<label class="d-flex-inline cursor-pointer">
							<input type="checkbox" class="m-2" [formControlName]="'rememberUser'"
							       (change)="selectRememberMe.emit($event.target['checked'])">
							<p class="m-0 cursor-pointer text-base font-standard text-gray-900 ml-8">{{loginTexts.rememberMe}}</p>
						</label>
						<a tabindex="0" class="text-decoration-none cursor-pointer text-base font-standard text-gray-900"
						   (click)="forgotPassword.emit()">{{loginTexts.forgotPassword}}</a>
					</div>
				</form>
			</div>
		</div>


	`, styles: [".img-container{max-width:288px}.form-container{max-width:432px}.text-decoration-none{text-decoration:none!important}.line-height-lg-5{line-height:1.71rem!important}.line-height-1{line-height:1!important}.transition-border{transition:background .15s,border-color .15s,color .15s!important}.w-max-432{max-width:432px!important}.w-288{width:288px!important}.btn:disabled,.btn-disabled{background-color:var(--white-100)!important;border-color:var(--gray-900);color:var(--gray-900);pointer-events:none!important;cursor:not-allowed!important}.btn:active,.btn-disabled:active{color:var(--white-100)!important;background-color:var(--action-800)!important;border-color:var(action-800)!important}.rotate-infinite{animation:spin 2.1s infinite linear;-webkit-animation:spin 2.1s infinite linear}@keyframes spin{0%{transform:rotate(0)}to{transform:rotate(360deg)}}\n"] }]
        }], ctorParameters: function () { return [{ type: i1$1.UntypedFormBuilder }, { type: CapsLockService }]; }, propDecorators: { submitLoginForm: [{
                type: Output
            }], forgotPassword: [{
                type: Output
            }], selectRememberMe: [{
                type: Output
            }], defaultEmail: [{
                type: Input
            }], infoText: [{
                type: Input
            }], isError: [{
                type: Input
            }], loading: [{
                type: Input
            }], isLockedOut: [{
                type: Input
            }], textLabels: [{
                type: Input
            }] } });

class LoginModule {
}
LoginModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: LoginModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
LoginModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: LoginModule, declarations: [LoginComponent, LoginAlertComponent, LoginLogoComponent], imports: [CommonModule,
        ReactiveFormsModule], exports: [LoginComponent] });
LoginModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: LoginModule, providers: [CapsLockService], imports: [CommonModule,
        ReactiveFormsModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: LoginModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        ReactiveFormsModule
                    ],
                    exports: [
                        LoginComponent
                    ],
                    declarations: [LoginComponent, LoginAlertComponent, LoginLogoComponent],
                    providers: [CapsLockService],
                }]
        }] });

class SpeechBubbleComponent {
    constructor() {
        this.borderColor = 'inherit';
    }
}
SpeechBubbleComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SpeechBubbleComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SpeechBubbleComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: SpeechBubbleComponent, selector: "cb-speech-bubble", inputs: { borderColor: "borderColor" }, ngImport: i0, template: `
<article class="bubble mb-8 p-16 border-radius-8"
	[ngStyle]="{'border-color': borderColor}">
	<ng-content></ng-content>
</article>

<span class="p-relative arrow-down-border" [ngStyle]="{'border-top-color': borderColor}"></span>
<span class="p-relative arrow-down"></span>`, isInline: true, styles: [".bubble{word-break:break-word;border-width:1px;border-style:solid}.arrow-down,.arrow-down-border{border-style:solid;border-width:10px 10px 0 10px;z-index:1;border-color:#ffffff transparent transparent transparent}.arrow-down{top:0;left:63px}.arrow-down-border{top:1px;left:83px}\n"], dependencies: [{ kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SpeechBubbleComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cb-speech-bubble', changeDetection: ChangeDetectionStrategy.OnPush, template: `
<article class="bubble mb-8 p-16 border-radius-8"
	[ngStyle]="{'border-color': borderColor}">
	<ng-content></ng-content>
</article>

<span class="p-relative arrow-down-border" [ngStyle]="{'border-top-color': borderColor}"></span>
<span class="p-relative arrow-down"></span>`, styles: [".bubble{word-break:break-word;border-width:1px;border-style:solid}.arrow-down,.arrow-down-border{border-style:solid;border-width:10px 10px 0 10px;z-index:1;border-color:#ffffff transparent transparent transparent}.arrow-down{top:0;left:63px}.arrow-down-border{top:1px;left:83px}\n"] }]
        }], propDecorators: { borderColor: [{
                type: Input
            }] } });

class SpeechBubbleModule {
}
SpeechBubbleModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SpeechBubbleModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SpeechBubbleModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: SpeechBubbleModule, declarations: [SpeechBubbleComponent], imports: [CommonModule], exports: [SpeechBubbleComponent] });
SpeechBubbleModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SpeechBubbleModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SpeechBubbleModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [SpeechBubbleComponent],
                    exports: [SpeechBubbleComponent]
                }]
        }] });

var SortDirection;
(function (SortDirection) {
    SortDirection["ASCENDING"] = "ASC";
    SortDirection["DESCENDING"] = "DESC";
})(SortDirection || (SortDirection = {}));
class SortDirectionComponent {
    constructor() {
        this.directionChange = new EventEmitter();
        this.OPTIONS = [
            {
                value: SortDirection.ASCENDING,
                icon: 'q-icon q-icon-sort-up',
                class: 'sort-direction-icon',
                ariaLabel: 'Sort Ascending',
                tooltip: 'Sort Ascending'
            },
            {
                value: SortDirection.DESCENDING,
                icon: 'q-icon q-icon-sort-down',
                class: 'sort-direction-icon',
                ariaLabel: 'Sort Descending',
                tooltip: 'Sort Descending'
            }
        ];
    }
}
SortDirectionComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SortDirectionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SortDirectionComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: SortDirectionComponent, selector: "cb-sort-direction", inputs: { direction: "direction" }, outputs: { directionChange: "directionChange" }, ngImport: i0, template: `
<cb-push-buttons
	[list]="OPTIONS"
	[(selectedValue)]="direction"
	(selectedValueChange)="directionChange.emit(direction)">
</cb-push-buttons>
`, isInline: true, dependencies: [{ kind: "component", type: PushButtonsComponent, selector: "cb-push-buttons", inputs: ["list", "selectedValue"], outputs: ["selectedValueChange"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SortDirectionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'cb-sort-direction',
                    template: `
<cb-push-buttons
	[list]="OPTIONS"
	[(selectedValue)]="direction"
	(selectedValueChange)="directionChange.emit(direction)">
</cb-push-buttons>
`
                }]
        }], propDecorators: { direction: [{
                type: Input
            }], directionChange: [{
                type: Output
            }] } });

class SortDirectionModule {
}
SortDirectionModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SortDirectionModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SortDirectionModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: SortDirectionModule, declarations: [SortDirectionComponent], imports: [CommonModule, PushButtonsModule], exports: [SortDirectionComponent] });
SortDirectionModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SortDirectionModule, imports: [CommonModule, PushButtonsModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SortDirectionModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SortDirectionComponent],
                    imports: [CommonModule, PushButtonsModule],
                    exports: [SortDirectionComponent],
                }]
        }] });

var AccountSwitcherLabels;
(function (AccountSwitcherLabels) {
    AccountSwitcherLabels["TITLE"] = "Find Accounts";
    AccountSwitcherLabels["NO_ITEMS"] = "No accounts available";
    AccountSwitcherLabels["NO_RESULTS"] = "No results available";
})(AccountSwitcherLabels || (AccountSwitcherLabels = {}));

class AccountSwitcherComponent {
    constructor() {
        this.searchValue = '';
        this.options = [];
        this.filterLabel = AccountSwitcherLabels.TITLE;
        this.noAccountsLabel = AccountSwitcherLabels.NO_ITEMS;
        this.noResultsLabel = AccountSwitcherLabels.NO_RESULTS;
        this.optionsList$ = of();
        this.accountSelected = new EventEmitter();
        this.alignRight = false;
    }
    ngOnInit() {
        this.getOptions();
    }
    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }
    getOptions() {
        this.subscription = this.optionsList$?.subscribe(data => {
            this.options = data;
        }, err => {
            // do nothing
        });
    }
    onClose($event) {
        if (!$event) {
            this.searchValue = ''; // clear text on close
        }
        else {
            this.searchableListComponent.focusOnSearchField();
        }
    }
    onSelectOption($event) {
        this.accountSelected.emit($event);
    }
}
AccountSwitcherComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AccountSwitcherComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
AccountSwitcherComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AccountSwitcherComponent, selector: "cb-account-switcher", inputs: { optionsList$: "optionsList$", alignRight: "alignRight" }, outputs: { accountSelected: "accountSelected" }, viewQueries: [{ propertyName: "searchableListComponent", first: true, predicate: SearchableListComponent, descendants: true }], ngImport: i0, template: "<div ngbDropdown display=\"static\" #accountSwitcher=ngbDropdown\r\n\t class=\"h-100-percent border-l-1 border-l-solid border-color-inherit\" (openChange)=\"onClose($event)\">\r\n\t<a ngbDropdownToggle tabindex=\"0\" href=\"javascript:void(0)\" role=\"button\"\r\n\t   class=\"text-decoration-none cursor-pointer font-inherit h-100-percent align-items-center d-flex ph-16\"\r\n\t   title=\"Change Master Account\"\r\n\t   aria-haspopup=\"true\"\r\n\t   (keyup.space)=\"accountSwitcher.toggle()\"\r\n\t   [attr.aria-expanded]=\"accountSwitcher.isOpen()\">\r\n\t\t<span class=\"w-max-200 overflow-hidden text-ellipsis\"><ng-content></ng-content></span>\r\n\t\t<span class=\"q-icon q-icon-angle-bottom ml-8\" aria-hidden=\"true\"></span>\r\n\t</a>\r\n\t<div ngbDropdownMenu x-placement=\"bottom-right\" [ngClass]=\"{'dropdown-menu dropdown-menu-right': alignRight}\"\r\n\t\t class=\"w-fit-content w-max-320 w-min-240 p-0 border-0 box-shadow-default text-gray-900 text-base font-standard\">\r\n\t\t<cb-searchable-list class=\"border-radius-4\"\r\n\t\t\t[allowTabOut]=\"false\"\r\n\t\t\t[options]=\"options\"\r\n\t\t\t[displayProperty]=\"'name'\"\r\n\t\t\t[filterLabel]=\"filterLabel\"\r\n\t\t\t[noItemsLabel]=\"noAccountsLabel\"\r\n\t\t\t[noResultsLabel]=\"noResultsLabel\"\r\n\t\t\t(selectOption)=\"onSelectOption($event); accountSwitcher.close()\">\r\n\t\t</cb-searchable-list>\r\n\t</div>\r\n</div>\r\n\r\n", styles: [".dropdown.show .dropdown-toggle{background:var(--header-active-menu-bg, var(--white));color:var(--header-link-color-hover, var(--action-700))!important}.dropdown-toggle:hover{color:var(--header-link-color-hover, var(--action-700))!important}.dropdown-toggle:after{display:none!important}.dropdown-toggle:focus,.dropdown-toggle:focus-visible{outline:2px solid var(--focus-outline-color, var(--gray-1000))!important;outline-offset:-2px!important}.dropdown-menu{background-color:var(--white)}:host-context(:root,.light-mode){--black-50-percent-fallback: rgba(0, 0, 0, .5)}:host-context(.dark-mode,.application-dark-mode){--black-50-percent-fallback: rgba(255, 255, 255, .5)}.box-shadow-default{box-shadow:0 0 8px var(--black-50-percent, var(--black-50-percent-fallback))!important}.w-fit-content{width:fit-content!important;width:-moz-fit-content!important}.font-inherit{color:inherit!important;font-family:inherit!important;font-size:inherit!important}.bg-focus-primary-color-300:focus-visible,.bg-focus-primary-color-300:focus{background-color:var(--action-600)!important}.bg-active-action-800:active{background-color:var(--action-800)!important}.text-ellipsis{text-overflow:ellipsis!important}.whitespace-pre-line{white-space:pre-line!important}.line-height-16{line-height:16px!important}.border-color-inherit{border-color:inherit!important}.text-decoration-none{text-decoration:none!important}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgbDropdown, selector: "[ngbDropdown]", inputs: ["autoClose", "dropdownClass", "open", "placement", "popperOptions", "container", "display"], outputs: ["openChange"], exportAs: ["ngbDropdown"] }, { kind: "directive", type: i2.NgbDropdownToggle, selector: "[ngbDropdownToggle]" }, { kind: "directive", type: i2.NgbDropdownMenu, selector: "[ngbDropdownMenu]" }, { kind: "component", type: SearchableListComponent, selector: "cb-searchable-list", inputs: ["options", "displayProperty", "allowTabOut", "noItemsLabel", "noResultsLabel", "filterLabel"], outputs: ["selectOption"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AccountSwitcherComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cb-account-switcher', template: "<div ngbDropdown display=\"static\" #accountSwitcher=ngbDropdown\r\n\t class=\"h-100-percent border-l-1 border-l-solid border-color-inherit\" (openChange)=\"onClose($event)\">\r\n\t<a ngbDropdownToggle tabindex=\"0\" href=\"javascript:void(0)\" role=\"button\"\r\n\t   class=\"text-decoration-none cursor-pointer font-inherit h-100-percent align-items-center d-flex ph-16\"\r\n\t   title=\"Change Master Account\"\r\n\t   aria-haspopup=\"true\"\r\n\t   (keyup.space)=\"accountSwitcher.toggle()\"\r\n\t   [attr.aria-expanded]=\"accountSwitcher.isOpen()\">\r\n\t\t<span class=\"w-max-200 overflow-hidden text-ellipsis\"><ng-content></ng-content></span>\r\n\t\t<span class=\"q-icon q-icon-angle-bottom ml-8\" aria-hidden=\"true\"></span>\r\n\t</a>\r\n\t<div ngbDropdownMenu x-placement=\"bottom-right\" [ngClass]=\"{'dropdown-menu dropdown-menu-right': alignRight}\"\r\n\t\t class=\"w-fit-content w-max-320 w-min-240 p-0 border-0 box-shadow-default text-gray-900 text-base font-standard\">\r\n\t\t<cb-searchable-list class=\"border-radius-4\"\r\n\t\t\t[allowTabOut]=\"false\"\r\n\t\t\t[options]=\"options\"\r\n\t\t\t[displayProperty]=\"'name'\"\r\n\t\t\t[filterLabel]=\"filterLabel\"\r\n\t\t\t[noItemsLabel]=\"noAccountsLabel\"\r\n\t\t\t[noResultsLabel]=\"noResultsLabel\"\r\n\t\t\t(selectOption)=\"onSelectOption($event); accountSwitcher.close()\">\r\n\t\t</cb-searchable-list>\r\n\t</div>\r\n</div>\r\n\r\n", styles: [".dropdown.show .dropdown-toggle{background:var(--header-active-menu-bg, var(--white));color:var(--header-link-color-hover, var(--action-700))!important}.dropdown-toggle:hover{color:var(--header-link-color-hover, var(--action-700))!important}.dropdown-toggle:after{display:none!important}.dropdown-toggle:focus,.dropdown-toggle:focus-visible{outline:2px solid var(--focus-outline-color, var(--gray-1000))!important;outline-offset:-2px!important}.dropdown-menu{background-color:var(--white)}:host-context(:root,.light-mode){--black-50-percent-fallback: rgba(0, 0, 0, .5)}:host-context(.dark-mode,.application-dark-mode){--black-50-percent-fallback: rgba(255, 255, 255, .5)}.box-shadow-default{box-shadow:0 0 8px var(--black-50-percent, var(--black-50-percent-fallback))!important}.w-fit-content{width:fit-content!important;width:-moz-fit-content!important}.font-inherit{color:inherit!important;font-family:inherit!important;font-size:inherit!important}.bg-focus-primary-color-300:focus-visible,.bg-focus-primary-color-300:focus{background-color:var(--action-600)!important}.bg-active-action-800:active{background-color:var(--action-800)!important}.text-ellipsis{text-overflow:ellipsis!important}.whitespace-pre-line{white-space:pre-line!important}.line-height-16{line-height:16px!important}.border-color-inherit{border-color:inherit!important}.text-decoration-none{text-decoration:none!important}\n"] }]
        }], propDecorators: { optionsList$: [{
                type: Input
            }], accountSelected: [{
                type: Output
            }], alignRight: [{
                type: Input
            }], searchableListComponent: [{
                type: ViewChild,
                args: [SearchableListComponent, { static: false }]
            }] } });

class AccountSwitcherModule {
}
AccountSwitcherModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AccountSwitcherModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AccountSwitcherModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: AccountSwitcherModule, declarations: [AccountSwitcherComponent], imports: [CommonModule,
        FormsModule,
        NgbModule,
        SelectModule,
        SearchableListModule], exports: [AccountSwitcherComponent] });
AccountSwitcherModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AccountSwitcherModule, imports: [CommonModule,
        FormsModule,
        NgbModule,
        SelectModule,
        SearchableListModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AccountSwitcherModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [AccountSwitcherComponent],
                    imports: [
                        CommonModule,
                        FormsModule,
                        NgbModule,
                        SelectModule,
                        SearchableListModule
                    ],
                    exports: [AccountSwitcherComponent]
                }]
        }] });

class ToggleComponent {
    constructor(ref) {
        this.ref = ref;
        // Placeholders for the callbacks which are later provided
        // by the Control Value Accessor
        this.onTouchedCallback = () => {
            // intentionally left empty
        };
        this.onChangeCallback = () => {
            // intentionally left empty
        };
    }
    toggleValue(event) {
        if (this.disabled) {
            return;
        }
        event.stopPropagation();
        event.preventDefault();
        this.innerValue = !this.innerValue;
        this.onChangeCallback(this.innerValue);
        this.onTouchedCallback();
    }
    // get accessor
    get value() {
        return this.innerValue;
    }
    // set accessor including call the onchange callback
    set value(value) {
        if (value !== this.innerValue) {
            this.innerValue = value;
            this.onChangeCallback(value);
        }
    }
    // From ControlValueAccessor interface
    writeValue(value) {
        if (value !== this.innerValue) {
            this.innerValue = value;
            this.ref.markForCheck();
            setTimeout(() => this.ref.markForCheck()); // to check initial value
        }
    }
    // From ControlValueAccessor interface
    registerOnChange(fn) {
        this.onChangeCallback = fn;
    }
    // From ControlValueAccessor interface
    registerOnTouched(fn) {
        this.onTouchedCallback = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
        this.ref.detectChanges();
    }
}
ToggleComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ToggleComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
ToggleComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ToggleComponent, selector: "cb-toggle", inputs: { label: "label" }, providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ToggleComponent),
            multi: true
        }], ngImport: i0, template: `
		<button
			[attr.aria-checked]="value"
			[attr.aria-label]="label"
			[attr.aria-disabled]="disabled"
			role="switch"
			class="h-32 d-flex align-items-center bg-none border-0 box-sizing-box"
			(click)="toggleValue($event)">
			<div class="toggle-container p-0 h-24 w-48 border-2 border-solid overflow-hidden border-radius-full "
				[ngClass]="{
					'on bg-action-800 border-action-800': value,
					'border-gray-900': !value,
					'opacity-50 cursor-not-allowed': disabled,
					'cursor-pointer hover:bg-action-600 hover:border-action-600': !disabled
				}">
				<div class="toggle-background d-flex h-100-percent p-relative">
					<div class="toggle p-absolute border-radius-full"
					     [ngClass]="{
					     'on bg-white': value,
					     'bg-gray-900': !value
					     }"></div>
				</div>
			</div>
		</button>
	`, isInline: true, styles: ["button:focus,button:focus-within,button:focus-visible{outline-style:solid;outline-width:2px;outline-offset:0;outline-color:var(--focus-outline-color, var(--action-1000))}.toggle-background,.toggle,.toggle-container{transition:background-color .3s cubic-bezier(0,0,.2,1),border-color .3s cubic-bezier(0,0,.2,1)}.toggle{top:1px;width:18px;height:18px;transition:left .3s cubic-bezier(0,0,.2,1);left:2px}.on .toggle{left:calc(100% - 20px)}.hover\\:border-action-600:hover{border-color:var(--action-600, var(--blue-600))!important}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ToggleComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cb-toggle', template: `
		<button
			[attr.aria-checked]="value"
			[attr.aria-label]="label"
			[attr.aria-disabled]="disabled"
			role="switch"
			class="h-32 d-flex align-items-center bg-none border-0 box-sizing-box"
			(click)="toggleValue($event)">
			<div class="toggle-container p-0 h-24 w-48 border-2 border-solid overflow-hidden border-radius-full "
				[ngClass]="{
					'on bg-action-800 border-action-800': value,
					'border-gray-900': !value,
					'opacity-50 cursor-not-allowed': disabled,
					'cursor-pointer hover:bg-action-600 hover:border-action-600': !disabled
				}">
				<div class="toggle-background d-flex h-100-percent p-relative">
					<div class="toggle p-absolute border-radius-full"
					     [ngClass]="{
					     'on bg-white': value,
					     'bg-gray-900': !value
					     }"></div>
				</div>
			</div>
		</button>
	`, providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => ToggleComponent),
                            multi: true
                        }], changeDetection: ChangeDetectionStrategy.OnPush, styles: ["button:focus,button:focus-within,button:focus-visible{outline-style:solid;outline-width:2px;outline-offset:0;outline-color:var(--focus-outline-color, var(--action-1000))}.toggle-background,.toggle,.toggle-container{transition:background-color .3s cubic-bezier(0,0,.2,1),border-color .3s cubic-bezier(0,0,.2,1)}.toggle{top:1px;width:18px;height:18px;transition:left .3s cubic-bezier(0,0,.2,1);left:2px}.on .toggle{left:calc(100% - 20px)}.hover\\:border-action-600:hover{border-color:var(--action-600, var(--blue-600))!important}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { label: [{
                type: Input
            }] } });

class ToggleModule {
}
ToggleModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ToggleModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ToggleModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: ToggleModule, declarations: [ToggleComponent], imports: [CommonModule, FormsModule], exports: [ToggleComponent] });
ToggleModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ToggleModule, imports: [CommonModule, FormsModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ToggleModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [ToggleComponent],
                    imports: [CommonModule, FormsModule],
                    exports: [ToggleComponent],
                }]
        }] });

class SpacesComponent {
    constructor() {
        this.buttonLabel = 'Dropdown Button';
        this.buttonTitle = 'Select User Space';
    }
    onChange($event) {
        if ($event) {
            const menu = this.dropdownMenu.nativeElement;
            const firstItem = menu.children[0];
            // allow menu to render before focusing
            setTimeout(() => {
                firstItem?.focus();
            }, 0);
        }
    }
    // Will return false for callbacks, which will redirecting to href
    onSelectOption($event, link) {
        if (!this.isURLLink(link)) {
            $event.preventDefault();
            $event.stopPropagation();
            link.callback();
            return false;
        }
        else {
            return true;
        }
    }
    handleForwardKeyNavigation($event, index) {
        if (index === this.optionsList.length - 1) {
            const focusableItems = this.dropdownMenu.nativeElement.querySelectorAll('.focusable');
            this.focusOnFirstOrLastItem($event, focusableItems.length - 1, focusableItems);
        }
    }
    handleBackwardKeyNavigation($event, index) {
        if (index === 0) {
            const focusableItems = this.dropdownMenu.nativeElement.querySelectorAll('.focusable');
            this.focusOnFirstOrLastItem($event, 0, focusableItems);
        }
    }
    focusOnFirstOrLastItem($event, currentIndex, items) {
        const nextIndex = currentIndex === 0 ? items.length - 1 : 0;
        if (items.length !== 0) {
            $event.preventDefault();
            $event.stopPropagation();
            items[nextIndex]?.focus();
        }
    }
    isURLLink(link) {
        return link && !!(link.url && !link.callback);
    }
    isIcon(option) {
        return !!(option.class && option.class.indexOf('q-icon') !== -1);
    }
}
SpacesComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SpacesComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SpacesComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: SpacesComponent, selector: "cb-spaces", inputs: { optionsList: "optionsList", buttonLabel: "buttonLabel", buttonTitle: "buttonTitle" }, viewQueries: [{ propertyName: "dropdownMenu", first: true, predicate: ["dropdown"], descendants: true }], ngImport: i0, template: "<div ngbDropdown #spaces=ngbDropdown placement=\"bottom-right\" class=\"h-100-percent border-l-1 border-l-solid border-color-inherit\"\n\t (openChange)=\"onChange($event)\">\n\t<a ngbDropdownToggle tabindex=\"0\" href=\"javascript:void(0)\" role=\"button\"\n\t\t\t[title]=\"buttonTitle\" [attr.aria-label]=\"buttonLabel\"\n\t\t\tclass=\"text-decoration-none cursor-pointer font-inherit h-100-percent align-items-center d-flex ph-16\"\n\t\t\taria-haspopup=\"true\"\n\t\t\t[attr.aria-expanded]=\"spaces.isOpen()\">\n\t\t<span class=\"w-max-200 overflow-hidden text-ellipsis\"><ng-content></ng-content></span>\n\t\t<span class=\"q-icon q-icon-angle-bottom ml-8\" aria-hidden=\"true\"></span>\n\t</a>\n\t<div role=\"menu\" #dropdown ngbDropdownMenu [class]=\"'dropdown-menu dropdown-menu-right '+ [\n\t\t\t'border-0',\n\t\t\t'box-shadow-default',\n\t\t\t'border-radius-4',\n\t\t\t'font-standard',\n\t\t\t'h-max-75-vh',\n\t\t\t'm-0',\n\t\t\t'ph-0',\n\t\t\t'pv-8',\n\t\t\t'overflow-y-auto',\n\t\t\t'text-base',\n\t\t\t'w-fit-content',\n\t\t\t'w-max-320',\n\t\t\t'w-min-240'\n\t\t].join(' ')\">\n\t\t<ng-container *ngFor=\"let option of optionsList; let i = index\">\n\t\t\t<a ngbDropdownItem *ngIf=\"!option.divider\"\n\t\t\t   [class]=\"[\n\t\t\t\t'active-text-white',\n\t\t\t\t'active:bg-action-800',\n\t\t\t\t'align-items-center',\n\t\t\t\t'header-dropdown-link',\n\t\t\t\t'hover:text-white',\n\t\t\t\t'box-sizing-box',\n\t\t\t\t'cursor-pointer',\n\t\t\t\t'd-flex',\n\t\t\t\t'focusable',\n\t\t\t\t'h-min-32',\n\t\t\t\t'line-height-16',\n\t\t\t\t'ph-16',\n\t\t\t\t'pv-8',\n\t\t\t\t'hover:bg-action-700',\n\t\t\t\t'text-gray-1000',\n\t\t\t\t'text-decoration-none',\n\t\t\t\t'text-wrap',\n\t\t\t\t'w-100-percent',\n\t\t\t\t'whitespace-pre-line'\n\t\t\t].join(' ')\"\n\t\t\t   role=\"menuitem\" tabindex=\"0\" href='{{option.url ? option.url : \"javascript:void()\"}}'\n\t\t\t   (keydown.tab)=\"handleForwardKeyNavigation($event, i)\"\n\t\t\t   (keydown.arrowDown)=\"handleForwardKeyNavigation($event, i)\"\n\t\t\t   (keydown.shift.tab)=\"handleBackwardKeyNavigation($event, i)\"\n\t\t\t   (keydown.arrowUp)=\"handleBackwardKeyNavigation($event, i)\"\n\t\t\t   (click)=\"onSelectOption($event, option); spaces.close()\">\n\t\t\t\t<span aria-hidden=\"true\" *ngIf=\"isIcon(option)\" class=\"q-icon mr-8 {{option.class}}\"></span>\n\t\t\t\t<span [class.ml-4]=\"isIcon(option)\">{{option.name}}</span>\n\t\t\t</a>\n\t\t\t\t<div *ngIf=\"option.divider\" class=\"divider mv-4\" role=\"separator\" aria-hidden=\"true\"></div>\n\t\t</ng-container>\n\n\t</div>\n</div>\n", styles: [".w-fit-content{width:fit-content!important;width:-moz-fit-content!important}.font-inherit{color:inherit!important;font-family:inherit!important;font-size:inherit!important}.bg-focus-primary-color-300:focus-visible,.bg-focus-primary-color-300:focus{background-color:var(--action-400)!important}.bg-active-action-800:active{background-color:var(--action-800)!important}.text-ellipsis{text-overflow:ellipsis!important}.whitespace-pre-line{white-space:pre-line!important}.line-height-16{line-height:16px!important}.border-color-inherit{border-color:inherit!important}.text-wrap{white-space:normal!important}.text-decoration-none{text-decoration:none!important}.active-text-white:active{color:var(--white)!important}.active\\:bg-action-800:active{background-color:var(--action-800, var(--blue-800))!important}.dropdown.show .dropdown-toggle{background:var(--header-active-menu-bg, var(--white));color:var(--header-link-color-hover, var(--action-700))!important}.dropdown-toggle:hover{color:var(--header-link-color-hover, var(--action-700))!important}.dropdown-toggle:after{display:none!important}.dropdown-toggle:focus,.dropdown-toggle:focus-visible{outline:2px solid var(--focus-outline-color, var(--gray-1000))!important;outline-offset:-2px!important}.dropdown-menu{background-color:var(--white)}:host-context(:root,.light-mode){--black-50-percent-fallback: rgba(0, 0, 0, .5)}:host-context(.dark-mode,.application-dark-mode){--black-50-percent-fallback: rgba(255, 255, 255, .5)}.box-shadow-default{box-shadow:0 0 8px var(--black-50-percent, var(--black-50-percent-fallback))!important}.dropdown-menu-right{right:0;left:auto}.divider{background-color:var(--gray-300);height:1px;pointer-events:none!important}.dropdown-item{color:var(--gray-900)}.header-dropdown-link:focus,.header-dropdown-link:focus-visible,.dropdown-item:focus,.dropdown-item:focus-visible{outline:2px solid var(--focus-outline-color, var(--action-1000));outline-offset:-2px!important;color:var(--focus-text-color, var(--white))!important;background-color:var(--focus-background-color, var(--action-700))}.header-dropdown-link:active,.dropdown-item:active{background-color:var(--header-dropdown-active, var(--action-800))}.active-text-white:active,.hover-text-white:hover{color:var(--white)!important}.hover-bg-action-700:hover{background-color:var(--action-700)}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgbDropdown, selector: "[ngbDropdown]", inputs: ["autoClose", "dropdownClass", "open", "placement", "popperOptions", "container", "display"], outputs: ["openChange"], exportAs: ["ngbDropdown"] }, { kind: "directive", type: i2.NgbDropdownToggle, selector: "[ngbDropdownToggle]" }, { kind: "directive", type: i2.NgbDropdownMenu, selector: "[ngbDropdownMenu]" }, { kind: "directive", type: i2.NgbDropdownItem, selector: "[ngbDropdownItem]", inputs: ["disabled"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SpacesComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cb-spaces', template: "<div ngbDropdown #spaces=ngbDropdown placement=\"bottom-right\" class=\"h-100-percent border-l-1 border-l-solid border-color-inherit\"\n\t (openChange)=\"onChange($event)\">\n\t<a ngbDropdownToggle tabindex=\"0\" href=\"javascript:void(0)\" role=\"button\"\n\t\t\t[title]=\"buttonTitle\" [attr.aria-label]=\"buttonLabel\"\n\t\t\tclass=\"text-decoration-none cursor-pointer font-inherit h-100-percent align-items-center d-flex ph-16\"\n\t\t\taria-haspopup=\"true\"\n\t\t\t[attr.aria-expanded]=\"spaces.isOpen()\">\n\t\t<span class=\"w-max-200 overflow-hidden text-ellipsis\"><ng-content></ng-content></span>\n\t\t<span class=\"q-icon q-icon-angle-bottom ml-8\" aria-hidden=\"true\"></span>\n\t</a>\n\t<div role=\"menu\" #dropdown ngbDropdownMenu [class]=\"'dropdown-menu dropdown-menu-right '+ [\n\t\t\t'border-0',\n\t\t\t'box-shadow-default',\n\t\t\t'border-radius-4',\n\t\t\t'font-standard',\n\t\t\t'h-max-75-vh',\n\t\t\t'm-0',\n\t\t\t'ph-0',\n\t\t\t'pv-8',\n\t\t\t'overflow-y-auto',\n\t\t\t'text-base',\n\t\t\t'w-fit-content',\n\t\t\t'w-max-320',\n\t\t\t'w-min-240'\n\t\t].join(' ')\">\n\t\t<ng-container *ngFor=\"let option of optionsList; let i = index\">\n\t\t\t<a ngbDropdownItem *ngIf=\"!option.divider\"\n\t\t\t   [class]=\"[\n\t\t\t\t'active-text-white',\n\t\t\t\t'active:bg-action-800',\n\t\t\t\t'align-items-center',\n\t\t\t\t'header-dropdown-link',\n\t\t\t\t'hover:text-white',\n\t\t\t\t'box-sizing-box',\n\t\t\t\t'cursor-pointer',\n\t\t\t\t'd-flex',\n\t\t\t\t'focusable',\n\t\t\t\t'h-min-32',\n\t\t\t\t'line-height-16',\n\t\t\t\t'ph-16',\n\t\t\t\t'pv-8',\n\t\t\t\t'hover:bg-action-700',\n\t\t\t\t'text-gray-1000',\n\t\t\t\t'text-decoration-none',\n\t\t\t\t'text-wrap',\n\t\t\t\t'w-100-percent',\n\t\t\t\t'whitespace-pre-line'\n\t\t\t].join(' ')\"\n\t\t\t   role=\"menuitem\" tabindex=\"0\" href='{{option.url ? option.url : \"javascript:void()\"}}'\n\t\t\t   (keydown.tab)=\"handleForwardKeyNavigation($event, i)\"\n\t\t\t   (keydown.arrowDown)=\"handleForwardKeyNavigation($event, i)\"\n\t\t\t   (keydown.shift.tab)=\"handleBackwardKeyNavigation($event, i)\"\n\t\t\t   (keydown.arrowUp)=\"handleBackwardKeyNavigation($event, i)\"\n\t\t\t   (click)=\"onSelectOption($event, option); spaces.close()\">\n\t\t\t\t<span aria-hidden=\"true\" *ngIf=\"isIcon(option)\" class=\"q-icon mr-8 {{option.class}}\"></span>\n\t\t\t\t<span [class.ml-4]=\"isIcon(option)\">{{option.name}}</span>\n\t\t\t</a>\n\t\t\t\t<div *ngIf=\"option.divider\" class=\"divider mv-4\" role=\"separator\" aria-hidden=\"true\"></div>\n\t\t</ng-container>\n\n\t</div>\n</div>\n", styles: [".w-fit-content{width:fit-content!important;width:-moz-fit-content!important}.font-inherit{color:inherit!important;font-family:inherit!important;font-size:inherit!important}.bg-focus-primary-color-300:focus-visible,.bg-focus-primary-color-300:focus{background-color:var(--action-400)!important}.bg-active-action-800:active{background-color:var(--action-800)!important}.text-ellipsis{text-overflow:ellipsis!important}.whitespace-pre-line{white-space:pre-line!important}.line-height-16{line-height:16px!important}.border-color-inherit{border-color:inherit!important}.text-wrap{white-space:normal!important}.text-decoration-none{text-decoration:none!important}.active-text-white:active{color:var(--white)!important}.active\\:bg-action-800:active{background-color:var(--action-800, var(--blue-800))!important}.dropdown.show .dropdown-toggle{background:var(--header-active-menu-bg, var(--white));color:var(--header-link-color-hover, var(--action-700))!important}.dropdown-toggle:hover{color:var(--header-link-color-hover, var(--action-700))!important}.dropdown-toggle:after{display:none!important}.dropdown-toggle:focus,.dropdown-toggle:focus-visible{outline:2px solid var(--focus-outline-color, var(--gray-1000))!important;outline-offset:-2px!important}.dropdown-menu{background-color:var(--white)}:host-context(:root,.light-mode){--black-50-percent-fallback: rgba(0, 0, 0, .5)}:host-context(.dark-mode,.application-dark-mode){--black-50-percent-fallback: rgba(255, 255, 255, .5)}.box-shadow-default{box-shadow:0 0 8px var(--black-50-percent, var(--black-50-percent-fallback))!important}.dropdown-menu-right{right:0;left:auto}.divider{background-color:var(--gray-300);height:1px;pointer-events:none!important}.dropdown-item{color:var(--gray-900)}.header-dropdown-link:focus,.header-dropdown-link:focus-visible,.dropdown-item:focus,.dropdown-item:focus-visible{outline:2px solid var(--focus-outline-color, var(--action-1000));outline-offset:-2px!important;color:var(--focus-text-color, var(--white))!important;background-color:var(--focus-background-color, var(--action-700))}.header-dropdown-link:active,.dropdown-item:active{background-color:var(--header-dropdown-active, var(--action-800))}.active-text-white:active,.hover-text-white:hover{color:var(--white)!important}.hover-bg-action-700:hover{background-color:var(--action-700)}\n"] }]
        }], propDecorators: { optionsList: [{
                type: Input
            }], buttonLabel: [{
                type: Input
            }], buttonTitle: [{
                type: Input
            }], dropdownMenu: [{
                type: ViewChild,
                args: ['dropdown', { static: false }]
            }] } });

class SpacesModule {
}
SpacesModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SpacesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SpacesModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: SpacesModule, declarations: [SpacesComponent], imports: [CommonModule,
        NgbModule], exports: [SpacesComponent] });
SpacesModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SpacesModule, imports: [CommonModule,
        NgbModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SpacesModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        SpacesComponent
                    ],
                    imports: [
                        CommonModule,
                        NgbModule
                    ],
                    exports: [SpacesComponent]
                }]
        }] });

class RadioGroupComponent {
    constructor() {
        // Placeholders for the callbacks which are later provided
        // by the Control Value Accessor
        this.onChangeCallback = ((__) => {
            // intentionally left empty
        });
        this.onTouchedCallback = (() => {
            // intentionally left empty
        });
        this.getId = (option, index) => {
            return _.isUndefined(option.htmlId) ? `${this.groupId}-${index}` : option.htmlId;
        };
        this.isOptionSelected = (option) => {
            return option.value === this.value ? '' : null;
        };
        this.select = (option) => {
            this.value = option.value;
            this.onChangeCallback(this.value);
            this.onTouchedCallback();
        };
    }
    ngOnInit() {
        this.groupId = UuidUtil.uuidv3();
    }
    // ControlValueAccessor
    writeValue(value) {
        if (value !== this.value) {
            this.value = value;
            this.onChangeCallback(this.value);
        }
    }
    registerOnChange(fn) {
        this.onChangeCallback = fn;
    }
    registerOnTouched(fn) {
        this.onTouchedCallback = fn;
    }
}
RadioGroupComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RadioGroupComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
RadioGroupComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: RadioGroupComponent, selector: "cb-radio-group", inputs: { options: "options", disabled: "disabled", inline: "inline" }, providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => RadioGroupComponent), multi: true },
    ], ngImport: i0, template: `
			<fieldset class="border-0 font-standard p-0 m-0 d-flex"
					  [ngClass]="{'flex-direction-column gap-8': !inline, 'gap-16': inline}">
				<div class="radio-wrapper" *ngFor="let option of options; index as $index">
					<label class="pv-8 ph-0 m-0 font-semibold d-flex align-items-center line-height-1" [ngClass]="{'disabled': disabled || option.disabled}"
						   [class.cursor-pointer]="!disabled" for="{{getId(option, $index)}}">
						<input type="radio"
							   [attr.name]="'_radio-'+groupId"
							   class="m-0 ml-8"
							   id="{{getId(option, $index)}}"
							   [value]="option.value"
							   [attr.checked]="isOptionSelected(option)"
							   [(ngModel)]="value"
							   (ngModelChange)="select(option)"
							   [disabled]="disabled || option.disabled">
						<span>{{option.displayName}}</span>
					</label>
				</div>
			</fieldset>
		`, isInline: true, styles: [".radio-wrapper{width:fit-content}.radio-wrapper:focus-within{outline:2px solid var(--focus-outline-color, var(--action-800));outline-offset:2px}.line-height-1{line-height:1!important}.gap-8{gap:8px!important}.gap-16{gap:16px!important}label{color:var(--gray-900)}label.disabled{pointer-events:none;color:var(--gray-600)}label.disabled input[type=radio]+span:before{border:2px solid var(--gray-600)}label input[type=radio]{opacity:0}label input[type=radio]+span{position:relative;display:inline-block;cursor:pointer;padding-left:8px}label input[type=radio]+span:before{content:\"\";display:inline-block;position:absolute;left:-24px;border-radius:50%;border:2px solid var(--gray-900);width:24px;height:24px;margin-top:-4px;box-sizing:border-box}label input[type=radio]+span:after{content:\"\";position:absolute;display:inline-block;left:-18px;top:6px;border-radius:50%;width:12px;height:12px;margin-top:-4px}label input[type=radio][checked]+span:after{background:var(--gray-900)}label input[type=radio][checked]+span:before{background:none}label:hover span:before{border:2px solid var(--action-600)}label:hover input[type=radio][checked]+span:after{background:var(--action-600)}label:hover:not(.disabled) input[type=radio]+span:before{border:2px solid var(--action-600)}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1$1.RadioControlValueAccessor, selector: "input[type=radio][formControlName],input[type=radio][formControl],input[type=radio][ngModel]", inputs: ["name", "formControlName", "value"] }, { kind: "directive", type: i1$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RadioGroupComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cb-radio-group', changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => RadioGroupComponent), multi: true },
                    ], template: `
			<fieldset class="border-0 font-standard p-0 m-0 d-flex"
					  [ngClass]="{'flex-direction-column gap-8': !inline, 'gap-16': inline}">
				<div class="radio-wrapper" *ngFor="let option of options; index as $index">
					<label class="pv-8 ph-0 m-0 font-semibold d-flex align-items-center line-height-1" [ngClass]="{'disabled': disabled || option.disabled}"
						   [class.cursor-pointer]="!disabled" for="{{getId(option, $index)}}">
						<input type="radio"
							   [attr.name]="'_radio-'+groupId"
							   class="m-0 ml-8"
							   id="{{getId(option, $index)}}"
							   [value]="option.value"
							   [attr.checked]="isOptionSelected(option)"
							   [(ngModel)]="value"
							   (ngModelChange)="select(option)"
							   [disabled]="disabled || option.disabled">
						<span>{{option.displayName}}</span>
					</label>
				</div>
			</fieldset>
		`, styles: [".radio-wrapper{width:fit-content}.radio-wrapper:focus-within{outline:2px solid var(--focus-outline-color, var(--action-800));outline-offset:2px}.line-height-1{line-height:1!important}.gap-8{gap:8px!important}.gap-16{gap:16px!important}label{color:var(--gray-900)}label.disabled{pointer-events:none;color:var(--gray-600)}label.disabled input[type=radio]+span:before{border:2px solid var(--gray-600)}label input[type=radio]{opacity:0}label input[type=radio]+span{position:relative;display:inline-block;cursor:pointer;padding-left:8px}label input[type=radio]+span:before{content:\"\";display:inline-block;position:absolute;left:-24px;border-radius:50%;border:2px solid var(--gray-900);width:24px;height:24px;margin-top:-4px;box-sizing:border-box}label input[type=radio]+span:after{content:\"\";position:absolute;display:inline-block;left:-18px;top:6px;border-radius:50%;width:12px;height:12px;margin-top:-4px}label input[type=radio][checked]+span:after{background:var(--gray-900)}label input[type=radio][checked]+span:before{background:none}label:hover span:before{border:2px solid var(--action-600)}label:hover input[type=radio][checked]+span:after{background:var(--action-600)}label:hover:not(.disabled) input[type=radio]+span:before{border:2px solid var(--action-600)}\n"] }]
        }], propDecorators: { options: [{
                type: Input
            }], disabled: [{
                type: Input
            }], inline: [{
                type: Input
            }] } });

class GenericRadioButtonsModule {
}
GenericRadioButtonsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: GenericRadioButtonsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
GenericRadioButtonsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: GenericRadioButtonsModule, declarations: [RadioGroupComponent], imports: [CommonModule,
        NgbModule,
        FormsModule], exports: [RadioGroupComponent] });
GenericRadioButtonsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: GenericRadioButtonsModule, imports: [CommonModule,
        NgbModule,
        FormsModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: GenericRadioButtonsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [RadioGroupComponent],
                    imports: [
                        CommonModule,
                        NgbModule,
                        FormsModule
                    ],
                    exports: [RadioGroupComponent],
                }]
        }] });

class ChangeUtils {
    static hasChange(simpleChange) {
        return simpleChange && !simpleChange.firstChange && simpleChange.currentValue !== simpleChange.previousValue;
    }
}
class CollapsingBase {
    constructor() {
        this.collapsedChange = new EventEmitter();
        this.toggleCollapse = (event) => {
            if (this.disableCollapse) {
                return;
            }
            this.panelCollapsed = !this.panelCollapsed;
            this.collapsedChange.emit(this.panelCollapsed);
        };
    }
    ngOnChanges(changes) {
        if (changes.openPanel?.previousValue !== changes.openPanel?.currentValue) {
            this.panelCollapsed = false;
        }
        if (changes.collapsed?.currentValue !== undefined) {
            this.panelCollapsed = changes.collapsed.currentValue;
        }
    }
}
CollapsingBase.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CollapsingBase, deps: [], target: i0.ɵɵFactoryTarget.Directive });
CollapsingBase.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: CollapsingBase, selector: "[collapsing-base]", inputs: { collapsed: "collapsed", openPanel: "openPanel", disableCollapse: "disableCollapse" }, outputs: { collapsedChange: "collapsedChange" }, usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CollapsingBase, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: '[collapsing-base]'
                }]
        }], propDecorators: { collapsed: [{
                type: Input
            }], openPanel: [{
                type: Input
            }], disableCollapse: [{
                type: Input
            }], collapsedChange: [{
                type: Output
            }] } });

class CollapsingPanelComponent extends CollapsingBase {
    constructor() {
        super(...arguments);
        this.disableCollapse = false;
        this.disabled = false;
        this.toggleLabel = 'Toggle Panel';
        this.collapsed = false;
        this.panelBodyId = UuidUtil.uuidv3();
        this.panelCollapsed = false;
        this.ngOnInit = () => {
            this.panelCollapsed = this.collapsed;
        };
        this.toggleCollapse = (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (!this.disableCollapse) {
                this.panelCollapsed = !this.panelCollapsed;
            }
        };
    }
}
CollapsingPanelComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CollapsingPanelComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
CollapsingPanelComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: CollapsingPanelComponent, selector: "cb-collapsing-panel", inputs: { disableCollapse: "disableCollapse", disabled: "disabled", toggleLabel: "toggleLabel", collapsed: "collapsed", panelBodyId: "panelBodyId" }, usesInheritance: true, ngImport: i0, template: `
<section class="panel panel-default font-standard text-gray-900 bg-none"
	[attr.aria-expanded]="!panelCollapsed" [class.disabled]="disabled">
	<button class="panel-heading d-flex justify-start pv-12 ph-16 text-gray-900 bg-gray-200 align-items-center box-sizing-box w-100-percent
	border-solid border-t-1 border-h-1 border-t-radius-2 border-gray-600"
		[ngClass]="{
		 	'cursor-pointer': !disableCollapse,
		 	'disableCollapse': disableCollapse,
			'expanded border-b-0 border-b-radius-0': !panelCollapsed,
			'border-b-1 border-b-radius-2': panelCollapsed
			}"
		(click)="toggleCollapse($event)" (keydown.enter)="toggleCollapse($event)"
		[attr.aria-expanded]="!collapsed"
		[attr.aria-controls]="panelBodyId"
		[attr.tabindex]="!disableCollapse && !disabled ? 0 : -1"
		[attr.aria-label]="!panelCollapsed && !disabled ? toggleLabel: null">
		<div class="panel-actions kb-focusable p-8" *ngIf="!disableCollapse" [attr.tabindex]="-1">
			<span class="q-icon q-icon-angle-bottom" [attr.aria-hidden]="true" *ngIf="!panelCollapsed"></span>
			<span class="q-icon q-icon-angle-right" [attr.aria-hidden]="true" *ngIf="panelCollapsed"></span>
		</div>
		<div class="d-flex flex-1">
			<ng-content select="panel-heading" class="flex-1"></ng-content>
		</div>
	</button>
	<div [id]="panelBodyId" class="panel-body bg-white box-sizing-box h-min-200 border-solid border-1 border-gray-600 p-16
	border-b-radius-2" *ngIf="!panelCollapsed">
		<ng-content select="panel-body"></ng-content>
	</div>
</section>
`, isInline: true, styles: [".panel-heading{min-height:56px!important}.panel-heading:not(.disableCollapse):focus,.panel-heading:not(.disableCollapse):focus-visible{outline:2px solid var(--focus-outline-color, var(--action-1000))!important}.pv-12{padding-top:12px!important;padding-bottom:12px!important}.disabled{opacity:.5!important;pointer-events:none!important}.panel-actions:focus,.panel-actions:focus-visible{outline:none!important}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CollapsingPanelComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cb-collapsing-panel', template: `
<section class="panel panel-default font-standard text-gray-900 bg-none"
	[attr.aria-expanded]="!panelCollapsed" [class.disabled]="disabled">
	<button class="panel-heading d-flex justify-start pv-12 ph-16 text-gray-900 bg-gray-200 align-items-center box-sizing-box w-100-percent
	border-solid border-t-1 border-h-1 border-t-radius-2 border-gray-600"
		[ngClass]="{
		 	'cursor-pointer': !disableCollapse,
		 	'disableCollapse': disableCollapse,
			'expanded border-b-0 border-b-radius-0': !panelCollapsed,
			'border-b-1 border-b-radius-2': panelCollapsed
			}"
		(click)="toggleCollapse($event)" (keydown.enter)="toggleCollapse($event)"
		[attr.aria-expanded]="!collapsed"
		[attr.aria-controls]="panelBodyId"
		[attr.tabindex]="!disableCollapse && !disabled ? 0 : -1"
		[attr.aria-label]="!panelCollapsed && !disabled ? toggleLabel: null">
		<div class="panel-actions kb-focusable p-8" *ngIf="!disableCollapse" [attr.tabindex]="-1">
			<span class="q-icon q-icon-angle-bottom" [attr.aria-hidden]="true" *ngIf="!panelCollapsed"></span>
			<span class="q-icon q-icon-angle-right" [attr.aria-hidden]="true" *ngIf="panelCollapsed"></span>
		</div>
		<div class="d-flex flex-1">
			<ng-content select="panel-heading" class="flex-1"></ng-content>
		</div>
	</button>
	<div [id]="panelBodyId" class="panel-body bg-white box-sizing-box h-min-200 border-solid border-1 border-gray-600 p-16
	border-b-radius-2" *ngIf="!panelCollapsed">
		<ng-content select="panel-body"></ng-content>
	</div>
</section>
`, styles: [".panel-heading{min-height:56px!important}.panel-heading:not(.disableCollapse):focus,.panel-heading:not(.disableCollapse):focus-visible{outline:2px solid var(--focus-outline-color, var(--action-1000))!important}.pv-12{padding-top:12px!important;padding-bottom:12px!important}.disabled{opacity:.5!important;pointer-events:none!important}.panel-actions:focus,.panel-actions:focus-visible{outline:none!important}\n"] }]
        }], propDecorators: { disableCollapse: [{
                type: Input
            }], disabled: [{
                type: Input
            }], toggleLabel: [{
                type: Input
            }], collapsed: [{
                type: Input
            }], panelBodyId: [{
                type: Input
            }] } });

class CollapsingPanelModule {
}
CollapsingPanelModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CollapsingPanelModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CollapsingPanelModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: CollapsingPanelModule, declarations: [CollapsingPanelComponent], imports: [CommonModule], exports: [CollapsingPanelComponent] });
CollapsingPanelModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CollapsingPanelModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CollapsingPanelModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CollapsingPanelComponent],
                    imports: [
                        CommonModule
                    ],
                    exports: [CollapsingPanelComponent]
                }]
        }] });

class CardComponent {
}
CardComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CardComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CardComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: CardComponent, selector: "cb-card", ngImport: i0, template: `
		<article class="card border-1 border-solid border-gray-900 border-radius-2 h-100-percent">
			<div class="card-body p-16 text-gray-900">
				<ng-content select="card-body"></ng-content>
			</div>
		</article>
	`, isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CardComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'cb-card',
                    template: `
		<article class="card border-1 border-solid border-gray-900 border-radius-2 h-100-percent">
			<div class="card-body p-16 text-gray-900">
				<ng-content select="card-body"></ng-content>
			</div>
		</article>
	`,
                    changeDetection: ChangeDetectionStrategy.OnPush
                }]
        }] });

class ActionCardComponent {
}
ActionCardComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ActionCardComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ActionCardComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ActionCardComponent, selector: "cb-action-card", ngImport: i0, template: `
	<cb-card>
		<card-body>
			<h5 class="card-title m-0 text-base font-bold"><ng-content select="card-title"></ng-content></h5>
			<p class="card-text m-0 pv-8"><ng-content select="card-description"></ng-content></p>
			<ng-content select="card-action"></ng-content>
		</card-body>
	</cb-card>
	`, isInline: true, dependencies: [{ kind: "component", type: CardComponent, selector: "cb-card" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ActionCardComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'cb-action-card',
                    template: `
	<cb-card>
		<card-body>
			<h5 class="card-title m-0 text-base font-bold"><ng-content select="card-title"></ng-content></h5>
			<p class="card-text m-0 pv-8"><ng-content select="card-description"></ng-content></p>
			<ng-content select="card-action"></ng-content>
		</card-body>
	</cb-card>
	`,
                    changeDetection: ChangeDetectionStrategy.OnPush
                }]
        }] });

class CardModule {
}
CardModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CardModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: CardModule, declarations: [CardComponent, ActionCardComponent], imports: [CommonModule], exports: [CardComponent, ActionCardComponent] });
CardModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CardModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CardModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [CardComponent, ActionCardComponent],
                    exports: [CardComponent, ActionCardComponent],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA]
                }]
        }] });

class ExpandingInputComponent {
    constructor() {
        this.placeholder = 'Click to Edit';
        this.maxLength = 250;
        this.editing = false;
        this.showTooltip = false;
        this.size = 10;
        // Placeholders for the callbacks which are later provided
        // by the Control Value Accessor
        this.onTouchedCallback = _.noop;
        this.onChangeCallback = _.noop;
        this.showBorder = () => {
            this.editing = true;
        };
        this.shouldShowTooltip = () => {
            return !!this.tooltipLabel && !this.editing;
        };
        this.resize = (val) => {
            if (val?.length === 0) {
                this.size = this.placeholder.length;
            }
            else {
                this.size = val.length;
            }
        };
        this.onChange = (value) => {
            if (value !== this.value) {
                this.onChangeCallback(this.value);
                this.onTouchedCallback();
            }
        };
        this.onTouch = () => { };
    }
    ngOnInit() {
        this.resize(this.placeholder);
    }
    // ControlValueAccessor
    writeValue(value) {
        if (value === null) {
            return;
        }
        if (value && value !== this.value) {
            this.value = value;
            this.size = this.value.length;
            this.onChange(value);
        }
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouch = fn;
    }
}
ExpandingInputComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ExpandingInputComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ExpandingInputComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ExpandingInputComponent, selector: "cb-expanding-input", inputs: { disabled: "disabled", tooltipLabel: "tooltipLabel", placeholder: "placeholder", maxLength: "maxLength" }, providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => ExpandingInputComponent), multi: true },
    ], ngImport: i0, template: `
			<ng-template #popover >
				<div class="ph-16 pv-8 border-radius-4 font-standard box-shadow-default bg-white text-gray-1000 text-sm-1" aria-live="assertive">
					{{tooltipLabel}}
				</div>
			</ng-template>
				<input
					[ngbPopover]="this.shouldShowTooltip() ? popover: null"
					[maxlength]="maxLength"
					[placement]="'right'"
					(input)="resize($event.target.value)"
					[attr.size]="size === 0 ? 1: size"
					placeholder="{{placeholder}}"
					triggers="mouseenter:mouseleave"
					[disabled]="disabled"
					class="form-control bg-transparent ph-8 pv-4 edit-title"
					(focus)="showBorder()"
					(blur)="editing=false"
					(ngModelChange)="onChange($event)"
					[openDelay]="300"
					[closeDelay]="500"
					[(ngModel)]="value">
	`, isInline: true, styles: [".dropdown.show .dropdown-toggle{background:var(--header-active-menu-bg, var(--white));color:var(--header-link-color-hover, var(--action-700))!important}.dropdown-toggle:hover{color:var(--header-link-color-hover, var(--action-700))!important}.dropdown-toggle:after{display:none!important}.dropdown-toggle:focus,.dropdown-toggle:focus-visible{outline:2px solid var(--focus-outline-color, var(--gray-1000))!important;outline-offset:-2px!important}.dropdown-menu{background-color:var(--white)}:host-context(:root,.light-mode){--black-50-percent-fallback: rgba(0, 0, 0, .5)}:host-context(.dark-mode,.application-dark-mode){--black-50-percent-fallback: rgba(255, 255, 255, .5)}.box-shadow-default{box-shadow:0 0 8px var(--black-50-percent, var(--black-50-percent-fallback))!important}input{border:1px solid transparent!important;height:inherit;color:inherit;background-color:transparent;border-radius:4px}input:hover{border:1px solid var(--gray-600)!important}input:focus,input:focus-visible{border:1px solid var(--gray-600)!important;box-shadow:none!important}.width-auto{width:auto!important}:host ::ng-deep .arrow:after{border-top-color:transparent!important;border-bottom-color:transparent!important;border-right-color:transparent!important;border-left-color:transparent!important;border-style:solid;position:absolute;content:\"\";border-width:5px 5px 5px 0;border-right-color:var(--white)!important;right:100%;left:auto;top:calc(50% - 8px)}\n"], dependencies: [{ kind: "directive", type: i2.NgbPopover, selector: "[ngbPopover]", inputs: ["animation", "autoClose", "ngbPopover", "popoverTitle", "placement", "popperOptions", "triggers", "positionTarget", "container", "disablePopover", "popoverClass", "openDelay", "closeDelay"], outputs: ["shown", "hidden"], exportAs: ["ngbPopover"] }, { kind: "directive", type: i1$1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$1.MaxLengthValidator, selector: "[maxlength][formControlName],[maxlength][formControl],[maxlength][ngModel]", inputs: ["maxlength"] }, { kind: "directive", type: i1$1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ExpandingInputComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cb-expanding-input', providers: [
                        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => ExpandingInputComponent), multi: true },
                    ], template: `
			<ng-template #popover >
				<div class="ph-16 pv-8 border-radius-4 font-standard box-shadow-default bg-white text-gray-1000 text-sm-1" aria-live="assertive">
					{{tooltipLabel}}
				</div>
			</ng-template>
				<input
					[ngbPopover]="this.shouldShowTooltip() ? popover: null"
					[maxlength]="maxLength"
					[placement]="'right'"
					(input)="resize($event.target.value)"
					[attr.size]="size === 0 ? 1: size"
					placeholder="{{placeholder}}"
					triggers="mouseenter:mouseleave"
					[disabled]="disabled"
					class="form-control bg-transparent ph-8 pv-4 edit-title"
					(focus)="showBorder()"
					(blur)="editing=false"
					(ngModelChange)="onChange($event)"
					[openDelay]="300"
					[closeDelay]="500"
					[(ngModel)]="value">
	`, styles: [".dropdown.show .dropdown-toggle{background:var(--header-active-menu-bg, var(--white));color:var(--header-link-color-hover, var(--action-700))!important}.dropdown-toggle:hover{color:var(--header-link-color-hover, var(--action-700))!important}.dropdown-toggle:after{display:none!important}.dropdown-toggle:focus,.dropdown-toggle:focus-visible{outline:2px solid var(--focus-outline-color, var(--gray-1000))!important;outline-offset:-2px!important}.dropdown-menu{background-color:var(--white)}:host-context(:root,.light-mode){--black-50-percent-fallback: rgba(0, 0, 0, .5)}:host-context(.dark-mode,.application-dark-mode){--black-50-percent-fallback: rgba(255, 255, 255, .5)}.box-shadow-default{box-shadow:0 0 8px var(--black-50-percent, var(--black-50-percent-fallback))!important}input{border:1px solid transparent!important;height:inherit;color:inherit;background-color:transparent;border-radius:4px}input:hover{border:1px solid var(--gray-600)!important}input:focus,input:focus-visible{border:1px solid var(--gray-600)!important;box-shadow:none!important}.width-auto{width:auto!important}:host ::ng-deep .arrow:after{border-top-color:transparent!important;border-bottom-color:transparent!important;border-right-color:transparent!important;border-left-color:transparent!important;border-style:solid;position:absolute;content:\"\";border-width:5px 5px 5px 0;border-right-color:var(--white)!important;right:100%;left:auto;top:calc(50% - 8px)}\n"] }]
        }], propDecorators: { disabled: [{
                type: Input
            }], tooltipLabel: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], maxLength: [{
                type: Input
            }] } });

class ExpandingInputModule {
}
ExpandingInputModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ExpandingInputModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ExpandingInputModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: ExpandingInputModule, declarations: [ExpandingInputComponent], imports: [CommonModule,
        NgbModule,
        FormsModule], exports: [ExpandingInputComponent] });
ExpandingInputModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ExpandingInputModule, imports: [CommonModule,
        NgbModule,
        FormsModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ExpandingInputModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [ExpandingInputComponent],
                    imports: [
                        CommonModule,
                        NgbModule,
                        FormsModule
                    ],
                    exports: [ExpandingInputComponent]
                }]
        }] });

class UnifiedAngularComponentsModule {
}
UnifiedAngularComponentsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: UnifiedAngularComponentsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
UnifiedAngularComponentsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: UnifiedAngularComponentsModule, exports: [InlineHelpModule,
        SidebarNavModule,
        LoaderModule,
        PushButtonsModule,
        DragHandleModule,
        AvatarModule,
        ToastModule,
        ModalModule,
        SelectModule,
        HeaderModule,
        NoticeModule,
        LoginModule,
        SpeechBubbleModule,
        SortDirectionModule,
        ToggleModule,
        AccountSwitcherModule,
        ToggleModule,
        SearchableListModule,
        ClearableInputModule,
        SpacesModule,
        GenericRadioButtonsModule,
        CardModule,
        CollapsingPanelModule,
        ExpandingInputModule] });
UnifiedAngularComponentsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: UnifiedAngularComponentsModule, imports: [InlineHelpModule,
        SidebarNavModule,
        LoaderModule,
        PushButtonsModule,
        DragHandleModule,
        AvatarModule,
        ToastModule,
        ModalModule,
        SelectModule,
        HeaderModule,
        NoticeModule,
        LoginModule,
        SpeechBubbleModule,
        SortDirectionModule,
        ToggleModule,
        AccountSwitcherModule,
        ToggleModule,
        SearchableListModule,
        ClearableInputModule,
        SpacesModule,
        GenericRadioButtonsModule,
        CardModule,
        CollapsingPanelModule,
        ExpandingInputModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: UnifiedAngularComponentsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [],
                    exports: [
                        InlineHelpModule,
                        SidebarNavModule,
                        LoaderModule,
                        PushButtonsModule,
                        DragHandleModule,
                        AvatarModule,
                        ToastModule,
                        ModalModule,
                        SelectModule,
                        HeaderModule,
                        NoticeModule,
                        LoginModule,
                        SpeechBubbleModule,
                        SortDirectionModule,
                        ToggleModule,
                        AccountSwitcherModule,
                        ToggleModule,
                        SearchableListModule,
                        ClearableInputModule,
                        SpacesModule,
                        GenericRadioButtonsModule,
                        CardModule,
                        CollapsingPanelModule,
                        ExpandingInputModule
                    ],
                }]
        }] });

/*
 * Public API Surface of unified-angular-components
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AccountSwitcherComponent, AccountSwitcherLabels, AccountSwitcherModule, ActionCardComponent, AlertLevel, AvatarComponent, AvatarModule, AvatarSize, CapsLockService, CardComponent, CardModule, ClearableInputComponent, ClearableInputModule, CollapsingPanelComponent, CollapsingPanelModule, DragHandleComponent, DragHandleModule, DropdownPlacement, ExpandingInputComponent, ExpandingInputModule, GenericRadioButtonsModule, GravatarFallbackOption, HeaderComponent, HeaderModule, HighlightMatchPipe, InlineHelpComponent, InlineHelpModule, LoaderComponent, LoaderModule, LoginComponent, LoginModule, ModalFooterComponent, ModalHeaderComponent, ModalModule, NoticeComponent, NoticeModule, PushButtonsComponent, PushButtonsModule, RadioGroupComponent, SearchConstants, SearchPipe, SearchableListComponent, SearchableListModule, SelectComponent, SelectModule, SidebarNavComponent, SidebarNavModule, SortDirection, SortDirectionComponent, SortDirectionModule, SpacesComponent, SpacesModule, SpeechBubbleComponent, SpeechBubbleModule, Toast, ToastComponent, ToastItemComponent, ToastModule, ToastService, ToggleComponent, ToggleModule, UnifiedAngularComponentsModule };
